import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowUpRight, FileSearch, LoaderCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { LOGO_URL, journey } from '../data/mock';
import bisSchemeOneData from '../data/bisSchemeOneProducts.json';
import beeProductData from '../data/beeProducts.json';
import isiMarkingFeeData from '../data/isiMarkingFees.json';

const getPageKey = (pathname) => {
  let cleanPath = decodeURIComponent(pathname).replace(/^\/+|\/+$/g, '');
  cleanPath = cleanPath.replace(/\.html$/i, '');
  if (cleanPath === 'global') return 'global';
  return cleanPath || 'legacy-home';
};

const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const brandAmcHeroScope = [
  { title: 'Regulatory monitoring', detail: 'Alerts and impact', accent: '#c41e3a' },
  { title: 'Compliance updates', detail: 'Weekly and monthly', accent: '#285fcb' },
  { title: 'Health checks', detail: 'Quarterly review', accent: '#078778' },
  { title: 'Risk scorecards', detail: 'Portfolio visibility', accent: '#a96508' },
  { title: 'Product mapping', detail: 'Applicability matrix', accent: '#7b46c5' },
  { title: 'Market-entry advisory', detail: 'Launch readiness', accent: '#c43b75' },
  { title: 'Vendor compliance', detail: 'Supply-chain checks', accent: '#2d708f' },
  { title: 'Dashboards and reports', detail: 'Management view', accent: '#3a7b3f' },
];

const manufacturerAmcHeroScope = [
  { title: 'Production filings', detail: 'Periodic returns', accent: '#c41e3a' },
  { title: 'Licence renewals', detail: 'Deadline control', accent: '#285fcb' },
  { title: 'Brand and model additions', detail: 'Unlimited inclusions', accent: '#078778' },
  { title: 'NC / PC resolution', detail: 'Corrective support', accent: '#a96508' },
  { title: 'Surveillance readiness', detail: 'Inspection support', accent: '#7b46c5' },
  { title: 'QAP advisory', detail: 'Quality-plan upkeep', accent: '#c43b75' },
  { title: 'Stock inventory', detail: 'Records and guidance', accent: '#2d708f' },
  { title: 'Regulatory monitoring', detail: 'Standards and QCOs', accent: '#3a7b3f' },
];

const amcHeroScopes = {
  'amc-for-brands': {
    label: 'Brand AMC scope',
    ariaLabel: 'Scope of work included in AMC for brands',
    items: brandAmcHeroScope,
  },
  'amc-for-manufacturers': {
    label: 'Manufacturer AMC scope',
    ariaLabel: 'Scope of work included in AMC for manufacturers',
    items: manufacturerAmcHeroScope,
  },
  'factory-compliance-license-management': {
    label: 'Manufacturer AMC scope',
    ariaLabel: 'Scope of work included in AMC for manufacturers',
    items: manufacturerAmcHeroScope,
  },
};

const virtualIsiProductPage = (pageKey) => {
  const item = bisSchemeOneData.products.find((product) => product.route === `/${pageKey}`);
  if (!item) return null;
  const product = escapeHtml(item.product);
  const standard = escapeHtml(item.standard);
  const category = escapeHtml(item.category);
  const notificationUrl = escapeHtml(item.notificationUrl || bisSchemeOneData.source);

  return {
    route: item.route,
    key: pageKey,
    source: 'official-bis-scheme-i',
    title: `${item.product} ISI Certification | ${item.standard} | Complise India`,
    description: `ISI Mark certification route for ${item.product} under ${item.standard}, including applicability, testing, documentation, factory readiness and BIS filing support.`,
    category: 'Product Certifications',
    bodyClass: 'subservice-page isi-product-page',
    stylesheet: '/legacy-source/legacy-content.css',
    css: '',
    html: `<main>
      <section class="subservice-hero isi-product-hero">
        <div class="shell subservice-hero-grid">
          <div class="subservice-hero-copy">
            <div class="subservice-kicker"><span></span> BIS Scheme-I compulsory product</div>
            <h1 class="subservice-hero-title">${product}</h1>
            <p class="subservice-hero-text">This product is listed by the Bureau of Indian Standards under the Scheme-I compulsory certification framework. Complise India supports standard mapping, testing readiness, documentation, factory preparation and licence filing.</p>
            <div class="subservice-badges"><span>BIS Scheme-I</span><span>${standard}</span><span>ISI Mark route</span></div>
            <div class="subservice-hero-actions"><a class="sub-btn primary" href="/contact">Start Free Consultation</a><a class="sub-btn secondary" href="/isi-mark-certification-scheme-1#compulsory-products">Back to product list</a></div>
          </div>
          <div class="subservice-hero-card">
            <div class="subservice-logo-card"><img src="/legacy-source/assets/services/isimarkclarification.png" alt="ISI Mark Certification icon"></div>
            <h2>${product}</h2><p>${category}</p>
          </div>
        </div>
      </section>
      <section class="guide-layout-section">
        <div class="shell guide-layout">
          <aside class="guide-toc"><div class="guide-toc-card"><span>Product route</span><a href="#overview">Overview</a><a href="#applicability">Applicability</a><a href="#documents">Documents</a><a href="#process">Process</a><a href="#qco">Official notification</a></div><div class="guide-side-form"><h3>Check this product route</h3><p>Share model details, factory location and intended use for an exact applicability review.</p><a class="sub-btn primary" href="/contact">Send enquiry</a></div></aside>
          <article class="guide-article">
            <section class="guide-block" id="overview"><div class="guide-section-head compact"><span>Overview</span><h2>ISI certification route for ${product}</h2><p>Products listed under compulsory BIS Scheme-I ordinarily require a valid licence, compliant use of the Standard Mark, applicable testing and continuing surveillance readiness before manufacture, import or sale in India.</p></div><div class="product-route-card"><div class="mini-stat"><b>Indian Standard</b><span>${standard}</span></div><div class="mini-stat"><b>Product category</b><span>${category}</span></div><div class="mini-stat"><b>Certification route</b><span>ISI Mark under BIS Scheme-I</span></div></div></section>
            <section class="guide-block" id="applicability"><div class="guide-section-head compact"><span>Applicability</span><h2>Confirm the exact product scope before filing</h2><p>Applicability depends on product construction, specifications, intended use, the notified Quality Control Order, manufacturing location and whether the product is made domestically or imported.</p></div><div class="subservice-mini-grid"><div><b>Standard mapping</b><p>Confirm the applicable Indian Standard and product scope.</p></div><div><b>Testing readiness</b><p>Plan samples, laboratory testing and in-house quality controls.</p></div><div><b>Licence planning</b><p>Prepare for filing, factory inspection, marking and renewals.</p></div></div></section>
            <section class="guide-block" id="documents"><div class="guide-section-head compact"><span>Documentation</span><h2>Typical records required</h2><p>Manufacturer records, factory details, technical specifications, testing evidence, quality-control records, authorization documents, marking details and BIS portal filings are coordinated according to the final product scope.</p></div></section>
            <section class="guide-block" id="process"><div class="guide-section-head compact"><span>Process</span><h2>How the approval path is managed</h2><p>The route normally covers applicability confirmation, document readiness, application filing, testing coordination, factory inspection support, query response, licence grant and ongoing renewal monitoring.</p></div></section>
            <section class="guide-block" id="qco"><div class="guide-section-head compact"><span>Official reference</span><h2>BIS notification and QCO details</h2><p>Always review the current official notification, amendments and implementation dates before making a regulatory decision.</p><div class="subservice-hero-actions"><a class="sub-btn secondary" href="${notificationUrl}" target="_blank" rel="noopener">View official notification</a><a class="sub-btn primary" href="/contact">Confirm applicability</a></div></div></section>
            <div class="product-cta-strip"><div><b>Need confirmation for ${product}?</b><p>Share the product specification, models, factory location and intended market route for a detailed compliance plan.</p></div><a class="sub-btn primary" href="/contact">Talk to Complise</a></div>
          </article>
        </div>
      </section>
    </main>`,
  };
};

const virtualBeeProductPage = (pageKey) => {
  const matches = beeProductData.products.filter((item) => item.route === `/${pageKey}`);
  if (!matches.length) return null;

  const primary = matches.find((item) => item.scheme === 'Mandatory') || matches[0];
  const product = escapeHtml(primary.product);
  const schemes = [...new Set(matches.map((item) => item.scheme))];
  const schemeLabel = schemes.join(' / ');
  const statusLabels = matches.map((item) => item.status).filter(Boolean);
  const aliases = [...new Set(matches.flatMap((item) => item.aliases || []))];
  const officialLinks = matches.map((item) => `
    <article class="bee-official-doc-card">
      <span>${escapeHtml(item.scheme)} scheme</span>
      <strong>${escapeHtml(item.notificationTitle)}</strong>
      ${item.status ? `<p>${escapeHtml(item.status)}</p>` : '<p>Current BEE Standards &amp; Labelling schedule.</p>'}
      <div class="bee-official-doc-actions">
        <a href="${escapeHtml(item.notificationUrl)}" target="_blank" rel="noopener">Official schedule PDF ↗</a>
        <a href="${escapeHtml(item.labelUrl)}" target="_blank" rel="noopener">Sample label ${escapeHtml(item.labelFormat || 'PDF')} ↗</a>
      </div>
    </article>`).join('');

  return {
    route: primary.route,
    key: pageKey,
    source: 'official-bee-standards-labelling',
    title: `${primary.product} BEE Registration & Star Label | Complise India`,
    description: `BEE Standards & Labelling route for ${primary.product}, with official schedule, sample label, applicability, testing and registration guidance.`,
    category: 'Product Certifications',
    bodyClass: 'subservice-page bee-page bee-product-page',
    stylesheet: '/legacy-source/legacy-content.css',
    css: '',
    html: `<main>
      <section class="subservice-hero bee-hero">
        <div class="shell subservice-hero-grid">
          <div class="subservice-hero-copy">
            <div class="subservice-kicker"><span></span> BEE Standards &amp; Labelling product</div>
            <h1 class="subservice-hero-title">${product}</h1>
            <p class="subservice-hero-text">Official ${escapeHtml(schemeLabel.toLowerCase())} appliance route under the Bureau of Energy Efficiency Standards &amp; Labelling Programme. Complise India supports applicability, testing, brand registration, model filing and label approval.</p>
            <div class="subservice-badges"><span>${escapeHtml(schemeLabel)}</span><span>BEE Star Label</span>${statusLabels.map((status) => `<span>${escapeHtml(status)}</span>`).join('')}</div>
            <div class="subservice-hero-actions"><a class="sub-btn primary" href="/contact">Check applicability</a><a class="sub-btn secondary" href="/bee-certification#covered-products">Back to BEE products</a></div>
          </div>
          <div class="subservice-hero-card">
            <div class="subservice-logo-card"><img src="/legacy-source/assets/services/bee.png" alt="BEE Certification logo"></div>
            <h2>${product}</h2><p>${escapeHtml(schemeLabel)} appliance</p>
          </div>
        </div>
      </section>
      <section class="guide-layout-section">
        <div class="shell guide-layout">
          <aside class="guide-toc"><div class="guide-toc-card"><span>Product route</span><a href="#overview">Overview</a><a href="#official-documents">Official PDFs</a><a href="#applicability">Applicability</a><a href="#process">Process</a></div><div class="guide-side-form"><h3>Confirm this BEE route</h3><p>Share the model, rated capacity, brand ownership and manufacturing or import route.</p><a class="sub-btn primary" href="/contact">Send enquiry</a></div></aside>
          <article class="guide-article">
            <section class="guide-block" id="overview"><div class="guide-section-head compact"><span>Official BEE category</span><h2>BEE route for ${product}</h2><p>This product appears in BEE's official ${escapeHtml(schemeLabel.toLowerCase())} appliance list. The exact rating, testing method, validity period and label artwork requirements are governed by the current product schedule and amendments.</p></div><div class="product-route-card"><div class="mini-stat"><b>Programme</b><span>Standards &amp; Labelling</span></div><div class="mini-stat"><b>Scheme</b><span>${escapeHtml(schemeLabel)}</span></div><div class="mini-stat"><b>Route</b><span>Testing, brand registration and model approval</span></div></div>${aliases.length ? `<p><strong>Also searched as:</strong> ${escapeHtml(aliases.join(', '))}</p>` : ''}</section>
            <section class="guide-block" id="official-documents"><div class="guide-section-head compact"><span>Official source files</span><h2>Schedule, notification and sample label</h2><p>Open the current files hosted by the official BEE Star Label portal before making a final regulatory decision.</p></div><div class="bee-official-doc-grid">${officialLinks}</div></section>
            <section class="guide-block" id="applicability"><div class="guide-section-head compact"><span>Applicability</span><h2>Confirm the product scope before testing</h2><p>Applicability depends on product type, rated capacity, construction, energy-performance parameters, model family, applicant role and the effective date of the current BEE schedule.</p></div><div class="subservice-mini-grid"><div><b>Schedule mapping</b><p>Confirm the exact BEE product schedule and current amendment.</p></div><div><b>Test planning</b><p>Align the model and sample with the prescribed laboratory method.</p></div><div><b>Label readiness</b><p>Prepare brand, model and label data for portal approval.</p></div></div></section>
            <section class="guide-block" id="process"><div class="guide-section-head compact"><span>Registration path</span><h2>How the BEE approval is managed</h2><p>The usual route covers scope confirmation, approved laboratory testing, brand registration, model application, technical scrutiny, fee coordination, label authorization and ongoing renewal or amendment tracking.</p></div></section>
            <div class="product-cta-strip"><div><b>Need confirmation for ${product}?</b><p>Send the product specification, model range, test information and intended India market route for a focused BEE plan.</p></div><a class="sub-btn primary" href="/contact">Talk to Complise</a></div>
          </article>
        </div>
      </section>
    </main>`,
  };
};

const topLevelServiceGroups = [
  {
    family: 'bis',
    pages: [
      'bureau-of-indian-standards-bis',
      'isi-mark-certification-scheme-1',
      'crs-certification',
      'bis-hallmark-registration',
      'bis-fmcs-certification',
    ],
  },
  { family: 'bee', pages: ['bureau-of-energy-efficiency-bee', 'bee-certification'] },
  { family: 'connectivity', pages: ['wireless-planning-coordination-wpc', 'wpc-approvals-and-licenses'] },
  { family: 'connectivity', pages: ['telecommunication-engineering-centre-tec', 'tec-mtcte-certification'] },
  { family: 'connectivity', pages: ['standardisation-testing-quality-certification-stqc', 'stqc-certification'] },
  { family: 'lmpc', pages: ['consumer-affairs-legal-metrology', 'legal-metrology-certification'] },
  { family: 'medical', pages: ['central-drugs-standard-control-organization-cdsco', 'medical-device-registration'] },
  { family: 'food', pages: ['food-safety-standards-authority-india-fssai', 'fssai-approval'] },
  { family: 'connectivity', pages: ['department-of-telecommunication-dot', 'tac-imei-registration'] },
  { family: 'peso', pages: ['petroleum-explosives-safety-organization-peso', 'peso-certification'] },
  { family: 'trade', pages: ['directorate-general-foreign-trade-dgft', 'dgft-approval'] },
  { family: 'automotive', pages: ['automotive-research-association-india-arai', 'arai-approval'] },
  { family: 'business', pages: ['business-legal-compliance'] },
];

const topLevelServiceFamilyByPageKey = Object.fromEntries(
  topLevelServiceGroups.flatMap(({ family, pages }) => pages.map((page) => [page, family])),
);

const serviceProductDirectoryConfigs = {
  'bureau-of-indian-standards-bis': {
    title: 'Products and routes covered under BIS', authority: 'BIS', includeIsi: true, prefixes: ['/crs-product-', '/hallmark-'],
  },
  'bis-fmcs-certification': {
    title: 'Scheme-I products supported under the FMCS route', authority: 'BIS FMCS', includeIsi: true, prefixes: [],
  },
  'crs-certification': {
    title: 'Products covered under BIS CRS', authority: 'BIS CRS', prefixes: ['/crs-product-'],
  },
  'bis-hallmark-registration': {
    title: 'Articles and registration routes covered under Hallmarking', authority: 'BIS Hallmarking', prefixes: ['/hallmark-'],
  },
  'bee-certification': {
    title: 'Products covered under BEE registration and labelling', authority: 'BEE', includeBee: true, prefixes: ['/bee-'],
  },
  'wpc-approvals-and-licenses': {
    title: 'Wireless products commonly covered under WPC', authority: 'WPC', prefixes: ['/wpc-'],
  },
  'tec-mtcte-certification': {
    title: 'Telecom products and approval routes covered under TEC', authority: 'TEC / MTCTE', prefixes: ['/tec-', '/mtcte-', '/gr-ir-', '/tsec-', '/nccs-'],
  },
  'stqc-certification': {
    title: 'Products and systems covered under STQC', authority: 'STQC', prefixes: ['/stqc-'],
  },
  'legal-metrology-certification': {
    title: 'Packaged-product and registration routes under Legal Metrology', authority: 'Legal Metrology', prefixes: ['/lmpc-'],
  },
  'medical-device-registration': {
    title: 'Products and approval routes covered under CDSCO', authority: 'CDSCO', prefixes: ['/cdsco-'],
  },
  'fssai-approval': {
    title: 'Food-business and product approval routes under FSSAI', authority: 'FSSAI', prefixes: ['/fssai-'],
  },
  'tac-imei-registration': {
    title: 'Devices and registration routes covered under TAC and IMEI', authority: 'DoT / GSMA', prefixes: ['/imei-'],
  },
  'peso-certification': {
    title: 'Products and regulated approval routes under PESO', authority: 'PESO', prefixes: ['/peso-'],
  },
  'dgft-approval': {
    title: 'Import-export products and authorization routes under DGFT', authority: 'DGFT', prefixes: ['/dgft-'],
  },
  'arai-approval': {
    title: 'Vehicles, equipment and components covered under automotive approval', authority: 'ARAI', prefixes: ['/arai-'],
  },
};

const serviceProductDirectoryAliases = {
  'bureau-of-energy-efficiency-bee': 'bee-certification',
  'wireless-planning-coordination-wpc': 'wpc-approvals-and-licenses',
  'telecommunication-engineering-centre-tec': 'tec-mtcte-certification',
  'standardisation-testing-quality-certification-stqc': 'stqc-certification',
  'consumer-affairs-legal-metrology': 'legal-metrology-certification',
  'central-drugs-standard-control-organization-cdsco': 'medical-device-registration',
  'food-safety-standards-authority-india-fssai': 'fssai-approval',
  'department-of-telecommunication-dot': 'tac-imei-registration',
  'petroleum-explosives-safety-organization-peso': 'peso-certification',
  'directorate-general-foreign-trade-dgft': 'dgft-approval',
  'automotive-research-association-india-arai': 'arai-approval',
};

const commonServiceVisuals = {
  testing: {
    src: '/legacy-source/assets/isi/isi-laboratory-product-testing.webp',
    alt: 'Engineer conducting product conformity testing in a laboratory',
    eyebrow: 'Technical evidence',
    title: 'Testing and technical records built around the applicable standard',
    copy: 'Test planning, calibrated instruments and traceable evidence help keep the approval file clear and review-ready.',
  },
  factory: {
    src: '/legacy-source/assets/isi/isi-factory-quality-inspection.webp',
    alt: 'Quality engineers inspecting products on a manufacturing line',
    eyebrow: 'Operational readiness',
    title: 'Compliance checked where products and processes come together',
    copy: 'Manufacturing controls, product details and on-site readiness are coordinated before inspection or filing milestones.',
  },
  documents: {
    src: '/legacy-source/assets/isi/isi-compliance-documentation.webp',
    alt: 'Compliance professional reviewing technical documents with a manufacturing supervisor',
    eyebrow: 'Document control',
    title: 'A structured technical file for smoother authority review',
    copy: 'Applications, declarations, drawings and supporting records are organised into one controlled compliance trail.',
  },
};

const serviceVisualStories = {
  bis: [
    {
      ...commonServiceVisuals.factory,
      eyebrow: 'Factory readiness',
      title: 'Quality systems verified where products are made',
      copy: 'Factory capability, testing facilities and production controls are reviewed together before the BIS inspection stage.',
    },
    {
      ...commonServiceVisuals.testing,
      eyebrow: 'Product testing',
      title: 'Evidence built through calibrated laboratory testing',
      copy: 'Samples, instruments and test records must align with the applicable Indian Standard and certification route.',
    },
    commonServiceVisuals.documents,
  ],
  bee: [
    {
      src: '/legacy-source/assets/top-services/energy-efficiency-audit.webp',
      alt: 'Energy efficiency testing of appliances in an Indian laboratory',
      eyebrow: 'Energy performance',
      title: 'Appliance efficiency measured before the label is filed',
      copy: 'Model data, power consumption and laboratory evidence are aligned with the applicable BEE schedule and label route.',
    },
    commonServiceVisuals.testing,
    commonServiceVisuals.documents,
  ],
  connectivity: [
    {
      src: '/legacy-source/assets/top-services/wireless-rf-testing.webp',
      alt: 'Wireless and RF device testing in an Indian electronics laboratory',
      eyebrow: 'RF and device testing',
      title: 'Connectivity products assessed against the right technical route',
      copy: 'Frequency use, device configuration, test evidence and applicant details are mapped before regulatory filing.',
    },
    commonServiceVisuals.testing,
    commonServiceVisuals.documents,
  ],
  lmpc: [
    {
      src: '/legacy-source/assets/top-services/legal-metrology-packaging.webp',
      alt: 'Packaged consumer goods undergoing label and quantity inspection',
      eyebrow: 'Packaged goods',
      title: 'Labels, declarations and pack details checked before market release',
      copy: 'Mandatory declarations, importer information and packaging records are reviewed as one legal-metrology workflow.',
    },
    commonServiceVisuals.factory,
    commonServiceVisuals.documents,
  ],
  medical: [
    {
      src: '/legacy-source/assets/top-services/medical-device-compliance.webp',
      alt: 'Medical device quality inspection in an Indian clean laboratory',
      eyebrow: 'Medical quality systems',
      title: 'Device evidence prepared with safety and traceability in focus',
      copy: 'Classification, technical documentation, quality records and applicant responsibilities are coordinated for CDSCO review.',
    },
    commonServiceVisuals.testing,
    commonServiceVisuals.documents,
  ],
  food: [
    {
      src: '/legacy-source/assets/top-services/food-safety-inspection.webp',
      alt: 'Food safety inspection in a modern Indian manufacturing facility',
      eyebrow: 'Food safety controls',
      title: 'Hygiene, process and product records reviewed at the source',
      copy: 'Facility practices, product categories and licence inputs are checked together before the FSSAI application moves.',
    },
    commonServiceVisuals.testing,
    commonServiceVisuals.documents,
  ],
  peso: [
    {
      src: '/legacy-source/assets/top-services/peso-safety-inspection.webp',
      alt: 'Petroleum and pressure equipment safety inspection at an Indian industrial site',
      eyebrow: 'Industrial safety',
      title: 'Hazardous equipment assessed with site safety at the centre',
      copy: 'Equipment specifications, installation conditions and safety records are mapped to the applicable PESO approval route.',
    },
    commonServiceVisuals.factory,
    commonServiceVisuals.documents,
  ],
  trade: [
    {
      src: '/legacy-source/assets/top-services/import-export-compliance.webp',
      alt: 'Import export compliance review at an Indian logistics facility',
      eyebrow: 'Trade readiness',
      title: 'Goods and documents aligned before they reach the border',
      copy: 'Classification, authorisations and shipment records are coordinated to reduce avoidable import-export delays.',
    },
    commonServiceVisuals.documents,
    {
      src: '/legacy-source/assets/top-services/business-legal-compliance.webp',
      alt: 'Indian compliance professionals reviewing business and trade records',
      eyebrow: 'Managed execution',
      title: 'One accountable desk across filings and follow-up',
      copy: 'Commercial records, authority communication and next actions stay visible throughout the compliance journey.',
    },
  ],
  automotive: [
    {
      src: '/legacy-source/assets/top-services/automotive-testing.webp',
      alt: 'Automotive component and vehicle testing at an Indian test facility',
      eyebrow: 'Vehicle and component testing',
      title: 'Automotive evidence developed around real test conditions',
      copy: 'Vehicle category, component specifications, homologation tests and production-conformity records are planned together.',
    },
    commonServiceVisuals.factory,
    commonServiceVisuals.documents,
  ],
  business: [
    {
      src: '/legacy-source/assets/top-services/business-legal-compliance.webp',
      alt: 'Indian professionals reviewing corporate legal and tax compliance records',
      eyebrow: 'Business compliance',
      title: 'Corporate, tax and legal records managed through one clear workflow',
      copy: 'Entity documents, statutory filings and brand or trade requirements are organised around the business objective.',
    },
    commonServiceVisuals.documents,
    {
      src: '/legacy-source/assets/top-services/import-export-compliance.webp',
      alt: 'Indian trade compliance professionals reviewing shipment documentation',
      eyebrow: 'Connected obligations',
      title: 'Compliance that follows the business from setup to market access',
      copy: 'Registrations, filings, intellectual property and trade documentation remain connected instead of being handled in isolation.',
    },
  ],
};

const getLegacyFaIconType = (icon) => {
  const classes = [...icon.classList].join(' ');
  const has = (...tokens) => tokens.some((token) => classes.includes(`fa-${token}`));

  if (has('heart-pulse', 'stethoscope')) return 'medical';
  if (has('bowl-food')) return 'food';
  if (has('car-side')) return 'auto';
  if (has('bolt', 'plug', 'car-battery', 'blender', 'kitchen-set')) return 'appliance';
  if (has('wifi', 'signal', 'satellite-dish', 'tower-broadcast', 'wave-square')) return 'wireless';
  if (has('tower-cell', 'router', 'ethernet', 'server', 'network-wired', 'mobile-screen-button', 'mobile-button', 'tablet-screen-button', 'headset', 'video', 'camera', 'hard-drive')) return 'telecom';
  if (has('flask', 'flask-vial', 'vial-circle-check', 'vial-circle-exclamation', 'bug', 'magnifying-glass-chart', 'gauge-high')) return 'lab';
  if (has('industry', 'warehouse', 'gears', 'helmet-safety', 'screwdriver-wrench', 'oil-well', 'fire-flame-curved', 'explosion', 'wind')) return 'factory';
  if (has('scale-balanced', 'landmark', 'building-columns', 'certificate', 'copyright', 'file-invoice-dollar', 'file-contract', 'passport', 'id-card')) return 'legal';
  if (has('fingerprint', 'user-shield', 'user-lock', 'person-military-rifle')) return 'identity';
  if (has('store', 'truck-fast', 'truck-moving', 'truck-ramp-box', 'box-open', 'boxes-packing', 'boxes-stacked', 'cart-shopping', 'route', 'globe', 'earth-americas', 'handshake', 'handshake-angle', 'user-tie')) return 'trade';
  if (has('book', 'clipboard-check', 'clipboard-question', 'list-check', 'file-circle-xmark', 'file-import', 'file-export', 'folder-open', 'tags', 'code-branch', 'code-compare', 'diagram-project', 'layer-group')) return 'document';
  if (has('shield', 'lock', 'ban', 'triangle-exclamation', 'circle-exclamation', 'shield-virus')) return 'default';
  return 'default';
};

const legacyEnhancementsCss = `
:host(.isi-service-family) .service-guide-hero,
:host(.isi-service-family) .subservice-hero,
:host(.isi-service-family) .sub-hero {
  position: relative !important;
  overflow: hidden !important;
  background:
    radial-gradient(circle at 86% 18%, rgba(196, 30, 58, 0.12), transparent 31%),
    radial-gradient(circle at 12% 82%, rgba(31, 79, 143, 0.08), transparent 28%),
    linear-gradient(145deg, #ffffff 0%, #fbfcff 52%, #f2f5fa 100%) !important;
  color: #101525 !important;
  border-bottom: 1px solid rgba(18, 21, 37, 0.09) !important;
}

:host(.isi-service-family) .service-guide-hero::before,
:host(.isi-service-family) .subservice-hero::before,
:host(.isi-service-family) .sub-hero::before {
  background-image:
    linear-gradient(rgba(18, 21, 37, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(18, 21, 37, 0.035) 1px, transparent 1px) !important;
  background-size: 46px 46px !important;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.42), transparent 78%) !important;
}

:host(.isi-service-family) :is(.service-guide-hero-grid, .service-guide-hero__grid, .sub-hero-grid) {
  position: relative !important;
  z-index: 1 !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 370px) !important;
  gap: clamp(34px, 5vw, 70px) !important;
  align-items: center !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(h1, .subservice-hero-title) {
  color: #101525 !important;
  text-shadow: none !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero) :is(.service-guide-copy > p, .subservice-hero-text),
:host(.isi-service-family) .service-guide-hero__grid > div:first-child > p,
:host(.isi-service-family) .sub-hero-copy > p {
  color: #526178 !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.sub-eyebrow, .subservice-kicker, .eyebrow) {
  color: #c41e3a !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.guide-meta span, .subservice-badges span, .hero-pills span, .hero-chip-row span) {
  border-color: rgba(18, 21, 37, 0.1) !important;
  background: rgba(255, 255, 255, 0.9) !important;
  color: #344056 !important;
  box-shadow: 0 10px 26px rgba(18, 21, 37, 0.06) !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.guide-identity-card, .subservice-visual-card, .subservice-hero-card, .service-guide-hero__card, .sub-hero-card) {
  position: relative !important;
  overflow: hidden !important;
  border: 1px solid rgba(18, 21, 37, 0.11) !important;
  background: rgba(255, 255, 255, 0.96) !important;
  color: #101525 !important;
  box-shadow: 0 28px 74px rgba(18, 21, 37, 0.13) !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.guide-identity-card, .subservice-visual-card, .subservice-hero-card, .service-guide-hero__card, .sub-hero-card)::after {
  content: "";
  position: absolute;
  right: -50px;
  bottom: -62px;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: rgba(196, 30, 58, 0.08);
  pointer-events: none;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.guide-identity-card strong, .subservice-visual-card strong, .subservice-hero-card h2, .service-guide-hero__card h2, .sub-hero-card h2) {
  position: relative;
  z-index: 1;
  color: #101525 !important;
}

:host(.isi-service-family) .service-guide-hero .guide-identity-card span,
:host(.isi-service-family) .service-guide-hero .subservice-visual-card span,
:host(.isi-service-family) .service-guide-hero .service-guide-hero__card .authority-mini-list span {
  position: relative;
  z-index: 1;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  min-height: 34px;
  margin-top: 10px !important;
  padding: 7px 12px;
  border: 1px solid rgba(196, 30, 58, 0.16);
  border-radius: 999px;
  background: #fff2f4;
  color: #a81732 !important;
  font-size: 0.78rem !important;
  font-weight: 900 !important;
  line-height: 1.35 !important;
  letter-spacing: 0.035em;
  text-align: center;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero) :is(.subservice-visual-card, .subservice-hero-card) p {
  position: relative;
  z-index: 1;
  color: #526178 !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.service-guide-hero__card, .sub-hero-card) p {
  position: relative;
  z-index: 1;
  color: #526178 !important;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.service-guide-hero__card, .sub-hero-card) img {
  position: relative;
  z-index: 1;
}

:host(.isi-service-family) :is(.service-guide-hero, .subservice-hero) .sub-btn.secondary {
  border-color: rgba(18, 21, 37, 0.13) !important;
  background: #ffffff !important;
  color: #101525 !important;
}

:host(.isi-service-family) .authority-page {
  padding-bottom: 72px;
  background:
    radial-gradient(circle at 8% 28%, rgba(31, 79, 143, 0.055), transparent 24%),
    linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%) !important;
  color: #101525 !important;
}

:host(.isi-service-family) .authority-hero {
  position: relative !important;
  overflow: hidden !important;
  background:
    radial-gradient(circle at 86% 18%, rgba(196, 30, 58, 0.12), transparent 31%),
    radial-gradient(circle at 12% 82%, rgba(31, 79, 143, 0.08), transparent 28%),
    linear-gradient(145deg, #ffffff 0%, #fbfcff 52%, #f2f5fa 100%) !important;
  color: #101525 !important;
  border-bottom: 1px solid rgba(18, 21, 37, 0.09) !important;
}

:host(.isi-service-family) .authority-hero::before {
  background-image:
    linear-gradient(rgba(18, 21, 37, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(18, 21, 37, 0.035) 1px, transparent 1px) !important;
}

:host(.isi-service-family) .authority-hero-grid {
  grid-template-columns: minmax(0, 1fr) minmax(300px, 370px) !important;
  gap: clamp(34px, 5vw, 70px) !important;
  align-items: center !important;
}

:host(.isi-service-family) .authority-hero :is(h1, p) {
  text-shadow: none !important;
}

:host(.isi-service-family) .authority-hero h1 {
  color: #101525 !important;
}

:host(.isi-service-family) .authority-hero p {
  color: #526178 !important;
}

:host(.isi-service-family) .authority-kicker {
  color: #c41e3a !important;
}

:host(.isi-service-family) .authority-btn.ghost {
  border-color: rgba(18, 21, 37, 0.13) !important;
  background: #ffffff !important;
  color: #101525 !important;
}

:host(.isi-service-family) .authority-logo-panel {
  position: relative !important;
  overflow: hidden !important;
  border: 1px solid rgba(18, 21, 37, 0.11) !important;
  background: rgba(255, 255, 255, 0.96) !important;
  color: #101525 !important;
  box-shadow: 0 28px 74px rgba(18, 21, 37, 0.13) !important;
}

:host(.isi-service-family) .authority-logo-panel strong {
  color: #101525 !important;
}

:host(.isi-service-family) .authority-logo-panel span {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  min-height: 34px;
  padding: 7px 12px;
  border: 1px solid rgba(196, 30, 58, 0.16);
  border-radius: 999px;
  background: #fff2f4 !important;
  color: #a81732 !important;
  font-size: 0.78rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.035em;
}

:host(.isi-service-family) :is(.authority-stats article, .authority-sub-card, .authority-industries, .authority-docs, .authority-process-row article) {
  border-color: rgba(18, 21, 37, 0.1) !important;
  background: #ffffff !important;
  color: #344054 !important;
  box-shadow: 0 18px 46px rgba(18, 21, 37, 0.07) !important;
}

:host(.isi-service-family) :is(.authority-section-head, .authority-industries, .authority-docs, .authority-process) h2,
:host(.isi-service-family) :is(.authority-stats, .authority-sub-card, .authority-process-row) strong {
  color: #101525 !important;
}

:host(.isi-service-family) .authority-photo-feature {
  width: min(1180px, calc(100% - 44px));
  margin: clamp(34px, 5vw, 60px) auto;
}

:host(.isi-service-family) .isi-photo-feature {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.14fr) minmax(240px, 0.86fr);
  min-height: 330px;
  margin: 0;
  overflow: hidden;
  border: 1px solid rgba(18, 21, 37, 0.1);
  border-radius: 30px;
  background: #101827;
  box-shadow: 0 22px 54px rgba(15, 23, 42, 0.12);
}

:host(.isi-service-family) .isi-photo-feature.is-reversed {
  grid-template-columns: minmax(240px, 0.86fr) minmax(0, 1.14fr);
}

:host(.isi-service-family) .isi-photo-feature img {
  width: 100%;
  height: 100%;
  min-height: 330px;
  object-fit: cover;
  display: block;
}

:host(.isi-service-family) .isi-photo-feature.is-reversed img {
  order: 2;
}

:host(.isi-service-family) .isi-photo-feature figcaption {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(28px, 4vw, 46px);
  background:
    radial-gradient(circle at 86% 18%, rgba(196, 30, 58, 0.34), transparent 38%),
    linear-gradient(145deg, #111827 0%, #1d2939 100%);
  color: #ffffff;
}

:host(.isi-service-family) .isi-photo-feature figcaption span {
  margin-bottom: 12px;
  color: #ff7085;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

:host(.isi-service-family) .isi-photo-feature figcaption strong {
  color: #ffffff;
  font-size: clamp(1.45rem, 2.4vw, 2rem);
  line-height: 1.12;
  letter-spacing: -0.035em;
  text-wrap: balance;
}

:host(.isi-service-family) .isi-photo-feature figcaption p {
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.95rem;
  line-height: 1.65;
}

@media (max-width: 980px) {
  :host(.isi-service-family) :is(.service-guide-hero-grid, .service-guide-hero__grid),
  :host(.isi-service-family) .isi-photo-feature,
  :host(.isi-service-family) .isi-photo-feature.is-reversed {
    grid-template-columns: 1fr !important;
  }

  :host(.isi-service-family) .authority-hero-grid {
    grid-template-columns: 1fr !important;
  }

  :host(.isi-service-family) .isi-photo-feature.is-reversed img {
    order: 0;
  }
}

@media (max-width: 600px) {
  :host(.isi-service-family) .isi-photo-feature,
  :host(.isi-service-family) .isi-photo-feature img {
    min-height: 260px;
  }

  :host(.isi-service-family) .isi-photo-feature img {
    height: 260px;
  }
}

/* Shared interior design system for the imported top-level service guides. */
:host(.top-level-service-page) .legacy-document,
:host(.top-level-service-page) .legacy-document *,
:host(.top-level-service-page) .legacy-document *::before,
:host(.top-level-service-page) .legacy-document *::after {
  box-sizing: border-box;
}

:host(.top-level-service-page) .legacy-document {
  overflow: clip;
  background: #f7f9fc;
  color: #344054;
}

:host(.top-level-service-page) .legacy-document :is(.guide-layout-section, .service-guide-section, .subservice-body) {
  padding: clamp(42px, 6vw, 78px) 0 !important;
  background:
    radial-gradient(circle at 8% 18%, rgba(31, 79, 143, 0.045), transparent 24%),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 48%, #f6f8fb 100%) !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-layout, .service-guide-layout, .subservice-body.shell, .subservice-body > .shell) {
  display: grid !important;
  grid-template-columns: minmax(220px, 264px) minmax(0, 1fr) !important;
  gap: clamp(24px, 3.4vw, 46px) !important;
  width: min(1180px, calc(100% - 44px)) !important;
  max-width: 1180px !important;
  margin-inline: auto !important;
  align-items: start !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) {
  display: grid !important;
  min-width: 0 !important;
  gap: 22px !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar) {
  position: sticky !important;
  top: 106px !important;
  display: grid !important;
  gap: 16px !important;
  min-width: 0 !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-toc-card, .toc-card, .guide-side-form, .guide-side-card, .guide-side-news, .sidebar-insights, .sidebar-contact, .mini-contact-card, .toc-cta) {
  overflow: hidden !important;
  padding: 20px !important;
  border: 1px solid rgba(18, 21, 37, 0.1) !important;
  border-radius: 20px !important;
  background: #ffffff !important;
  color: #475467 !important;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.065) !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-toc-card, .toc-card) > :is(span, .toc-label, .toc-kicker) {
  display: block !important;
  margin: 0 0 12px !important;
  color: #c41e3a !important;
  font-size: 0.72rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.11em !important;
  text-transform: uppercase !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-toc-card, .toc-card) > a {
  display: flex !important;
  align-items: center !important;
  min-height: 38px !important;
  margin: 0 !important;
  padding: 8px 10px !important;
  border-bottom: 1px solid rgba(18, 21, 37, 0.065) !important;
  border-radius: 9px !important;
  color: #475467 !important;
  font-size: 0.82rem !important;
  font-weight: 750 !important;
  line-height: 1.35 !important;
  text-decoration: none !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-toc-card, .toc-card) > a:hover {
  background: #fff2f4 !important;
  color: #b91834 !important;
  transform: translateX(3px);
}

:host(.top-level-service-page) .legacy-document :is(.guide-side-form, .guide-side-card, .sidebar-contact, .mini-contact-card, .toc-cta) {
  border-color: rgba(196, 30, 58, 0.15) !important;
  background:
    radial-gradient(circle at 100% 0, rgba(196, 30, 58, 0.1), transparent 42%),
    #fffafb !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) {
  min-width: 0 !important;
  margin: 0 !important;
  padding: clamp(24px, 3.2vw, 38px) !important;
  border: 1px solid rgba(18, 21, 37, 0.095) !important;
  border-radius: 26px !important;
  background: #ffffff !important;
  color: #475467 !important;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.065) !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section) > :first-child {
  margin-top: 0 !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-section-head, .head) {
  max-width: 780px !important;
  margin: 0 0 22px !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-section-head, .head:not(.isi-table-row)) > span,
:host(.top-level-service-page) .legacy-document :is(.section-kicker, .guide-kicker, .guide-eyebrow, .eyebrow) {
  display: inline-flex !important;
  align-items: center !important;
  width: fit-content !important;
  margin: 0 0 9px !important;
  color: #c41e3a !important;
  font-size: 0.72rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.105em !important;
  text-transform: uppercase !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block, .guide-side-form, .guide-side-card) :is(h2, h3, h4, strong, b) {
  color: #101828 !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) h2 {
  margin: 0 0 12px !important;
  font-size: clamp(1.42rem, 2.5vw, 2.05rem) !important;
  line-height: 1.18 !important;
  letter-spacing: -0.035em !important;
  text-wrap: balance;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) h3 {
  margin: 0 0 9px !important;
  font-size: 1.03rem !important;
  line-height: 1.32 !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) :is(p, li) {
  color: #526178 !important;
  line-height: 1.72 !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) a:not(.sub-btn):not(.btn) {
  color: #b91834 !important;
}

:host(.top-level-service-page) .legacy-document :is(
  .crs-meaning-card, .crs-purpose-grid, .crs-applicant-grid, .crs-product-grid, .crs-feature-grid, .crs-doc-grid, .crs-doc-checklist, .crs-benefit-grid,
  .hallmark-timeline-grid, .hallmark-card-grid, .hallmark-stakeholder-grid, .hallmark-component-grid, .hallmark-two-col,
  .fmcs-definition-grid, .fmcs-eligibility-grid, .fmcs-condition-grid, .fmcs-doc-list, .fmcs-process-grid, .fmcs-renewal-grid, .fmcs-support-grid, .fmcs-benefit-grid,
  .bee-applicant-grid, .bee-feature-grid, .bee-product-columns, .bee-doc-grid, .bee-benefit-grid, .bee-two-col,
  .wpc-stat-grid, .wpc-product-grid, .wpc-testing-grid, .wpc-air-grid, .wpc-document-grid, .wpc-process-steps, .wpc-risk-grid, .wpc-benefit-grid, .wpc-support-grid,
  .tec-stat-grid, .tec-cert-type-grid, .tec-product-grid, .tec-er-grid, .tec-air-grid, .tec-document-grid, .tec-process-steps, .tec-fee-grid, .tec-risk-grid, .tec-role-grid, .tec-benefit-grid,
  .stqc-stat-grid, .stqc-scope-grid, .stqc-assurance-grid, .stqc-evaluation-grid, .stqc-document-grid, .stqc-process-steps, .stqc-location-grid, .stqc-recognition-grid, .stqc-er-grid, .stqc-ppp-grid, .stqc-product-grid, .stqc-risk-grid, .stqc-benefit-grid,
  .lmpc-objective-grid, .lmpc-product-grid, .lmpc-role-grid, .lmpc-license-grid, .lmpc-process-steps, .lmpc-exemption-grid,
  .cdsco-function-grid, .cdsco-class-grid, .cdsco-product-grid, .cdsco-route-grid, .cdsco-doc-grid, .cdsco-process-rail, .cdsco-benefit-grid,
  .fssai-definition-grid, .fssai-objective-grid, .fssai-license-grid, .fssai-function-grid, .fssai-doc-group-grid, .fssai-process-grid, .fssai-risk-grid, .fssai-support-grid, .fssai-benefit-grid,
  .imei-product-grid,
  .peso-stat-grid, .peso-cert-grid, .peso-equipment-grid, .peso-role-grid, .peso-product-grid, .peso-industry-grid, .peso-smpv-grid, .peso-air-grid, .peso-document-grid, .peso-process-steps, .peso-timeline-grid, .peso-risk-grid, .peso-benefit-grid,
  .dgft-function-grid, .dgft-class-grid, .dgft-applicant-grid, .dgft-service-grid, .dgft-process-rail, .dgft-risk-grid, .dgft-benefit-grid, .dgft-two-col,
  .arai-feature-grid, .arai-category-grid, .arai-test-grid, .arai-doc-grid, .arai-process-rail, .arai-benefit-grid, .arai-two-col,
  .overview-stat-grid, .icon-feature-grid, .icon-card-grid, .benefit-grid, .product-icon-grid, .product-route-grid, .comparison-grid, .role-card-grid, .declaration-grid, .document-cluster-grid, .document-columns, .support-scope-grid, .split-card-grid, .risk-grid, .checklist-card-grid, .reporting-grid, .industry-card-grid, .process-step-grid, .related-card-grid, .related-grid, .guide-highlight-row, .step-grid-small, .pill-grid
) {
  display: grid !important;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 205px), 1fr)) !important;
  gap: 15px !important;
  margin: 20px 0 0 !important;
  padding: 0 !important;
}

:host(.top-level-service-page) .legacy-document :is(
  .crs-meaning-card, .crs-purpose-grid, .crs-applicant-grid, .crs-product-grid, .crs-feature-grid, .crs-doc-grid, .crs-doc-checklist, .crs-benefit-grid,
  .hallmark-timeline-grid, .hallmark-card-grid, .hallmark-stakeholder-grid, .hallmark-component-grid, .hallmark-two-col,
  .fmcs-definition-grid, .fmcs-eligibility-grid, .fmcs-condition-grid, .fmcs-doc-list, .fmcs-process-grid, .fmcs-renewal-grid, .fmcs-support-grid, .fmcs-benefit-grid,
  .bee-applicant-grid, .bee-feature-grid, .bee-product-columns, .bee-doc-grid, .bee-benefit-grid, .bee-two-col,
  .wpc-stat-grid, .wpc-product-grid, .wpc-testing-grid, .wpc-air-grid, .wpc-document-grid, .wpc-process-steps, .wpc-risk-grid, .wpc-benefit-grid, .wpc-support-grid,
  .tec-stat-grid, .tec-cert-type-grid, .tec-product-grid, .tec-er-grid, .tec-air-grid, .tec-document-grid, .tec-process-steps, .tec-fee-grid, .tec-risk-grid, .tec-role-grid, .tec-benefit-grid,
  .stqc-stat-grid, .stqc-scope-grid, .stqc-assurance-grid, .stqc-evaluation-grid, .stqc-document-grid, .stqc-process-steps, .stqc-location-grid, .stqc-recognition-grid, .stqc-er-grid, .stqc-ppp-grid, .stqc-product-grid, .stqc-risk-grid, .stqc-benefit-grid,
  .lmpc-objective-grid, .lmpc-product-grid, .lmpc-role-grid, .lmpc-license-grid, .lmpc-process-steps, .lmpc-exemption-grid,
  .cdsco-function-grid, .cdsco-class-grid, .cdsco-product-grid, .cdsco-route-grid, .cdsco-doc-grid, .cdsco-process-rail, .cdsco-benefit-grid,
  .fssai-definition-grid, .fssai-objective-grid, .fssai-license-grid, .fssai-function-grid, .fssai-doc-group-grid, .fssai-process-grid, .fssai-risk-grid, .fssai-support-grid, .fssai-benefit-grid,
  .imei-product-grid,
  .peso-stat-grid, .peso-cert-grid, .peso-equipment-grid, .peso-role-grid, .peso-product-grid, .peso-industry-grid, .peso-smpv-grid, .peso-air-grid, .peso-document-grid, .peso-process-steps, .peso-timeline-grid, .peso-risk-grid, .peso-benefit-grid,
  .dgft-function-grid, .dgft-class-grid, .dgft-applicant-grid, .dgft-service-grid, .dgft-process-rail, .dgft-risk-grid, .dgft-benefit-grid, .dgft-two-col,
  .arai-feature-grid, .arai-category-grid, .arai-test-grid, .arai-doc-grid, .arai-process-rail, .arai-benefit-grid, .arai-two-col,
  .overview-stat-grid, .icon-feature-grid, .icon-card-grid, .benefit-grid, .product-icon-grid, .product-route-grid, .comparison-grid, .role-card-grid, .declaration-grid, .document-cluster-grid, .document-columns, .support-scope-grid, .split-card-grid, .risk-grid, .checklist-card-grid, .reporting-grid, .industry-card-grid, .process-step-grid, .related-card-grid, .related-grid, .guide-highlight-row, .step-grid-small, .pill-grid
) > :is(div, article, a, li) {
  position: relative !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 19px !important;
  overflow: hidden !important;
  border: 1px solid rgba(18, 21, 37, 0.09) !important;
  border-radius: 17px !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%) !important;
  color: #475467 !important;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.045) !important;
  text-decoration: none !important;
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease !important;
}

:host(.top-level-service-page) .legacy-document :is(
  .crs-product-grid, .fmcs-support-grid, .bee-product-columns, .wpc-product-grid, .tec-product-grid, .stqc-product-grid,
  .lmpc-product-grid, .cdsco-product-grid, .fssai-license-grid, .imei-product-grid, .peso-product-grid,
  .dgft-service-grid, .arai-category-grid, .product-icon-grid, .product-route-grid, .related-card-grid, .related-grid
) > :is(a, article, div):hover {
  z-index: 1 !important;
  border-color: rgba(196, 30, 58, 0.26) !important;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.09) !important;
  transform: translateY(-4px) !important;
}

:host(.top-level-service-page) .legacy-document :is(
  .crs-meaning-card, .crs-purpose-grid, .crs-applicant-grid, .crs-product-grid, .crs-feature-grid, .crs-doc-grid, .crs-doc-checklist, .crs-benefit-grid,
  .hallmark-timeline-grid, .hallmark-card-grid, .hallmark-stakeholder-grid, .hallmark-component-grid, .hallmark-two-col,
  .fmcs-definition-grid, .fmcs-eligibility-grid, .fmcs-condition-grid, .fmcs-doc-list, .fmcs-process-grid, .fmcs-renewal-grid, .fmcs-support-grid, .fmcs-benefit-grid,
  .bee-applicant-grid, .bee-feature-grid, .bee-product-columns, .bee-doc-grid, .bee-benefit-grid, .bee-two-col,
  .wpc-stat-grid, .wpc-product-grid, .wpc-testing-grid, .wpc-air-grid, .wpc-document-grid, .wpc-process-steps, .wpc-risk-grid, .wpc-benefit-grid, .wpc-support-grid,
  .tec-stat-grid, .tec-cert-type-grid, .tec-product-grid, .tec-er-grid, .tec-air-grid, .tec-document-grid, .tec-process-steps, .tec-fee-grid, .tec-risk-grid, .tec-role-grid, .tec-benefit-grid,
  .stqc-stat-grid, .stqc-scope-grid, .stqc-assurance-grid, .stqc-evaluation-grid, .stqc-document-grid, .stqc-process-steps, .stqc-location-grid, .stqc-recognition-grid, .stqc-er-grid, .stqc-ppp-grid, .stqc-product-grid, .stqc-risk-grid, .stqc-benefit-grid,
  .lmpc-objective-grid, .lmpc-product-grid, .lmpc-role-grid, .lmpc-license-grid, .lmpc-process-steps, .lmpc-exemption-grid,
  .cdsco-function-grid, .cdsco-class-grid, .cdsco-product-grid, .cdsco-route-grid, .cdsco-doc-grid, .cdsco-process-rail, .cdsco-benefit-grid,
  .fssai-definition-grid, .fssai-objective-grid, .fssai-license-grid, .fssai-function-grid, .fssai-doc-group-grid, .fssai-process-grid, .fssai-risk-grid, .fssai-support-grid, .fssai-benefit-grid,
  .imei-product-grid,
  .peso-stat-grid, .peso-cert-grid, .peso-equipment-grid, .peso-role-grid, .peso-product-grid, .peso-industry-grid, .peso-smpv-grid, .peso-air-grid, .peso-document-grid, .peso-process-steps, .peso-timeline-grid, .peso-risk-grid, .peso-benefit-grid,
  .dgft-function-grid, .dgft-class-grid, .dgft-applicant-grid, .dgft-service-grid, .dgft-process-rail, .dgft-risk-grid, .dgft-benefit-grid, .dgft-two-col,
  .arai-feature-grid, .arai-category-grid, .arai-test-grid, .arai-doc-grid, .arai-process-rail, .arai-benefit-grid, .arai-two-col,
  .overview-stat-grid, .icon-feature-grid, .icon-card-grid, .benefit-grid, .product-icon-grid, .product-route-grid, .comparison-grid, .role-card-grid, .declaration-grid, .document-cluster-grid, .document-columns, .support-scope-grid, .split-card-grid, .risk-grid, .checklist-card-grid, .reporting-grid, .industry-card-grid, .process-step-grid, .related-card-grid, .related-grid, .guide-highlight-row, .step-grid-small, .pill-grid
) > :is(div, article, a, li)::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #c41e3a, #1f4f8f);
  opacity: 0.78;
}

:host(.top-level-service-page) .legacy-document :is(.guide-list, .clean-list, .danger-list, .frequency-checklist) {
  display: grid !important;
  gap: 10px !important;
  margin: 17px 0 0 !important;
  padding: 0 !important;
  list-style: none !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-list, .clean-list, .danger-list, .frequency-checklist) > li {
  position: relative !important;
  padding: 10px 12px 10px 38px !important;
  border: 1px solid rgba(18, 21, 37, 0.075) !important;
  border-radius: 12px !important;
  background: #f8fafc !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-list, .clean-list, .danger-list, .frequency-checklist) > li::before {
  content: "✓" !important;
  position: absolute !important;
  top: 10px !important;
  left: 13px !important;
  color: #c41e3a !important;
  font-weight: 950 !important;
}

:host(.top-level-service-page) .legacy-document :is(.responsive-table, .crs-product-table-wrap, .hallmark-table-wrap, .fssai-table-wrap, .arai-table-wrap, .dgft-table-wrap, .cdsco-table-wrap) {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  margin-top: 20px !important;
  overflow-x: auto !important;
  border: 1px solid rgba(18, 21, 37, 0.1) !important;
  border-radius: 17px !important;
  background: #ffffff !important;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.045) !important;
  -webkit-overflow-scrolling: touch;
}

:host(.top-level-service-page) .legacy-document :is(.responsive-table, .product-route-table, .compact-table, .compact-fee-table) table,
:host(.top-level-service-page) .legacy-document :is(table.crs-product-table, table.hallmark-table, table.fssai-table, table.arai-table, table.dgft-table, table.cdsco-table, table.comparison-table, table.isi-comparison-table) {
  width: 100% !important;
  min-width: 680px !important;
  margin: 0 !important;
  border-collapse: collapse !important;
  background: #ffffff !important;
}

:host(.top-level-service-page) .legacy-document :is(.responsive-table, .product-route-table, .compact-table, .compact-fee-table) table th,
:host(.top-level-service-page) .legacy-document :is(table.crs-product-table, table.hallmark-table, table.fssai-table, table.arai-table, table.dgft-table, table.cdsco-table, table.comparison-table, table.isi-comparison-table) th {
  padding: 14px 15px !important;
  border: 0 !important;
  background: #172033 !important;
  color: #ffffff !important;
  font-size: 0.76rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.055em !important;
  text-align: left !important;
  text-transform: uppercase !important;
  vertical-align: top !important;
}

:host(.top-level-service-page) .legacy-document :is(.responsive-table, .product-route-table, .compact-table, .compact-fee-table) table td,
:host(.top-level-service-page) .legacy-document :is(table.crs-product-table, table.hallmark-table, table.fssai-table, table.arai-table, table.dgft-table, table.cdsco-table, table.comparison-table, table.isi-comparison-table) td {
  padding: 14px 15px !important;
  border: 0 !important;
  border-bottom: 1px solid rgba(18, 21, 37, 0.075) !important;
  color: #526178 !important;
  line-height: 1.55 !important;
  text-align: left !important;
  vertical-align: top !important;
}

:host(.top-level-service-page) .legacy-document :is(.responsive-table, .product-route-table, .compact-table, .compact-fee-table) table tbody tr:nth-child(even) td,
:host(.top-level-service-page) .legacy-document :is(table.crs-product-table, table.hallmark-table, table.fssai-table, table.arai-table, table.dgft-table, table.cdsco-table, table.comparison-table, table.isi-comparison-table) tbody tr:nth-child(even) td {
  background: #f8fafc !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-faq, .faq-list, .sub-faq, .arai-faq-list, .bee-faq-list, .cdsco-faq-list, .dgft-faq-list) {
  display: grid !important;
  gap: 12px !important;
  margin-top: 19px !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-faq, .faq-list, .sub-faq, .arai-faq-list, .bee-faq-list, .cdsco-faq-list, .dgft-faq-list) details {
  padding: 0 18px !important;
  border: 1px solid rgba(18, 21, 37, 0.09) !important;
  border-radius: 14px !important;
  background: #f9fafc !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-faq, .faq-list, .sub-faq, .arai-faq-list, .bee-faq-list, .cdsco-faq-list, .dgft-faq-list) summary {
  padding: 17px 28px 17px 0 !important;
  color: #101828 !important;
  font-weight: 850 !important;
  line-height: 1.45 !important;
  cursor: pointer !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-faq, .faq-list, .sub-faq, .arai-faq-list, .bee-faq-list, .cdsco-faq-list, .dgft-faq-list) details > :not(summary) {
  margin: 0 0 17px !important;
}

:host(.top-level-service-page) .legacy-document :is(.notice-card, .soft-card, .guide-definition-card, .feature-split-card, .guide-question-panel, .process-visual-card, .fmcs-note-card, .fmcs-air-card, .fmcs-mark-panel, .fssai-objective-card, .fssai-validity-card, .arai-concept-card, .arai-validity-card, .wpc-definition-card, .tac-definition, .tac-number-card, .imei-meaning-block) {
  margin-top: 18px !important;
  padding: 20px !important;
  border: 1px solid rgba(31, 79, 143, 0.13) !important;
  border-radius: 17px !important;
  background: linear-gradient(145deg, #f7faff, #ffffff) !important;
  color: #475467 !important;
}

:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .sub-cta, .subservice-final-cta) {
  margin-top: 22px !important;
  padding: clamp(25px, 4vw, 40px) !important;
  overflow: hidden !important;
  border: 1px solid rgba(196, 30, 58, 0.18) !important;
  border-radius: 24px !important;
  background:
    radial-gradient(circle at 92% 12%, rgba(196, 30, 58, 0.22), transparent 34%),
    linear-gradient(135deg, #172033 0%, #222e45 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.16) !important;
}

:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .sub-cta, .subservice-final-cta) :is(h2, h3, strong) {
  color: #ffffff !important;
}

:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .sub-cta, .subservice-final-cta) p {
  color: rgba(255, 255, 255, 0.76) !important;
}

:host(.top-level-service-page) .legacy-document :is(.crs-process-visual, .crs-process-flow-visual, .fmcs-process-visual, .fssai-process-visual, .isi-process-visual, .service-process-visual, .process-visual-section, .process-image-section, .process-image-frame) {
  width: 100% !important;
  margin-top: 20px !important;
  overflow: hidden !important;
  border: 1px solid rgba(18, 21, 37, 0.09) !important;
  border-radius: 20px !important;
  background: #f8fafc !important;
}

:host(.top-level-service-page) .legacy-document :is(img, svg) {
  max-width: 100%;
}

:host(.top-level-service-page) .legacy-document :is(.subservice-hero-card, .subservice-visual-card) {
  position: relative !important;
  z-index: 1 !important;
  overflow: hidden !important;
  padding: clamp(25px, 3vw, 36px) !important;
  border: 1px solid rgba(18, 21, 37, 0.11) !important;
  border-radius: 26px !important;
  background: rgba(255, 255, 255, 0.97) !important;
  color: #526178 !important;
  box-shadow: 0 28px 74px rgba(18, 21, 37, 0.13) !important;
}

:host(.top-level-service-page) .legacy-document :is(.subservice-hero-card, .subservice-visual-card) :is(h2, strong) {
  color: #101828 !important;
}

:host(.top-level-service-page) .legacy-document .subservice-logo-card {
  display: grid !important;
  width: 112px !important;
  height: 112px !important;
  margin: 0 0 20px !important;
  place-items: center !important;
  border: 1px solid rgba(18, 21, 37, 0.09) !important;
  border-radius: 22px !important;
  background: #ffffff !important;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08) !important;
}

:host(.top-level-service-page) .legacy-document .subservice-logo-card img {
  width: 82px !important;
  height: 82px !important;
  object-fit: contain !important;
}

/* TEC / MTCTE page: stable columns, complete cards and local SVG icons. */
:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__grid,
:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-layout {
  width: min(1220px, calc(100% - 44px)) !important;
  max-width: 1220px !important;
  margin-inline: auto !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__grid {
  grid-template-columns: minmax(0, 1.36fr) minmax(310px, 380px) !important;
  gap: clamp(34px, 5vw, 70px) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-hero__content {
  display: flex !important;
  min-width: 0 !important;
  flex-direction: column !important;
  align-items: flex-start !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-hero__content > p:not(.eyebrow) {
  max-width: 780px !important;
  margin: 15px 0 0 !important;
  color: #526178 !important;
  font-size: 1rem !important;
  line-height: 1.7 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-hero__content > .hero-lead {
  margin-top: 20px !important;
  color: #344054 !important;
  font-size: clamp(1.06rem, 1.5vw, 1.2rem) !important;
  font-weight: 750 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .hero-actions {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 12px !important;
  margin-top: 28px !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .hero-actions .btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 50px !important;
  padding: 0 22px !important;
  border: 1px solid rgba(18, 21, 37, 0.12) !important;
  border-radius: 999px !important;
  font-weight: 850 !important;
  text-decoration: none !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .hero-actions .btn-primary {
  border-color: #c41e3a !important;
  background: #c41e3a !important;
  color: #ffffff !important;
  box-shadow: 0 15px 34px rgba(196, 30, 58, 0.2) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .hero-actions .btn-ghost {
  background: #ffffff !important;
  color: #172033 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__card {
  display: grid !important;
  min-width: 0 !important;
  align-content: center !important;
  justify-items: start !important;
  padding: clamp(28px, 3.4vw, 40px) !important;
  border-radius: 28px !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .authority-card-icon {
  display: grid !important;
  width: 112px !important;
  height: 112px !important;
  margin-bottom: 20px !important;
  place-items: center !important;
  border-radius: 22px !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .authority-card-icon::before {
  display: none !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .authority-card-icon img {
  width: 84px !important;
  height: 84px !important;
  object-fit: contain !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__card h2 {
  margin: 0 0 10px !important;
  color: #101828 !important;
  font-size: 1.65rem !important;
  letter-spacing: -0.035em !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__card > p {
  margin: 0 !important;
  color: #526178 !important;
  line-height: 1.65 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-layout {
  grid-template-columns: 268px minmax(0, 1fr) !important;
  gap: clamp(26px, 3.5vw, 44px) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-sidebar {
  width: 100% !important;
  max-height: calc(100vh - 124px) !important;
  padding: 0 5px 16px 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  scrollbar-width: thin;
  scrollbar-color: rgba(196, 30, 58, 0.34) transparent;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-content {
  width: 100% !important;
  max-width: 100% !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-content > .guide-block {
  width: 100% !important;
  max-width: 100% !important;
  scroll-margin-top: 112px;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document :is(.tec-stat-grid, .tec-role-grid, .tec-product-grid, .tec-risk-grid) {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document :is(.tec-cert-type-grid, .tec-er-grid, .tec-document-grid) {
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-fee-grid {
  grid-template-columns: minmax(0, 1fr) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-process-steps {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document :is(.tec-product-grid, .tec-air-grid, .support-scope-grid) > span {
  display: flex !important;
  min-width: 0 !important;
  min-height: 72px !important;
  margin: 0 !important;
  align-items: center !important;
  gap: 12px !important;
  padding: 15px 16px !important;
  border: 1px solid rgba(32, 127, 185, 0.15) !important;
  border-radius: 16px !important;
  background: linear-gradient(145deg, #ffffff, #f5f9fd) !important;
  color: #344054 !important;
  font-size: 0.9rem !important;
  font-weight: 800 !important;
  line-height: 1.4 !important;
  box-shadow: 0 9px 24px rgba(15, 23, 42, 0.045) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document [data-icon="document"] {
  --ci-local-icon: var(--ci-icon-doc);
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document [data-icon="lab"] {
  --ci-local-icon: var(--ci-icon-lab);
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document i.legacy-fa-icon {
  display: inline-flex !important;
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  margin: 0 0 12px !important;
  align-items: center !important;
  justify-content: center !important;
  border: 1px solid rgba(32, 127, 185, 0.13) !important;
  border-radius: 14px !important;
  background: rgba(32, 127, 185, 0.085) !important;
  color: transparent !important;
  font-size: 0 !important;
  font-style: normal !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document i.legacy-fa-icon::before {
  width: 23px !important;
  height: 23px !important;
  margin: 0 !important;
  background-position: center !important;
  background-size: contain !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-product-grid i.legacy-fa-icon,
:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-risk-grid i.legacy-fa-icon {
  margin: 0 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-process-steps article {
  min-height: 190px !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-process-steps article > span {
  display: inline-flex !important;
  width: 42px !important;
  height: 42px !important;
  margin: 0 0 14px !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 50% !important;
  background: #207fb9 !important;
  color: #ffffff !important;
  font-weight: 900 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-fee-grid > article {
  min-width: 0 !important;
  overflow: hidden !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-fee-grid :is(.responsive-table, .table-wrap) {
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: visible !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-fee-grid table {
  width: 100% !important;
  min-width: 0 !important;
  table-layout: fixed !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-fee-grid :is(th, td) {
  min-width: 0 !important;
  overflow-wrap: anywhere !important;
  white-space: normal !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .search-panel input {
  width: 100% !important;
  min-height: 50px !important;
  margin: 6px 0 0 !important;
  padding: 0 17px !important;
  border: 1px solid rgba(18, 21, 37, 0.12) !important;
  border-radius: 14px !important;
  background: #f8fafc !important;
  color: #101828 !important;
  font: inherit !important;
  outline: none !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .search-panel input:focus {
  border-color: rgba(32, 127, 185, 0.5) !important;
  box-shadow: 0 0 0 4px rgba(32, 127, 185, 0.1) !important;
}

@media (max-width: 980px) {
  :host(.top-level-service-page) .legacy-document :is(.guide-layout, .service-guide-layout, .subservice-body.shell, .subservice-body > .shell) {
    grid-template-columns: 1fr !important;
  }

  :host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar) {
    position: static !important;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)) !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__grid,
  :host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-layout {
    grid-template-columns: 1fr !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-sidebar {
    max-height: none !important;
    padding-right: 0 !important;
    overflow: visible !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document :is(.tec-stat-grid, .tec-role-grid, .tec-product-grid, .tec-risk-grid, .tec-process-steps) {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}

@media (max-width: 640px) {
  :host(.top-level-service-page) .legacy-document :is(.guide-layout-section, .service-guide-section, .subservice-body) {
    padding: 30px 0 !important;
  }

  :host(.top-level-service-page) .legacy-document :is(.guide-layout, .service-guide-layout, .subservice-body.shell, .subservice-body > .shell) {
    width: min(100% - 28px, 1180px) !important;
    gap: 16px !important;
  }

  :host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) {
    padding: 20px !important;
    border-radius: 20px !important;
  }

  :host(.top-level-service-page) .legacy-document :is(.crs-purpose-grid, .crs-applicant-grid, .crs-product-grid, .crs-feature-grid, .hallmark-card-grid, .hallmark-stakeholder-grid, .hallmark-component-grid) {
    grid-template-columns: 1fr !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .service-guide-hero__grid,
  :host(.legacy-page-tec-mtcte-certification) .legacy-document .service-guide-layout {
    width: min(100% - 28px, 1220px) !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document :is(.tec-stat-grid, .tec-role-grid, .tec-product-grid, .tec-cert-type-grid, .tec-er-grid, .tec-document-grid, .tec-fee-grid, .tec-risk-grid, .tec-process-steps) {
    grid-template-columns: 1fr !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .hero-actions {
    width: 100% !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .tec-hero .hero-actions .btn {
    width: 100% !important;
  }
}

:host(.isi-page) .guide-block.isi-product-search-panel {
  display: block !important;
  grid-template-columns: none !important;
  align-items: initial !important;
  gap: 0 !important;
  padding: clamp(22px, 3vw, 34px) !important;
  overflow: hidden;
  border: 1px solid rgba(18, 21, 37, 0.1) !important;
  border-radius: 30px !important;
  background: #ffffff !important;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08) !important;
}

:host(.isi-page) .isi-product-search-box {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  gap: 12px 18px !important;
  align-items: end !important;
  margin: 22px 0 18px !important;
  padding: 18px !important;
  border: 1px solid rgba(18, 21, 37, 0.09) !important;
  border-radius: 20px !important;
  background: #f7f8fb !important;
}

:host(.isi-page) .isi-product-search-box label {
  grid-column: 1 / -1;
  justify-self: start !important;
  margin: 0 !important;
  color: #111827 !important;
  font-size: 0.78rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.09em !important;
  text-transform: uppercase !important;
}

:host(.isi-page) .isi-product-search-box input {
  width: 100% !important;
  min-height: 52px !important;
  margin: 0 !important;
  padding: 0 18px 0 48px !important;
  border: 1px solid rgba(18, 21, 37, 0.14) !important;
  border-radius: 14px !important;
  background-color: #ffffff !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: 17px center !important;
  color: #111827 !important;
  font: inherit !important;
  font-weight: 700 !important;
  outline: none !important;
}

:host(.isi-page) .isi-product-search-box input:focus {
  border-color: rgba(196, 30, 58, 0.5) !important;
  box-shadow: 0 0 0 4px rgba(196, 30, 58, 0.09) !important;
}

:host(.isi-page) .isi-product-search-box input::placeholder {
  color: #8b93a5 !important;
}

:host(.isi-page) .isi-search-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-width: 150px;
}

:host(.isi-page) .isi-official-source {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 16px;
  padding: 13px 16px;
  border: 1px solid rgba(21, 128, 61, 0.16);
  border-radius: 14px;
  background: #f2fbf5;
  color: #356044;
  font-size: 0.84rem;
  font-weight: 750;
}

:host(.isi-page) .isi-official-source a {
  flex: 0 0 auto;
  color: #16743a !important;
  font-weight: 900 !important;
  text-decoration: none !important;
}

:host(.isi-page) .isi-search-count {
  color: #667085 !important;
  font-size: 0.84rem;
  font-weight: 800;
  white-space: nowrap;
}

:host(.isi-page) .isi-search-clear {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid rgba(196, 30, 58, 0.2);
  border-radius: 999px;
  background: #ffffff;
  color: #c41e3a;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 850;
  cursor: pointer;
}

:host(.isi-page) .isi-search-clear[hidden],
:host(.isi-page) .isi-products-table tr[hidden],
:host(.isi-page) .isi-search-empty[hidden] {
  display: none !important;
}

:host(.isi-page) .isi-table-wrap {
  width: 100% !important;
  max-height: min(620px, 70vh) !important;
  overflow: auto !important;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #c41e3a #eef1f6;
  border: 1px solid rgba(18, 21, 37, 0.1) !important;
  border-radius: 20px !important;
  background: #ffffff !important;
  box-shadow: none !important;
}

:host(.isi-page) .isi-table-wrap::-webkit-scrollbar,
:host .service-product-directory__list::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

:host(.isi-page) .isi-table-wrap::-webkit-scrollbar-track,
:host .service-product-directory__list::-webkit-scrollbar-track {
  border-radius: 999px;
  background: #eef1f6;
}

:host(.isi-page) .isi-table-wrap::-webkit-scrollbar-thumb,
:host .service-product-directory__list::-webkit-scrollbar-thumb {
  border: 2px solid #eef1f6;
  border-radius: 999px;
  background: linear-gradient(180deg, #d92346, #9f1731);
}

:host(.isi-page) .isi-products-table {
  width: 100% !important;
  min-width: 840px !important;
  table-layout: fixed !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  background: #ffffff !important;
}

:host(.isi-page) .isi-products-table th,
:host(.isi-page) .isi-products-table td {
  box-sizing: border-box !important;
  padding: 15px 16px !important;
  text-align: left !important;
  vertical-align: top !important;
  line-height: 1.45 !important;
  overflow-wrap: break-word !important;
}

:host(.isi-page) .isi-products-table thead th {
  position: sticky !important;
  top: 0 !important;
  z-index: 3 !important;
  border: 0 !important;
  background: #111827 !important;
  color: #ffffff !important;
  font-size: 0.76rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.07em !important;
  text-transform: uppercase !important;
  white-space: normal !important;
}

:host(.isi-page) .isi-products-table tbody td {
  border-right: 1px solid rgba(18, 21, 37, 0.06) !important;
  border-bottom: 1px solid rgba(18, 21, 37, 0.08) !important;
  background: #ffffff !important;
  color: #475467 !important;
  font-size: 0.9rem !important;
}

:host(.isi-page) .isi-products-table tbody td:first-child,
:host(.isi-page) .isi-products-table thead th:first-child {
  text-align: center !important;
}

:host(.isi-page) .isi-products-table tbody td:first-child {
  color: #c41e3a !important;
  font-size: 0.82rem !important;
  font-weight: 900 !important;
}

:host(.isi-page) .isi-products-table tbody td:nth-child(2) {
  color: #1f2937 !important;
  font-weight: 800 !important;
}

:host(.isi-page) .isi-products-table tbody tr:nth-child(even) td {
  background: #f8fafc !important;
}

:host(.isi-page) .isi-products-table tbody tr:hover td {
  background: #fff4f6 !important;
}

:host(.isi-page) .isi-products-table tbody td:last-child {
  border-right: 0 !important;
}

:host(.isi-page) .isi-products-table a {
  color: #b91834 !important;
  font-weight: 800 !important;
  text-decoration: none !important;
}

:host(.isi-page) .isi-products-table .isi-product-name {
  color: #202939 !important;
  font-weight: 800 !important;
}

:host(.isi-page) .isi-products-table .isi-qco-link {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 6px 10px;
  border: 1px solid rgba(196, 30, 58, 0.16);
  border-radius: 999px;
  background: #fff6f7;
  font-size: 0.78rem !important;
  line-height: 1.25 !important;
}

:host(.isi-page) .isi-search-empty {
  padding: 26px;
  border: 1px dashed rgba(196, 30, 58, 0.28);
  border-radius: 16px;
  background: #fff7f8;
  color: #667085;
  text-align: center;
  font-weight: 750;
}

@media (max-width: 760px) {
  :host(.isi-page) .guide-block.isi-product-search-panel {
    padding: 18px !important;
    border-radius: 24px !important;
  }

  :host(.isi-page) .isi-product-search-box {
    grid-template-columns: 1fr !important;
    padding: 14px !important;
  }

  :host(.isi-page) .isi-search-meta {
    justify-content: space-between;
    min-width: 0;
  }

  :host(.isi-page) .isi-official-source {
    align-items: flex-start;
    flex-direction: column;
  }

  :host(.isi-page) .isi-table-wrap {
    max-height: min(720px, 72vh) !important;
    padding-right: 5px;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    border: 0 !important;
    background: transparent !important;
  }

  :host(.isi-page) .isi-products-table,
  :host(.isi-page) .isi-products-table thead,
  :host(.isi-page) .isi-products-table tbody,
  :host(.isi-page) .isi-products-table tr,
  :host(.isi-page) .isi-products-table th,
  :host(.isi-page) .isi-products-table td {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  :host(.isi-page) .isi-products-table thead,
  :host(.isi-page) .isi-products-table colgroup {
    display: none !important;
  }

  :host(.isi-page) .isi-products-table tbody {
    display: grid !important;
    gap: 14px !important;
  }

  :host(.isi-page) .isi-products-table tbody tr {
    overflow: hidden;
    border: 1px solid rgba(18, 21, 37, 0.1);
    border-radius: 18px;
    background: #ffffff;
  }

  :host(.isi-page) .isi-products-table tbody td {
    display: grid !important;
    grid-template-columns: 112px minmax(0, 1fr) !important;
    gap: 12px !important;
    padding: 12px 14px !important;
    border-right: 0 !important;
    text-align: left !important;
  }

  :host(.isi-page) .isi-products-table tbody td::before {
    color: #667085;
    font-size: 0.69rem;
    font-weight: 900;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  :host(.isi-page) .isi-products-table tbody td:nth-child(1)::before { content: "Sr. No."; }
  :host(.isi-page) .isi-products-table tbody td:nth-child(2)::before { content: "Indian Standard"; }
  :host(.isi-page) .isi-products-table tbody td:nth-child(3)::before { content: "Product"; }
  :host(.isi-page) .isi-products-table tbody td:nth-child(4)::before { content: "Category"; }
  :host(.isi-page) .isi-products-table tbody td:nth-child(5)::before { content: "QCO / Notification"; }
}

/* ISI marking-fee calculator sourced from the supplied BIS fee schedule. */
:host(.isi-page) .isi-marking-calculator {
  position: relative;
  isolation: isolate;
  display: grid;
  gap: 24px;
  margin-top: 24px;
  padding: clamp(22px, 3vw, 34px);
  overflow: visible;
  border: 1px solid rgba(18, 21, 37, .10);
  border-radius: 28px;
  background:
    radial-gradient(circle at 92% 5%, rgba(36, 107, 253, .10), transparent 29%),
    radial-gradient(circle at 5% 96%, rgba(229, 31, 70, .11), transparent 30%),
    linear-gradient(145deg, #ffffff 0%, #f8faff 100%);
  box-shadow: 0 24px 58px rgba(20, 30, 52, .09);
}

:host(.isi-page) .isi-marking-calculator::before {
  position: absolute;
  z-index: -1;
  inset: 0;
  border-radius: inherit;
  background-image:
    linear-gradient(rgba(23, 32, 51, .035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(23, 32, 51, .035) 1px, transparent 1px);
  background-size: 36px 36px;
  content: "";
  mask-image: linear-gradient(to bottom, #000, transparent 75%);
}

:host(.isi-page) .isi-marking-calculator__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 22px;
}

:host(.isi-page) .isi-marking-calculator__head span,
:host(.isi-page) .isi-marking-field > span {
  display: block;
  color: #c41e3a;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: .14em;
  text-transform: uppercase;
}

:host(.isi-page) .isi-marking-calculator__head h3 {
  margin: 7px 0 6px !important;
  color: #111827 !important;
  font-size: clamp(22px, 2.4vw, 32px) !important;
  line-height: 1.05 !important;
  letter-spacing: -.035em !important;
}

:host(.isi-page) .isi-marking-calculator__head p {
  max-width: 650px;
  margin: 0 !important;
  color: #667085 !important;
  font-size: 13px !important;
  line-height: 1.6 !important;
}

:host(.isi-page) .isi-marking-calculator__count {
  flex: 0 0 auto;
  padding: 9px 12px;
  border: 1px solid rgba(196, 30, 58, .14);
  border-radius: 999px;
  background: #fff5f7;
  color: #ac1832;
  font-size: 11px;
  font-weight: 900;
  white-space: nowrap;
}

:host(.isi-page) .isi-marking-calculator__controls {
  display: grid;
  grid-template-columns: minmax(290px, .8fr) minmax(0, 1.2fr);
  gap: 18px;
  align-items: start;
}

:host(.isi-page) .isi-marking-field {
  min-width: 0;
  padding: 18px;
  border: 1px solid rgba(18, 21, 37, .08);
  border-radius: 21px;
  background: rgba(255, 255, 255, .86);
}

:host(.isi-page) .isi-marking-size-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
  margin-top: 12px;
}

:host(.isi-page) .isi-marking-size-option {
  position: relative;
  cursor: pointer;
}

:host(.isi-page) .isi-marking-size-option input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

:host(.isi-page) .isi-marking-size-option > span {
  display: grid;
  min-height: 72px;
  align-content: center;
  padding: 11px 13px;
  border: 1px solid #dfe4ec;
  border-radius: 15px;
  background: #ffffff;
  transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease, background .18s ease;
}

:host(.isi-page) .isi-marking-size-option strong {
  color: #1d2939;
  font-size: 13px;
  line-height: 1.2;
}

:host(.isi-page) .isi-marking-size-option small {
  margin-top: 4px;
  color: #7a8495;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .05em;
  text-transform: uppercase;
}

:host(.isi-page) .isi-marking-size-option input:checked + span {
  border-color: #c41e3a;
  background: linear-gradient(145deg, #fff7f8, #ffffff);
  box-shadow: 0 10px 26px rgba(196, 30, 58, .12), inset 0 0 0 1px rgba(196, 30, 58, .09);
  transform: translateY(-2px);
}

:host(.isi-page) .isi-marking-size-option input:focus-visible + span {
  outline: 3px solid rgba(36, 107, 253, .22);
  outline-offset: 2px;
}

:host(.isi-page) .isi-marking-search-wrap {
  position: relative;
  margin-top: 12px;
}

:host(.isi-page) .isi-marking-search {
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  padding: 0 46px 0 16px;
  border: 1px solid #d7dde7;
  border-radius: 15px;
  background: #ffffff;
  color: #1b2434;
  font: inherit;
  font-size: 14px;
  font-weight: 750;
  outline: none;
  transition: border-color .18s ease, box-shadow .18s ease;
}

:host(.isi-page) .isi-marking-search:focus {
  border-color: #c41e3a;
  box-shadow: 0 0 0 4px rgba(196, 30, 58, .09);
}

:host(.isi-page) .isi-marking-search-icon {
  position: absolute;
  top: 17px;
  right: 17px;
  width: 18px;
  height: 18px;
  border: 2px solid #8b95a5;
  border-radius: 50%;
  pointer-events: none;
}

:host(.isi-page) .isi-marking-search-icon::after {
  position: absolute;
  right: -5px;
  bottom: -3px;
  width: 7px;
  height: 2px;
  transform: rotate(45deg);
  border-radius: 999px;
  background: #8b95a5;
  content: "";
}

:host(.isi-page) .isi-marking-search-status {
  min-height: 18px;
  margin: 8px 2px 0;
  color: #7a8495;
  font-size: 11px;
  line-height: 1.4;
}

:host(.isi-page) .isi-marking-results {
  display: grid;
  max-height: 310px;
  margin-top: 8px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #dfe4ec;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(17, 24, 39, .12);
}

:host(.isi-page) .isi-marking-results[hidden] {
  display: none !important;
}

:host(.isi-page) .isi-marking-result-option {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 12px 14px;
  border: 0;
  border-bottom: 1px solid #eef1f5;
  background: #ffffff;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

:host(.isi-page) .isi-marking-result-option:last-child { border-bottom: 0; }
:host(.isi-page) .isi-marking-result-option:hover,
:host(.isi-page) .isi-marking-result-option:focus-visible { background: #fff5f7; outline: none; }

:host(.isi-page) .isi-marking-result-option strong {
  display: block;
  color: #111827;
  font-size: 12px;
  line-height: 1.3;
}

:host(.isi-page) .isi-marking-result-option small {
  display: -webkit-box;
  margin-top: 3px;
  overflow: hidden;
  color: #697386;
  font-size: 10px;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

:host(.isi-page) .isi-marking-result-option b {
  color: #c41e3a;
  font-size: 11px;
  white-space: nowrap;
}

:host(.isi-page) .isi-marking-result-option__meta {
  display: grid;
  justify-items: end;
  gap: 6px;
}

:host(.isi-page) .isi-marking-route {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 8px;
  font-style: normal;
  font-weight: 950;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}

:host(.isi-page) .isi-marking-route--mandatory {
  border: 1px solid rgba(196, 30, 58, .18);
  background: #fff0f3;
  color: #b41632;
}

:host(.isi-page) .isi-marking-route--voluntary {
  border: 1px solid rgba(31, 111, 235, .17);
  background: #edf5ff;
  color: #205eb9;
}

:host(.isi-page) .isi-marking-calculator__result {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 22px;
  align-items: center;
  padding: clamp(20px, 3vw, 30px);
  overflow: hidden;
  border-radius: 23px;
  background:
    radial-gradient(circle at 90% 20%, rgba(229, 31, 70, .27), transparent 30%),
    linear-gradient(135deg, #111827 0%, #17233d 100%);
  color: #ffffff;
  box-shadow: 0 22px 52px rgba(17, 24, 39, .20);
}

:host(.isi-page) .isi-marking-result-copy > span {
  color: #ff9aad !important;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: .15em;
  text-transform: uppercase;
}

:host(.isi-page) .isi-marking-result-copy strong {
  display: block;
  margin-top: 8px;
  color: #ffffff !important;
  font-size: clamp(17px, 2vw, 23px);
  line-height: 1.25;
}

:host(.isi-page) .isi-marking-result-copy p {
  max-width: 720px;
  margin: 7px 0 0 !important;
  color: #d2d9e5 !important;
  font-size: 12px !important;
  line-height: 1.55 !important;
}

:host(.isi-page) .isi-marking-result-value {
  min-width: 175px;
  text-align: right;
}

:host(.isi-page) .isi-marking-result-value strong {
  display: block;
  color: #ffffff !important;
  font-size: clamp(28px, 4vw, 44px);
  line-height: 1;
  letter-spacing: -.05em;
  white-space: nowrap;
}

:host(.isi-page) .isi-marking-result-value span {
  display: block;
  margin-top: 8px;
  color: #ff9aad !important;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .09em;
  text-transform: uppercase;
}

:host(.isi-page) .isi-marking-calculator__details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

:host(.isi-page) .isi-marking-calculator__details[hidden] {
  display: none !important;
}

:host(.isi-page) .isi-marking-detail-card,
:host(.isi-page) .isi-marking-rate-card {
  min-width: 0;
  padding: 17px;
  border: 1px solid rgba(18, 21, 37, .09);
  border-radius: 18px;
  background: rgba(255, 255, 255, .92);
  box-shadow: 0 10px 26px rgba(17, 24, 39, .055);
}

:host(.isi-page) .isi-marking-detail-card > span,
:host(.isi-page) .isi-marking-rate-card > div:first-child > span,
:host(.isi-page) .isi-marking-rate-slab > span {
  display: block;
  color: #778196;
  font-size: 8px;
  font-weight: 950;
  letter-spacing: .11em;
  text-transform: uppercase;
}

:host(.isi-page) .isi-marking-detail-card > strong {
  display: inline-flex;
  width: fit-content;
  margin-top: 9px;
  color: #111827;
  font-size: 16px;
  line-height: 1.2;
}

:host(.isi-page) .isi-marking-detail-card--status > strong {
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: .06em;
  text-transform: uppercase;
}

:host(.isi-page) .isi-marking-detail-card--status > strong[data-route="mandatory"] {
  border: 1px solid rgba(196, 30, 58, .17);
  background: #fff0f3;
  color: #b41632;
}

:host(.isi-page) .isi-marking-detail-card--status > strong[data-route="voluntary"] {
  border: 1px solid rgba(31, 111, 235, .17);
  background: #edf5ff;
  color: #205eb9;
}

:host(.isi-page) .isi-marking-detail-card > small,
:host(.isi-page) .isi-marking-rate-card > div:first-child > small {
  display: block;
  margin-top: 8px;
  color: #778196;
  font-size: 9px;
  line-height: 1.45;
}

:host(.isi-page) .isi-marking-rate-card {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: minmax(170px, .62fr) minmax(0, 1.38fr);
  gap: 14px;
  align-items: center;
}

:host(.isi-page) .isi-marking-rate-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(110px, 1fr));
  gap: 7px;
}

:host(.isi-page) .isi-marking-rate-slab {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  background: #f8faff;
}

:host(.isi-page) .isi-marking-rate-slab > strong {
  display: block;
  margin-top: 5px;
  overflow-wrap: anywhere;
  color: #111827;
  font-size: 11px;
  line-height: 1.25;
}

:host(.isi-page) .isi-marking-rate-slab > small {
  display: block;
  margin-top: 5px;
  color: #687387;
  font-size: 8px;
  line-height: 1.35;
}

:host(.isi-page) .isi-marking-calculator__note {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: -6px 2px 0;
  color: #697386;
  font-size: 11px;
  line-height: 1.55;
}

:host(.isi-page) .isi-marking-calculator__note::before {
  display: grid;
  flex: 0 0 20px;
  width: 20px;
  aspect-ratio: 1;
  place-items: center;
  border-radius: 50%;
  background: #eef3ff;
  color: #285fcb;
  content: "i";
  font-weight: 950;
}

@media (max-width: 760px) {
  :host(.isi-page) .isi-marking-calculator { padding: 18px; border-radius: 22px; }
  :host(.isi-page) .isi-marking-calculator__head,
  :host(.isi-page) .isi-marking-calculator__controls,
  :host(.isi-page) .isi-marking-calculator__result { grid-template-columns: 1fr; }
  :host(.isi-page) .isi-marking-calculator__head { display: grid; }
  :host(.isi-page) .isi-marking-calculator__count { width: fit-content; }
  :host(.isi-page) .isi-marking-size-grid { grid-template-columns: 1fr 1fr; }
  :host(.isi-page) .isi-marking-result-value { min-width: 0; text-align: left; }
  :host(.isi-page) .isi-marking-result-option { grid-template-columns: 1fr; gap: 5px; }
  :host(.isi-page) .isi-marking-result-option__meta { grid-auto-flow: column; justify-content: space-between; justify-items: start; }
  :host(.isi-page) .isi-marking-calculator__details,
  :host(.isi-page) .isi-marking-rate-card { grid-template-columns: 1fr; }
  :host(.isi-page) .isi-marking-rate-grid { grid-template-columns: 1fr; }
}

/* Shared product directories for every regulatory service family. */
:host .service-product-directory {
  display: block !important;
  margin-top: 26px !important;
  padding: clamp(22px, 3vw, 34px) !important;
  overflow: hidden;
  border: 1px solid rgba(18, 21, 37, 0.1) !important;
  border-radius: 28px !important;
  background: linear-gradient(145deg, #ffffff 0%, #fff9fa 100%) !important;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.07) !important;
}

:host .service-product-directory__head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 18px;
  margin-bottom: 20px;
}

:host .service-product-directory__head > div > span {
  display: block;
  margin-bottom: 8px;
  color: #c41e3a !important;
  font-size: 0.72rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.12em !important;
  text-transform: uppercase;
}

:host .service-product-directory__head h2 {
  margin: 0 !important;
  color: #171b27 !important;
  font-size: clamp(1.65rem, 3vw, 2.45rem) !important;
  line-height: 1.08 !important;
}

:host .service-product-directory__head p {
  max-width: 760px;
  margin: 10px 0 0 !important;
  color: #667085 !important;
  line-height: 1.7 !important;
}

:host .service-product-directory__count {
  min-width: 110px;
  padding: 12px 15px;
  border-radius: 16px;
  background: #171b27;
  color: #ffffff !important;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 850;
}

:host .service-product-directory__search {
  position: relative;
  margin-bottom: 16px;
}

:host .service-product-directory__search input {
  width: 100% !important;
  min-height: 52px !important;
  margin: 0 !important;
  padding: 0 18px 0 48px !important;
  border: 1px solid rgba(18, 21, 37, 0.14) !important;
  border-radius: 15px !important;
  background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E") no-repeat 17px center !important;
  color: #171b27 !important;
  font: inherit !important;
  font-weight: 700 !important;
  outline: none !important;
}

:host .service-product-directory__search input:focus {
  border-color: rgba(196, 30, 58, 0.5) !important;
  box-shadow: 0 0 0 4px rgba(196, 30, 58, 0.08) !important;
}

:host .service-product-directory__list {
  display: grid;
  gap: 9px;
  max-height: min(580px, 65vh);
  padding: 2px 8px 2px 2px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #c41e3a #eef1f6;
}

:host .service-product-directory__item {
  display: grid !important;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  min-height: 72px;
  padding: 11px 14px !important;
  border: 1px solid rgba(18, 21, 37, 0.08) !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  color: inherit !important;
  text-decoration: none !important;
  transition: border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}

:host .service-product-directory__item:hover {
  transform: translateY(-1px);
  border-color: rgba(196, 30, 58, 0.28) !important;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
}

:host .service-product-directory__number {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 12px;
  background: #fff1f3;
  color: #c41e3a !important;
  font-size: 0.78rem;
  font-weight: 900;
}

:host .service-product-directory__item strong {
  display: block;
  color: #202939 !important;
  font-size: 0.94rem !important;
  line-height: 1.4 !important;
}

:host .service-product-directory__item small {
  display: block;
  margin-top: 3px;
  color: #7b8496 !important;
  font-size: 0.76rem !important;
  line-height: 1.4 !important;
}

:host .service-product-directory__open {
  color: #b91834 !important;
  font-size: 0.78rem;
  font-weight: 850;
  white-space: nowrap;
}

:host .service-product-directory__more {
  display: block;
  min-height: 44px;
  margin: 18px auto 0;
  padding: 0 20px;
  border: 1px solid rgba(196, 30, 58, 0.22);
  border-radius: 999px;
  background: #ffffff;
  color: #b91834;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 850;
  cursor: pointer;
}

:host .service-product-directory__empty {
  padding: 24px;
  border: 1px dashed rgba(196, 30, 58, 0.25);
  border-radius: 16px;
  color: #667085 !important;
  text-align: center;
}

@media (max-width: 680px) {
  :host .service-product-directory {
    padding: 18px !important;
    border-radius: 22px !important;
  }

  :host .service-product-directory__head {
    grid-template-columns: 1fr;
    align-items: start;
  }

  :host .service-product-directory__count {
    justify-self: start;
  }

  :host .service-product-directory__item {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 10px;
  }

  :host .service-product-directory__open {
    display: none;
  }
}

/* Reliable layout foundation for imported lower-level certification pages. */
:host(.isi-product-page),
:host(.crs-product-page) {
  display: block;
  overflow-x: clip;
  background: #f5f7fb;
}

:host(.isi-product-page) .legacy-document,
:host(.crs-product-page) .legacy-document,
:host(.isi-product-page) main,
:host(.crs-product-page) main {
  display: block;
  width: 100%;
  min-width: 0;
}

:host(.isi-product-page) :is(.service-guide-hero-grid, .guide-layout),
:host(.crs-product-page) :is(.service-guide-hero-grid, .guide-layout) {
  width: min(1180px, calc(100% - 44px)) !important;
  margin-inline: auto !important;
}

:host(.isi-product-page) .service-guide-hero-grid,
:host(.crs-product-page) .service-guide-hero-grid {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 380px) !important;
  gap: clamp(28px, 5vw, 68px) !important;
  align-items: center !important;
}

:host(.isi-product-page) .service-guide-copy,
:host(.crs-product-page) .service-guide-copy,
:host(.isi-product-page) .guide-article,
:host(.crs-product-page) .guide-article {
  min-width: 0 !important;
}

:host(.isi-product-page) .service-guide-copy h1,
:host(.crs-product-page) .service-guide-copy h1 {
  max-width: 850px !important;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

:host(.isi-product-page) .guide-identity-card,
:host(.crs-product-page) .guide-identity-card {
  display: grid !important;
  align-content: center !important;
  justify-items: center !important;
  min-width: 0 !important;
  padding: clamp(24px, 3vw, 36px) !important;
  text-align: center !important;
}

:host(.isi-product-page) .guide-identity-card img,
:host(.crs-product-page) .guide-identity-card img {
  width: min(190px, 70%) !important;
  height: 150px !important;
  object-fit: contain !important;
}

:host(.isi-product-page) .guide-layout,
:host(.crs-product-page) .guide-layout {
  display: grid !important;
  grid-template-columns: 260px minmax(0, 1fr) !important;
  gap: 28px !important;
  align-items: start !important;
}

:host(.isi-product-page) .guide-toc,
:host(.crs-product-page) .guide-toc {
  position: sticky !important;
  top: 108px !important;
  min-width: 0 !important;
}

:host(.isi-product-page) .guide-article,
:host(.crs-product-page) .guide-article {
  display: grid !important;
  gap: 20px !important;
}

:host(.isi-product-page) .guide-article > .guide-block,
:host(.crs-product-page) .guide-article > .guide-block {
  display: block !important;
  width: 100% !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: clamp(24px, 3.5vw, 40px) !important;
  overflow: hidden !important;
  border: 1px solid rgba(15, 23, 42, 0.09) !important;
  border-radius: 28px !important;
  background: #ffffff !important;
  color: #334155 !important;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.065) !important;
}

:host(.isi-product-page) .guide-block h2,
:host(.crs-product-page) .guide-block h2 {
  max-width: 100% !important;
  color: #111827 !important;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

:host(.isi-product-page) :is(.isi-product-facts, .isi-product-applicability, .crs-product-detail-card, .crs-doc-checklist, .guide-timeline-grid),
:host(.crs-product-page) :is(.isi-product-facts, .isi-product-applicability, .crs-product-detail-card, .crs-doc-checklist, .guide-timeline-grid) {
  display: grid !important;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)) !important;
  gap: 14px !important;
  width: 100% !important;
}

:host(.isi-product-page) :is(table, .crs-pricing-table),
:host(.crs-product-page) :is(table, .crs-pricing-table) {
  width: 100% !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

:host(.isi-product-page) :is(.crs-pricing-table-wrap, .isi-table-wrap),
:host(.crs-product-page) :is(.crs-pricing-table-wrap, .isi-table-wrap) {
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: auto !important;
}

/* Shared AMC hero: Complise at the centre, quotation-backed AMC scope in orbit. */
.amc-hero-grid.amc-orbit-hero-grid {
  grid-template-columns: minmax(0, .92fr) minmax(550px, 640px) !important;
  gap: clamp(30px, 4vw, 62px) !important;
}

.amc-orbit-visual {
  --amc-orbit-radius: clamp(190px, 18vw, 226px);
  position: relative;
  width: min(100%, 610px);
  aspect-ratio: 1;
  margin-inline: auto;
  isolation: isolate;
}

.amc-orbit-glow {
  position: absolute;
  inset: 16%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(237, 20, 61, 0.19), rgba(237, 20, 61, 0.04) 48%, transparent 72%);
  filter: blur(18px);
}

.amc-orbit-ring,
.amc-orbit-ring::before,
.amc-orbit-ring::after {
  position: absolute;
  inset: 10%;
  border: 1px solid rgba(17, 24, 39, 0.16);
  border-radius: 50%;
  content: "";
}

.amc-orbit-ring::before { inset: 12%; border-style: dashed; }
.amc-orbit-ring::after { inset: 25%; border-color: rgba(237, 20, 61, 0.32); }

.amc-orbit-track {
  position: absolute;
  inset: 0;
  animation: amc-orbit-spin 48s linear infinite;
}

.amc-orbit-item {
  position: absolute;
  top: 50%;
  left: 50%;
  width: clamp(136px, 12vw, 158px);
  min-height: 66px;
  transform: translate(-50%, -50%) rotate(var(--amc-angle)) translateX(var(--amc-orbit-radius)) rotate(calc(-1 * var(--amc-angle)));
}

.amc-orbit-chip {
  display: grid;
  grid-template-columns: 27px minmax(0, 1fr);
  grid-template-areas: "index title" "index detail";
  align-items: center;
  column-gap: 9px;
  width: 100%;
  min-height: 66px;
  padding: 10px 12px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--amc-scope-accent) 20%, #dfe4ec);
  border-radius: 17px;
  background: color-mix(in srgb, var(--amc-scope-accent) 5%, rgba(255, 255, 255, 0.98));
  box-shadow: 0 14px 36px rgba(22, 32, 52, 0.14);
  text-align: left;
  animation: amc-orbit-counter 48s linear infinite;
  transition: scale 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}

.amc-orbit-chip:hover,
.amc-orbit-chip:focus-visible {
  z-index: 4;
  scale: 1.08;
  border-color: var(--amc-scope-accent);
  box-shadow: 0 20px 42px color-mix(in srgb, var(--amc-scope-accent) 25%, transparent);
  outline: none;
}

.amc-scope-index {
  grid-area: index;
  display: grid;
  width: 27px;
  aspect-ratio: 1;
  place-items: center;
  border-radius: 50%;
  background: var(--amc-scope-accent);
  color: #ffffff;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: .04em;
}

.amc-orbit-chip strong {
  grid-area: title;
  align-self: end;
  color: #172033;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.18;
}

.amc-orbit-chip small {
  grid-area: detail;
  align-self: start;
  margin-top: 3px;
  color: #697386;
  font-size: 8.5px;
  font-weight: 800;
  letter-spacing: .035em;
  line-height: 1.2;
  text-transform: uppercase;
}

.amc-orbit-centre {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 3;
  display: grid;
  width: clamp(142px, 14vw, 190px);
  aspect-ratio: 1;
  place-items: center;
  padding: 24px;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(237, 20, 61, 0.32);
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 28px 70px rgba(22, 32, 52, 0.18), 0 0 0 16px rgba(196, 30, 58, 0.04);
}

.amc-orbit-centre img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.amc-orbit-centre span {
  position: absolute;
  bottom: 20px;
  color: #c41e3a;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.amc-orbit-centre::after {
  position: absolute;
  top: calc(100% + 14px);
  left: 50%;
  z-index: 8;
  width: clamp(250px, 27vw, 330px);
  max-width: calc(100vw - 28px);
  padding: 9px 18px;
  transform: translateX(-50%);
  border: 1px solid rgba(196, 30, 58, 0.13);
  border-radius: 999px;
  background: rgba(255, 255, 255, .92);
  color: #5f6878;
  box-shadow: 0 12px 30px rgba(22, 32, 52, 0.12);
  content: "Continuous compliance ownership";
  font-size: 9.5px;
  font-weight: 850;
  letter-spacing: .08em;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
}

.amc-orbit-visual:hover .amc-orbit-track,
.amc-orbit-visual:hover .amc-orbit-chip {
  animation-play-state: paused;
}

@keyframes amc-orbit-spin { to { transform: rotate(360deg); } }
@keyframes amc-orbit-counter { to { transform: rotate(-360deg); } }

@media (max-width: 980px) {
  :host(.isi-product-page) .service-guide-hero-grid,
  :host(.crs-product-page) .service-guide-hero-grid,
  :host(.isi-product-page) .guide-layout,
  :host(.crs-product-page) .guide-layout,
  .amc-hero-grid.amc-orbit-hero-grid {
    grid-template-columns: 1fr !important;
  }

  :host(.isi-product-page) .guide-toc,
  :host(.crs-product-page) .guide-toc {
    position: static !important;
  }

  .amc-orbit-visual { margin-top: 18px; }
}

@media (max-width: 600px) {
  :host(.isi-product-page) :is(.service-guide-hero-grid, .guide-layout),
  :host(.crs-product-page) :is(.service-guide-hero-grid, .guide-layout) {
    width: min(100% - 28px, 1180px) !important;
  }

  :host(.isi-product-page) .sub-actions > *,
  :host(.crs-product-page) .sub-actions > * {
    width: 100% !important;
  }

  .amc-orbit-visual {
    --amc-orbit-radius: clamp(123px, 35vw, 140px);
    width: min(100%, 390px);
    margin-block: 22px 10px;
  }

  .amc-orbit-item {
    width: 112px;
    min-height: 54px;
  }

  .amc-orbit-chip {
    grid-template-columns: 21px minmax(0, 1fr);
    column-gap: 6px;
    min-height: 54px;
    padding: 7px 8px;
    border-radius: 13px;
  }
  .amc-scope-index { width: 21px; font-size: 7px; }
  .amc-orbit-chip strong { font-size: 8.5px; }
  .amc-orbit-chip small { font-size: 6.6px; }
  .amc-orbit-centre { width: 116px; padding: 18px; }
  .amc-orbit-centre span { bottom: 12px; font-size: 0.47rem; }
  .amc-orbit-centre::after { top: calc(100% + 11px); width: min(238px, calc(100vw - 28px)); padding: 7px 10px; font-size: 7px; }
}

@media (prefers-reduced-motion: reduce) {
  .amc-orbit-track,
  .amc-orbit-chip {
    animation-play-state: paused !important;
  }
}

/* About and contact page visuals inserted from the shared React shell. */
:host(.legacy-page-about) .about-hero-visual,
:host(.legacy-page-about) .about-story-visual,
:host(.legacy-page-contact) .contact-coverage-visual {
  position: relative;
  margin: 0;
  overflow: hidden;
  border-radius: 26px;
  background: #eef1f6;
}

:host(.legacy-page-about) .about-hero-pro {
  grid-template-columns: minmax(0, 1.08fr) minmax(430px, 0.92fr) !important;
  gap: clamp(22px, 2.5vw, 36px) !important;
  align-items: stretch !important;
}

:host(.legacy-page-about) .about-hero-copy,
:host(.legacy-page-about) .about-hero-panel {
  min-height: 620px !important;
  height: 100% !important;
}

:host(.legacy-page-about) .about-hero-panel {
  position: relative !important;
  isolation: isolate;
  display: flex !important;
  flex-direction: column;
  justify-content: space-between;
  gap: 22px !important;
  padding: 28px !important;
  overflow: hidden !important;
  background: #121525 !important;
}

:host(.legacy-page-about) .about-hero-visual {
  position: absolute;
  inset: 0;
  z-index: -2;
  min-height: 0;
  border-radius: inherit;
}

:host(.legacy-page-about) .about-hero-visual::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(180deg, rgba(8, 14, 28, 0.18), rgba(8, 14, 28, 0.38) 46%, rgba(8, 14, 28, 0.92));
}

:host(.legacy-page-about) .about-hero-visual figcaption {
  display: none !important;
}

:host(.legacy-page-about) .about-panel-top {
  position: relative;
  z-index: 2;
  width: min(100%, 430px);
  align-self: flex-start;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(12, 18, 32, 0.68) !important;
  backdrop-filter: blur(14px);
}

:host(.legacy-page-about) .about-metric-grid {
  position: relative;
  z-index: 2;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 12px !important;
}

:host(.legacy-page-about) .about-metric-grid div {
  min-height: 132px !important;
  padding: 18px !important;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.92) !important;
  backdrop-filter: blur(12px);
}

:host(.legacy-page-about) .about-intro-strip {
  align-items: center !important;
}

:host(.legacy-page-about) .about-story-grid {
  grid-template-columns: minmax(0, 0.95fr) minmax(420px, 1.05fr) !important;
  align-items: stretch !important;
}

:host(.legacy-page-about) .about-story-card {
  height: 100%;
}

:host(.legacy-page-about) .about-story-visual {
  min-height: 520px;
  padding: 0 !important;
}

:host(.legacy-page-contact) .contact-coverage-visual {
  min-height: 320px;
  padding: 0 !important;
}

:host(.legacy-page-about) :is(.about-hero-visual, .about-story-visual) img,
:host(.legacy-page-contact) .contact-coverage-visual img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 600ms ease;
}

:host(.legacy-page-about) :is(.about-hero-visual, .about-story-visual):hover img,
:host(.legacy-page-contact) .contact-coverage-visual:hover img {
  transform: scale(1.035);
}

:host(.legacy-page-about) :is(.about-hero-visual, .about-story-visual) figcaption,
:host(.legacy-page-contact) .contact-coverage-visual figcaption {
  position: absolute;
  right: 18px;
  bottom: 18px;
  left: 18px;
  display: grid;
  gap: 5px;
  padding: 18px 20px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 18px;
  background: rgba(18, 21, 37, 0.86);
  color: #ffffff;
  backdrop-filter: blur(12px);
}

:host(.legacy-page-about) :is(.about-hero-visual, .about-story-visual) figcaption span,
:host(.legacy-page-contact) .contact-coverage-visual figcaption span {
  color: #ff8da3;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

:host(.legacy-page-about) :is(.about-hero-visual, .about-story-visual) figcaption strong,
:host(.legacy-page-contact) .contact-coverage-visual figcaption strong {
  color: #ffffff;
  font-size: clamp(1rem, 1.5vw, 1.3rem);
  line-height: 1.25;
}

:host(.legacy-page-about) .about-timeline {
  display: grid !important;
  grid-auto-flow: column;
  grid-auto-columns: minmax(260px, 1fr);
  grid-template-columns: none !important;
  gap: 16px !important;
  width: 100%;
  max-width: 100%;
  padding: 6px 2px 20px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
  scrollbar-color: rgba(237, 20, 61, 0.35) transparent;
}

:host(.legacy-page-about) .about-timeline::before {
  display: none !important;
}

:host(.legacy-page-about) .about-timeline article {
  min-height: 300px;
  height: 100%;
  scroll-snap-align: start;
}

:host(.legacy-page-about) .about-timeline article small {
  color: #ed143d;
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

@media (max-width: 1180px) {
  :host(.legacy-page-about) .about-hero-pro,
  :host(.legacy-page-about) .about-story-grid {
    grid-template-columns: 1fr !important;
  }

  :host(.legacy-page-about) .about-hero-copy {
    min-height: 500px !important;
  }

  :host(.legacy-page-about) .about-hero-panel {
    min-height: 650px !important;
  }

  :host(.legacy-page-about) .about-story-visual {
    min-height: 430px;
  }
}

@media (max-width: 760px) {
  :host(.legacy-page-about) .about-hero-panel {
    min-height: 610px !important;
    padding: 20px !important;
    border-radius: 28px !important;
  }

  :host(.legacy-page-about) .about-panel-top {
    padding: 18px !important;
    border-radius: 20px !important;
  }

  :host(.legacy-page-about) .about-panel-top strong {
    font-size: 21px !important;
  }

  :host(.legacy-page-about) .about-metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  :host(.legacy-page-about) .about-metric-grid div {
    min-height: 120px !important;
    padding: 15px !important;
    border-radius: 20px !important;
  }

  :host(.legacy-page-about) .about-metric-grid strong {
    font-size: 26px !important;
  }

  :host(.legacy-page-about) .about-metric-grid span {
    font-size: 12px !important;
  }

  :host(.legacy-page-about) .about-story-visual {
    min-height: 350px;
  }

  :host(.legacy-page-about) .about-timeline {
    grid-auto-columns: minmax(250px, 84vw);
  }
}

@media (max-width: 480px) {
  :host(.legacy-page-about) .about-hero-copy {
    min-height: 480px !important;
  }

  :host(.legacy-page-about) .about-hero-copy h1 {
    font-size: 40px !important;
  }

  :host(.legacy-page-about) .about-hero-panel {
    min-height: 640px !important;
  }
}

/* Service catalogue visual refresh: brighter cards, clearer motion and reliable contrast. */
@keyframes service-card-reveal {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li),
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span) {
  --service-accent: #d91f45;
  --service-tint: #fff1f4;
  position: relative !important;
  isolation: isolate;
  overflow: hidden !important;
  border-color: color-mix(in srgb, var(--service-accent) 18%, transparent) !important;
  background: linear-gradient(145deg, #ffffff 18%, var(--service-tint) 100%) !important;
  box-shadow: 0 14px 34px color-mix(in srgb, var(--service-accent) 10%, transparent) !important;
  animation: service-card-reveal 520ms cubic-bezier(.2,.72,.2,1) both;
  transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li):nth-child(4n + 2),
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span):nth-child(4n + 2) {
  --service-accent: #2463c5;
  --service-tint: #eef5ff;
  animation-delay: 55ms;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li):nth-child(4n + 3),
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span):nth-child(4n + 3) {
  --service-accent: #087f78;
  --service-tint: #ecfbf8;
  animation-delay: 110ms;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li):nth-child(4n),
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span):nth-child(4n) {
  --service-accent: #b66b08;
  --service-tint: #fff8e8;
  animation-delay: 165ms;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li)::after,
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span)::after {
  content: "";
  position: absolute;
  z-index: -1;
  top: -42px;
  right: -38px;
  width: 104px;
  height: 104px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--service-accent) 12%, transparent);
  transition: transform 280ms ease;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li):hover,
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span):hover {
  z-index: 2 !important;
  border-color: color-mix(in srgb, var(--service-accent) 36%, transparent) !important;
  box-shadow: 0 22px 48px color-mix(in srgb, var(--service-accent) 17%, transparent) !important;
  transform: translateY(-5px) !important;
}

:host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li):hover::after,
:host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span):hover::after {
  transform: scale(1.35);
}

:host .service-product-directory {
  position: relative;
  background:
    radial-gradient(circle at 96% 4%, rgba(36, 99, 197, .10), transparent 26%),
    radial-gradient(circle at 2% 96%, rgba(217, 31, 69, .09), transparent 28%),
    #ffffff !important;
}

:host .service-product-directory__item:nth-child(4n + 1) { --directory-accent: #d91f45; --directory-tint: #fff3f5; }
:host .service-product-directory__item:nth-child(4n + 2) { --directory-accent: #2463c5; --directory-tint: #f2f7ff; }
:host .service-product-directory__item:nth-child(4n + 3) { --directory-accent: #087f78; --directory-tint: #effbf9; }
:host .service-product-directory__item:nth-child(4n) { --directory-accent: #b66b08; --directory-tint: #fff9ec; }

:host .service-product-directory__item {
  border-left: 4px solid var(--directory-accent, #d91f45) !important;
  background: linear-gradient(100deg, var(--directory-tint, #fff3f5), #ffffff 52%) !important;
}

:host .service-product-directory__item:hover {
  transform: translateX(4px) !important;
  box-shadow: 0 14px 30px color-mix(in srgb, var(--directory-accent, #d91f45) 14%, transparent) !important;
}

:host .service-product-directory__item .service-product-directory__number {
  background: color-mix(in srgb, var(--directory-accent, #d91f45) 12%, white) !important;
  color: var(--directory-accent, #d91f45) !important;
}

/* ISI table alignment repairs. */
:host(.isi-page) .isi-products-table {
  min-width: 760px !important;
}

:host(.isi-page) .isi-products-table :is(th, td):first-child {
  min-width: 72px !important;
  white-space: nowrap !important;
  overflow-wrap: normal !important;
  word-break: normal !important;
  font-variant-numeric: tabular-nums;
  vertical-align: middle !important;
}

:host(.isi-page) .isi-official-source span {
  color: #356044 !important;
}

@media (min-width: 761px) {
  :host(.isi-page) .isi-comparison-table .isi-table-row {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  :host(.isi-page) .isi-comparison-table .isi-table-row span {
    display: flex;
    width: 100% !important;
    box-sizing: border-box !important;
    min-height: 78px;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  :host(.isi-page) .isi-comparison-table .isi-table-row.head span {
    color: #ffffff !important;
  }
}

/* CTA contrast guard for every service-page family. */
:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .guide-question-panel, .sub-cta, .subservice-final-cta),
:host(.subservice-page) .legacy-document :is(.guide-cta-panel, .route-cta-block, .sub-cta, .contact-strip, .cta-band),
:host(.isi-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip),
:host(.crs-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) {
  position: relative !important;
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, .12) !important;
  background:
    radial-gradient(circle at 90% 12%, rgba(237, 20, 61, .30), transparent 32%),
    linear-gradient(135deg, #09162b 0%, #1b2942 58%, #371827 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 26px 68px rgba(8, 21, 42, .20) !important;
}

:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .guide-question-panel, .sub-cta, .subservice-final-cta) :is(h2, h3, h4, strong, b),
:host(.subservice-page) .legacy-document :is(.guide-cta-panel, .route-cta-block, .sub-cta, .contact-strip, .cta-band) :is(h2, h3, h4, strong, b),
:host(.isi-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) :is(h2, h3, h4, strong, b),
:host(.crs-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) :is(h2, h3, h4, strong, b) {
  color: #ffffff !important;
}

:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .guide-question-panel, .sub-cta, .subservice-final-cta) :is(p, li, span),
:host(.subservice-page) .legacy-document :is(.guide-cta-panel, .route-cta-block, .sub-cta, .contact-strip, .cta-band) :is(p, li, span),
:host(.isi-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) :is(p, li, span),
:host(.crs-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) :is(p, li, span) {
  color: rgba(255, 255, 255, .78) !important;
}

:host(.top-level-service-page) .legacy-document :is(.route-cta, .route-cta-block, .guide-question-panel, .sub-cta, .subservice-final-cta) :is(a, button),
:host(.subservice-page) .legacy-document :is(.guide-cta-panel, .route-cta-block, .sub-cta, .contact-strip, .cta-band) :is(a, button),
:host(.isi-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) :is(a, button),
:host(.crs-product-page) .legacy-document :is(.product-cta-strip, .guide-cta-panel, .route-cta-block, .contact-strip) :is(a, button) {
  color: #ffffff !important;
  background: linear-gradient(135deg, #ed143d, #ff4365) !important;
  border-color: transparent !important;
  box-shadow: 0 16px 34px rgba(237, 20, 61, .28) !important;
}

:host .legacy-document :is(.main-service-cta, .authority-cta) :is(h2, h3, strong) {
  color: #ffffff !important;
}

:host .legacy-document :is(.main-service-cta, .authority-cta) p {
  color: rgba(255, 255, 255, .78) !important;
}

:host(.subservice-page) .legacy-document .guide-cta-panel {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  grid-template-areas:
    "cta-title cta-action"
    "cta-copy cta-action" !important;
  align-items: center !important;
  column-gap: clamp(24px, 4vw, 54px) !important;
  row-gap: 0 !important;
}

:host(.subservice-page) .legacy-document .guide-cta-panel > h2 {
  grid-area: cta-title;
  width: auto !important;
  max-width: 760px !important;
  min-width: 0 !important;
}

:host(.subservice-page) .legacy-document .guide-cta-panel > p {
  grid-area: cta-copy;
  width: auto !important;
  max-width: 780px !important;
  min-width: 0 !important;
}

:host(.subservice-page) .legacy-document .guide-cta-panel > a,
:host(.subservice-page) .legacy-document .guide-cta-panel > button {
  grid-area: cta-action;
  align-self: center !important;
  justify-self: end !important;
}

/*
 * Structural fallback for every imported service guide.
 *
 * The migrated pages come from several generations of templates.  Some use
 * very specific page classes while others only expose the common guide
 * structure.  Keep the shared structure usable even when one of those
 * page-specific selectors is absent.
 */
:host(.subservice-page) .legacy-document,
:host(.subservice-page) .legacy-document *,
:host(.subservice-page) .legacy-document *::before,
:host(.subservice-page) .legacy-document *::after {
  box-sizing: border-box;
}

:host(.subservice-page) .legacy-document {
  overflow-x: clip !important;
  overflow-y: visible !important;
  background: #f7f9fc;
  color: #344054;
}

:host(.subservice-page) .legacy-document > main {
  overflow-x: clip !important;
  overflow-y: visible !important;
}

/* Keep the ISI hero, industry strip and article on one vertical axis. */
:host(.isi-page) .legacy-document .shell {
  width: min(1180px, calc(100% - 44px)) !important;
  max-width: 1180px !important;
  margin-inline: auto !important;
}

:host(.subservice-page) .legacy-document :is(.guide-layout-section, .service-guide-section, .subservice-body) {
  padding: clamp(42px, 6vw, 78px) 0 !important;
}

:host(.subservice-page) .legacy-document :is(.guide-layout, .service-guide-layout, .subservice-body.shell, .subservice-body > .shell) {
  display: grid !important;
  grid-template-columns: minmax(220px, 264px) minmax(0, 1fr) !important;
  align-items: start !important;
  gap: clamp(24px, 3.4vw, 46px) !important;
  width: min(1180px, calc(100% - 44px)) !important;
  max-width: 1180px !important;
  margin-inline: auto !important;
}

/* Some long-form service pages put the shell class on the grid itself instead of
 * wrapping it. Match both migrated structures so the rail and article never
 * become two full-width stacked blocks. */
:host(.top-level-service-page) .legacy-document .subservice-body.shell,
:host(.subservice-page) .legacy-document .subservice-body.shell {
  display: grid !important;
  grid-template-columns: minmax(220px, 264px) minmax(0, 1fr) !important;
  gap: clamp(24px, 3.4vw, 46px) !important;
  width: min(1180px, calc(100% - 44px)) !important;
  max-width: 1180px !important;
  margin-inline: auto !important;
  padding: clamp(42px, 6vw, 78px) 0 !important;
  align-items: start !important;
  background: transparent !important;
}

/* Keep every service hero and the article below it on one vertical axis. */
:host(.subservice-page) .legacy-document :is(.service-guide-hero-grid, .service-guide-hero__grid, .subservice-hero-grid, .sub-hero-grid) {
  width: min(1180px, calc(100% - 44px)) !important;
  max-width: 1180px !important;
  margin-inline: auto !important;
}

/* Legacy button rules targeted a light-DOM ancestor and cannot match inside
 * the shadow root. Provide a reliable shared action system here. */
:host(.subservice-page) .legacy-document :is(.hero-actions, .subservice-hero-actions) {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  gap: 12px !important;
}

:host(.subservice-page) .legacy-document :is(.primary-btn, .secondary-btn, .sub-btn, .btn) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 48px !important;
  padding: 0 20px !important;
  border: 1px solid transparent !important;
  border-radius: 999px !important;
  font-size: .9rem !important;
  font-weight: 850 !important;
  line-height: 1.2 !important;
  text-decoration: none !important;
}

:host(.subservice-page) .legacy-document :is(.primary-btn, .sub-btn.primary, .btn-primary) {
  border-color: #c41e3a !important;
  background: linear-gradient(135deg, #c41e3a, #e13c58) !important;
  color: #ffffff !important;
  box-shadow: 0 14px 34px rgba(196, 30, 58, .22) !important;
}

:host(.subservice-page) .legacy-document :is(.secondary-btn, .sub-btn.secondary, .btn-soft) {
  border-color: rgba(18, 21, 37, .13) !important;
  background: #ffffff !important;
  color: #172033 !important;
  box-shadow: 0 12px 28px rgba(15, 23, 42, .07) !important;
}

/* WPC previously had a dark hero. Its late white-text override made the title
 * disappear after the site moved to a light theme. */
:host(.wpc-page) .legacy-document .wpc-hero .sub-hero-copy h1 {
  color: #101525 !important;
}

:host(.wpc-page) .legacy-document .wpc-hero .sub-hero-copy p {
  color: #526178 !important;
}

:host(.wpc-page) .legacy-document .wpc-hero .sub-hero-copy .eyebrow {
  color: #c41e3a !important;
}

:host(.wpc-page) .legacy-document .wpc-hero .sub-hero-copy .eyebrow::before {
  background: #c41e3a !important;
}

:host(.wpc-page) .legacy-document .wpc-hero .hero-pills span {
  border-color: rgba(18, 21, 37, .1) !important;
  background: rgba(255, 255, 255, .92) !important;
  color: #344056 !important;
}

:host(.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) {
  display: grid !important;
  min-width: 0 !important;
  gap: 22px !important;
}

:host(.subservice-page) .legacy-document :is(.guide-toc-card, .toc-card, .guide-side-form, .guide-side-card, .guide-side-news, .sidebar-insights, .sidebar-contact, .mini-contact-card, .toc-cta) {
  min-width: 0 !important;
  overflow: hidden !important;
  padding: 20px !important;
  border: 1px solid rgba(18, 21, 37, .1) !important;
  border-radius: 20px !important;
  background: #ffffff !important;
  color: #475467 !important;
  box-shadow: 0 16px 40px rgba(15, 23, 42, .065) !important;
}

:host(.subservice-page) .legacy-document :is(.guide-toc-card, .toc-card) > :is(span, .toc-label, .toc-kicker) {
  display: block !important;
  margin: 0 0 12px !important;
  color: #c41e3a !important;
  font-size: .72rem !important;
  font-weight: 900 !important;
  letter-spacing: .11em !important;
  text-transform: uppercase !important;
}

:host(.subservice-page) .legacy-document :is(.guide-toc-card, .toc-card) > a {
  display: flex !important;
  align-items: center !important;
  min-height: 38px !important;
  margin: 0 !important;
  padding: 8px 10px !important;
  border-bottom: 1px solid rgba(18, 21, 37, .065) !important;
  border-radius: 9px !important;
  color: #475467 !important;
  font-size: .82rem !important;
  font-weight: 750 !important;
  line-height: 1.35 !important;
  text-decoration: none !important;
}

:host(.subservice-page) .legacy-document :is(.guide-toc-card, .toc-card) > a:hover {
  background: #fff2f4 !important;
  color: #b91834 !important;
  transform: translateX(3px);
}

:host(.subservice-page) .legacy-document :is(.guide-side-form, .guide-side-card, .sidebar-contact, .mini-contact-card, .toc-cta) {
  border-color: rgba(196, 30, 58, .15) !important;
  background:
    radial-gradient(circle at 100% 0, rgba(196, 30, 58, .1), transparent 42%),
    #fffafb !important;
}

:host(.subservice-page) .legacy-document .guide-side-form :is(h2, h3, strong) {
  color: #172033 !important;
}

:host(.subservice-page) .legacy-document .guide-side-form p {
  color: #667085 !important;
  line-height: 1.65 !important;
}

/* A viewport-sized sticky rail prevents an empty left column on long pages. */
:host(.subservice-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar),
:host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar) {
  position: sticky !important;
  top: 96px !important;
  display: grid !important;
  gap: 16px !important;
  min-width: 0 !important;
  max-height: calc(100dvh - 116px) !important;
  padding-right: 5px !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(196, 30, 58, .52) rgba(18, 21, 37, .06);
}

:host(.subservice-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar)::-webkit-scrollbar,
:host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar)::-webkit-scrollbar {
  width: 7px;
}

:host(.subservice-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar)::-webkit-scrollbar-track,
:host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar)::-webkit-scrollbar-track {
  border-radius: 999px;
  background: rgba(18, 21, 37, .05);
}

:host(.subservice-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar)::-webkit-scrollbar-thumb,
:host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar)::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: linear-gradient(180deg, #c41e3a, #e54c66);
}

:host(.subservice-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) {
  min-width: 0 !important;
  margin: 0 !important;
  padding: clamp(24px, 3.2vw, 38px) !important;
  overflow: hidden;
  border: 1px solid rgba(18, 21, 37, .095) !important;
  border-radius: 26px !important;
  background: #ffffff !important;
  color: #475467 !important;
  box-shadow: 0 18px 48px rgba(15, 23, 42, .065) !important;
  scroll-margin-top: 104px;
}

:host(.subservice-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) :is(h2, h3, h4, strong, b) {
  color: #172033;
}

:host(.subservice-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) h2 {
  margin: 8px 0 14px;
  font-size: clamp(1.72rem, 3vw, 2.48rem) !important;
  line-height: 1.1 !important;
  letter-spacing: -.045em !important;
}

:host(.subservice-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) :is(p, li) {
  color: #667085;
  line-height: 1.72;
}

:host(.subservice-page) .legacy-document .guide-block > :is([class$="-grid"], [class*="-grid "]) {
  min-width: 0;
  gap: 16px !important;
}

:host(.subservice-page) .legacy-document .guide-block > :is([class$="-grid"], [class*="-grid "]) > * {
  min-width: 0;
}

/* Tables from older templates now receive the same visible treatment. */
:host(.subservice-page) .legacy-document .guide-block :is(.responsive-table, [class$="-table-wrap"], [class*="-table-wrap "]) {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid rgba(18, 21, 37, .1);
  border-radius: 18px;
  background: #ffffff;
  scrollbar-width: thin;
}

:host(.subservice-page) .legacy-document .guide-block table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  color: #475467;
}

:host(.subservice-page) .legacy-document .guide-block table th {
  padding: 14px 16px;
  background: #121827;
  color: #ffffff !important;
  text-align: center;
  vertical-align: middle;
  font-size: .76rem;
  font-weight: 900;
  line-height: 1.35;
  letter-spacing: .055em;
  text-transform: uppercase;
}

:host(.subservice-page) .legacy-document .guide-block table td {
  padding: 14px 16px;
  border-right: 1px solid rgba(18, 21, 37, .06);
  border-bottom: 1px solid rgba(18, 21, 37, .08);
  background: #ffffff;
  color: #475467;
  vertical-align: middle;
  line-height: 1.55;
}

:host(.subservice-page) .legacy-document .guide-block table tbody tr:nth-child(even) td {
  background: #f8fafc;
}

:host(.subservice-page) .legacy-document :is(input, select, textarea) {
  max-width: 100%;
  border: 1px solid rgba(18, 21, 37, .14);
  background: #ffffff;
  color: #172033;
}

@media (max-width: 980px) {
  :host(.subservice-page) .legacy-document :is(.guide-layout, .service-guide-layout, .subservice-body.shell, .subservice-body > .shell) {
    grid-template-columns: 1fr !important;
    width: min(100% - 30px, 760px) !important;
  }

  :host(.subservice-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar),
  :host(.top-level-service-page) .legacy-document :is(.guide-toc, .service-guide-sidebar, .service-sidebar) {
    position: static !important;
    max-height: none !important;
    padding-right: 0 !important;
    overflow: visible !important;
  }
}

@media (max-width: 640px) {
  :host(.isi-page) .legacy-document .shell,
  :host(.subservice-page) .legacy-document :is(.guide-layout, .service-guide-layout, .subservice-body.shell, .subservice-body > .shell) {
    width: min(100% - 24px, 760px) !important;
  }

  :host(.subservice-page) .legacy-document :is(.guide-block, .content-section, .intro-panel, .intro-block, .faq-section, .related-services, .related-services-block) {
    padding: 20px !important;
    border-radius: 20px !important;
  }

  :host(.subservice-page) .legacy-document .guide-block table :is(th, td) {
    padding: 12px;
  }
}

@media (max-width: 900px) {
  :host(.subservice-page) .legacy-document .guide-cta-panel {
    grid-template-columns: 1fr !important;
    grid-template-areas: "cta-title" "cta-copy" "cta-action" !important;
    row-gap: 18px !important;
  }

  :host(.subservice-page) .legacy-document .guide-cta-panel > a,
  :host(.subservice-page) .legacy-document .guide-cta-panel > button {
    justify-self: start !important;
  }
}

/* Product hero identity: keep the logo, product name and supporting label on
 * one centered visual axis across static and generated product pages. */
:host(.isi-product-page) .legacy-document .subservice-hero-card,
:host(.crs-product-page) .legacy-document .guide-identity-card,
:host(.bee-product-page) .legacy-document .subservice-hero-card {
  display: grid !important;
  justify-items: center !important;
  align-content: center !important;
  text-align: center !important;
}

:host(.isi-product-page) .legacy-document .subservice-hero-card .subservice-logo-card,
:host(.isi-product-page) .legacy-document .subservice-hero-card > img,
:host(.crs-product-page) .legacy-document .guide-identity-card > img,
:host(.bee-product-page) .legacy-document .subservice-hero-card .subservice-logo-card,
:host(.bee-product-page) .legacy-document .subservice-hero-card > img {
  margin-inline: auto !important;
}

:host(.isi-product-page) .legacy-document .subservice-hero-card > h2,
:host(.isi-product-page) .legacy-document .subservice-hero-card > p,
:host(.crs-product-page) .legacy-document .guide-identity-card > strong,
:host(.crs-product-page) .legacy-document .guide-identity-card > span,
:host(.bee-product-page) .legacy-document .subservice-hero-card > h2,
:host(.bee-product-page) .legacy-document .subservice-hero-card > p {
  width: 100% !important;
  margin-inline: auto !important;
  text-align: center !important;
}

:host(.isi-product-page) .legacy-document .subservice-hero-card > h2,
:host(.crs-product-page) .legacy-document .guide-identity-card > strong,
:host(.bee-product-page) .legacy-document .subservice-hero-card > h2 {
  overflow-wrap: anywhere;
  word-break: normal;
  hyphens: auto;
}

/* WPC confirmation CTA: keep this promotional block distinct from the white
 * content cards even though the migrated markup also carries content-section. */
:host(.wpc-page) .legacy-document .route-cta.content-section {
  position: relative !important;
  isolation: isolate;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  align-items: center !important;
  gap: clamp(22px, 4vw, 48px) !important;
  min-width: 0 !important;
  padding: clamp(28px, 4vw, 44px) !important;
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, .12) !important;
  border-radius: 28px !important;
  background:
    radial-gradient(circle at 92% 8%, rgba(225, 60, 88, .42), transparent 34%),
    linear-gradient(135deg, #09162b 0%, #1b2942 58%, #371827 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 26px 68px rgba(8, 21, 42, .22) !important;
}

:host(.wpc-page) .legacy-document .route-cta.content-section::after {
  content: "";
  position: absolute;
  right: -58px;
  bottom: -78px;
  z-index: -1;
  width: 220px;
  height: 220px;
  border: 34px solid rgba(255, 255, 255, .05);
  border-radius: 50%;
  pointer-events: none;
}

:host(.wpc-page) .legacy-document .route-cta.content-section .section-kicker {
  color: #ffb8c3 !important;
}

:host(.wpc-page) .legacy-document .route-cta.content-section h2 {
  max-width: 720px;
  margin: 10px 0 12px !important;
  color: #ffffff !important;
  font-size: clamp(1.8rem, 3vw, 2.65rem) !important;
  line-height: 1.08 !important;
}

:host(.wpc-page) .legacy-document .route-cta.content-section p {
  max-width: 760px;
  margin: 0 !important;
  color: rgba(255, 255, 255, .76) !important;
  line-height: 1.7 !important;
}

:host(.wpc-page) .legacy-document .route-cta.content-section .primary-btn {
  position: relative;
  z-index: 1;
  min-width: 178px !important;
  min-height: 50px !important;
  border-color: #ffffff !important;
  background: #ffffff !important;
  color: #172033 !important;
  box-shadow: 0 16px 34px rgba(0, 0, 0, .2) !important;
}

:host(.wpc-page) .legacy-document .route-cta.content-section .primary-btn:hover {
  transform: translateY(-2px);
  background: #fff1f4 !important;
  color: #a81732 !important;
}

@media (max-width: 980px) {
  :host(.wpc-page) .legacy-document .route-cta.content-section {
    grid-template-columns: 1fr !important;
  }

  :host(.wpc-page) .legacy-document .route-cta.content-section .primary-btn {
    width: fit-content !important;
    justify-self: start !important;
  }
}

@media (max-width: 640px) {
  :host(.wpc-page) .legacy-document .route-cta.content-section {
    padding: 24px !important;
    border-radius: 22px !important;
  }

  :host(.wpc-page) .legacy-document .route-cta.content-section .primary-btn {
    width: 100% !important;
  }
}

/* TEC applicability CTA: the imported block also carries guide-block, whose
 * white-card background otherwise wins while the CTA copy remains white. */
:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block {
  position: relative !important;
  isolation: isolate;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  grid-template-areas:
    "tec-label tec-action"
    "tec-title tec-action"
    "tec-copy tec-action" !important;
  align-items: center !important;
  column-gap: clamp(28px, 5vw, 64px) !important;
  row-gap: 10px !important;
  min-width: 0 !important;
  margin-top: 26px !important;
  padding: clamp(30px, 4vw, 48px) !important;
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, .13) !important;
  border-radius: 28px !important;
  background:
    radial-gradient(circle at 91% 8%, rgba(65, 130, 255, .32), transparent 34%),
    radial-gradient(circle at 8% 100%, rgba(237, 20, 61, .20), transparent 34%),
    linear-gradient(135deg, #07152b 0%, #142846 58%, #1b3154 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 28px 72px rgba(7, 21, 43, .24) !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block::before {
  content: "TEC route mapping";
  grid-area: tec-label;
  position: relative;
  z-index: 2;
  color: #8fbcff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .16em;
  text-transform: uppercase;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block::after {
  content: "";
  position: absolute;
  right: -62px;
  bottom: -90px;
  z-index: -1;
  width: 250px;
  height: 250px;
  border: 36px solid rgba(255, 255, 255, .055);
  border-radius: 50%;
  pointer-events: none;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block h2 {
  grid-area: tec-title;
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 !important;
  color: #ffffff !important;
  font-size: clamp(1.85rem, 3vw, 2.7rem) !important;
  line-height: 1.08 !important;
  letter-spacing: -.045em !important;
  text-wrap: balance;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block p {
  grid-area: tec-copy;
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 2px 0 0 !important;
  color: rgba(255, 255, 255, .76) !important;
  font-size: 15px !important;
  line-height: 1.72 !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block .btn-primary {
  grid-area: tec-action;
  position: relative;
  z-index: 2;
  justify-self: end !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: max-content !important;
  min-width: 206px !important;
  min-height: 52px !important;
  padding: 0 22px !important;
  border: 1px solid rgba(255, 255, 255, .22) !important;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #ed143d, #ff4969) !important;
  color: #ffffff !important;
  font-size: 13px !important;
  font-weight: 850 !important;
  text-align: center !important;
  text-decoration: none !important;
  box-shadow: 0 18px 38px rgba(237, 20, 61, .30) !important;
  transition: transform .2s ease, box-shadow .2s ease !important;
}

:host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 24px 48px rgba(237, 20, 61, .38) !important;
}

@media (max-width: 900px) {
  :host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block {
    grid-template-columns: 1fr !important;
    grid-template-areas: "tec-label" "tec-title" "tec-copy" "tec-action" !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block .btn-primary {
    justify-self: start !important;
    margin-top: 10px !important;
  }
}

@media (max-width: 640px) {
  :host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block {
    padding: 25px !important;
    border-radius: 22px !important;
  }

  :host(.legacy-page-tec-mtcte-certification) .legacy-document .route-cta-block .btn-primary {
    width: 100% !important;
  }
}

/* Official BEE appliance directory and product-document cards. */
:host(.bee-page) .legacy-document .bee-product-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 26px 0 20px;
}

:host(.bee-page) .legacy-document .bee-product-toolbar input {
  width: min(100%, 620px);
  min-height: 52px;
  padding: 0 18px;
  border: 1px solid #dbe2ec;
  border-radius: 16px;
  outline: none;
  background: #ffffff;
  color: #111827;
  font: inherit;
  box-shadow: 0 10px 28px rgba(15, 23, 42, .06);
}

:host(.bee-page) .legacy-document .bee-product-toolbar input:focus {
  border-color: #c41e3a;
  box-shadow: 0 0 0 4px rgba(196, 30, 58, .1);
}

:host(.bee-page) .legacy-document .bee-product-toolbar span {
  flex: 0 0 auto;
  color: #667085;
  font-size: 13px;
  font-weight: 800;
}

:host(.bee-page) .legacy-document .bee-official-product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

:host(.bee-page) .legacy-document .bee-scheme-panel {
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e1e7ef;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 18px 46px rgba(15, 23, 42, .08);
}

:host(.bee-page) .legacy-document .bee-scheme-panel--mandatory {
  border-top: 5px solid #c41e3a;
}

:host(.bee-page) .legacy-document .bee-scheme-panel--voluntary {
  border-top: 5px solid #16796f;
}

:host(.bee-page) .legacy-document .bee-scheme-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 22px 18px;
  border-bottom: 1px solid #edf0f5;
  background: linear-gradient(135deg, #ffffff, #f8fafc);
}

:host(.bee-page) .legacy-document .bee-scheme-panel__head span {
  display: block;
  margin-bottom: 5px;
  color: #c41e3a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .14em;
  text-transform: uppercase;
}

:host(.bee-page) .legacy-document .bee-scheme-panel--voluntary .bee-scheme-panel__head span {
  color: #16796f;
}

:host(.bee-page) .legacy-document .bee-scheme-panel__head h3 {
  margin: 0;
  color: #111827;
  font-size: 21px;
  line-height: 1.2;
}

:host(.bee-page) .legacy-document .bee-scheme-panel__head > a {
  flex: 0 0 auto;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
}

:host(.bee-page) .legacy-document .bee-scheme-list {
  max-height: 680px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

:host(.bee-page) .legacy-document .bee-scheme-product {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  gap: 13px;
  align-items: center;
  min-width: 0;
  padding: 17px 18px;
  border-bottom: 1px solid #edf0f5;
  transition: background .2s ease, transform .2s ease;
}

:host(.bee-page) .legacy-document .bee-scheme-product[hidden] {
  display: none !important;
}

:host(.bee-page) .legacy-document .bee-scheme-product:hover {
  background: #fff7f8;
}

:host(.bee-page) .legacy-document .bee-scheme-panel--voluntary .bee-scheme-product:hover {
  background: #f2fbf9;
}

:host(.bee-page) .legacy-document .bee-scheme-product__number {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #fff0f3;
  color: #b4233f;
  font-size: 12px;
  font-weight: 900;
}

:host(.bee-page) .legacy-document .bee-scheme-panel--voluntary .bee-scheme-product__number {
  background: #e9f8f5;
  color: #0f7168;
}

:host(.bee-page) .legacy-document .bee-scheme-product__copy {
  min-width: 0;
}

:host(.bee-page) .legacy-document .bee-scheme-product__name {
  display: block;
  color: #111827;
  font-size: 14px;
  font-weight: 850;
  line-height: 1.35;
  text-decoration: none;
  overflow-wrap: anywhere;
}

:host(.bee-page) .legacy-document .bee-scheme-product__copy small,
:host(.bee-page) .legacy-document .bee-scheme-product__copy em {
  display: block;
  margin-top: 5px;
  color: #697586;
  font-size: 11px;
  font-style: normal;
  line-height: 1.4;
}

:host(.bee-page) .legacy-document .bee-scheme-product__copy em {
  color: #b4233f;
  font-weight: 800;
}

:host(.bee-page) .legacy-document .bee-scheme-product__actions {
  display: grid;
  gap: 7px;
}

:host(.bee-page) .legacy-document .bee-scheme-product__actions a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid #dfe5ed;
  border-radius: 9px;
  background: #ffffff;
  color: #344054;
  font-size: 10px;
  font-weight: 850;
  text-decoration: none;
  white-space: nowrap;
}

:host(.bee-page) .legacy-document .bee-scheme-product__actions a:hover {
  border-color: #c41e3a;
  color: #c41e3a;
}

:host(.bee-page) .legacy-document .bee-official-source {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f2f5f8;
  color: #5d6979;
  font-size: 12px;
}

:host(.bee-page) .legacy-document .bee-official-source a {
  color: #b4233f;
  font-weight: 850;
  text-decoration: none;
}

:host(.bee-product-page) {
  display: block;
  overflow-x: clip;
  background: #f5f7fb;
}

:host(.bee-product-page) .legacy-document .bee-official-doc-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 22px;
}

:host(.bee-product-page) .legacy-document .bee-official-doc-card {
  min-width: 0;
  padding: 22px;
  border: 1px solid #e1e7ef;
  border-radius: 20px;
  background: linear-gradient(145deg, #ffffff, #f7fafc);
}

:host(.bee-product-page) .legacy-document .bee-official-doc-card > span {
  color: #c41e3a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
  text-transform: uppercase;
}

:host(.bee-product-page) .legacy-document .bee-official-doc-card > strong {
  display: block;
  margin: 8px 0;
  color: #111827;
  font-size: 18px;
  line-height: 1.35;
}

:host(.bee-product-page) .legacy-document .bee-official-doc-card > p {
  margin: 0 0 16px;
  color: #667085;
}

:host(.bee-product-page) .legacy-document .bee-official-doc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

:host(.bee-product-page) .legacy-document .bee-official-doc-actions a {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  background: #172033;
  color: #ffffff;
  font-size: 11px;
  font-weight: 850;
  text-decoration: none;
}

@media (max-width: 980px) {
  :host(.bee-page) .legacy-document .bee-official-product-grid,
  :host(.bee-product-page) .legacy-document .bee-official-doc-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  :host(.bee-page) .legacy-document .bee-product-toolbar,
  :host(.bee-page) .legacy-document .bee-official-source {
    align-items: stretch;
    flex-direction: column;
  }

  :host(.bee-page) .legacy-document .bee-scheme-product {
    grid-template-columns: 38px minmax(0, 1fr);
  }

  :host(.bee-page) .legacy-document .bee-scheme-product__actions {
    grid-column: 2;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Contact support cards: illustrated, colourful and interactive. */
:host(.legacy-page-contact) .contact-support-card {
  position: relative;
  isolation: isolate;
  min-height: 310px;
  overflow: hidden;
  align-content: end;
  border-color: rgba(18, 21, 37, .08);
  background: linear-gradient(145deg, #ffffff 22%, #fff1f4 100%);
  box-shadow: 0 22px 58px rgba(18, 21, 37, .10);
  transition: transform 220ms ease, box-shadow 220ms ease;
}

:host(.legacy-page-contact) .contact-support-card:nth-child(2) {
  background: linear-gradient(145deg, #ffffff 18%, #edf5ff 100%);
}

:host(.legacy-page-contact) .contact-support-card:nth-child(3) {
  background: linear-gradient(145deg, #ffffff 18%, #ecfbf8 100%);
}

:host(.legacy-page-contact) .contact-support-card::after {
  content: "";
  position: absolute;
  z-index: -1;
  top: -74px;
  right: -70px;
  width: 220px;
  height: 220px;
  border: 34px solid rgba(237, 20, 61, .08);
  border-radius: 999px;
  transition: transform 320ms ease;
}

:host(.legacy-page-contact) .contact-support-card:nth-child(2)::after { border-color: rgba(36, 99, 197, .09); }
:host(.legacy-page-contact) .contact-support-card:nth-child(3)::after { border-color: rgba(8, 127, 120, .09); }

:host(.legacy-page-contact) .contact-support-card:hover {
  transform: translateY(-7px);
  box-shadow: 0 32px 72px rgba(18, 21, 37, .15);
}

:host(.legacy-page-contact) .contact-support-card:hover::after { transform: scale(1.15) rotate(8deg); }

:host(.legacy-page-contact) .contact-support-visual {
  position: absolute;
  top: 25px;
  right: 25px;
  width: 84px;
  height: 84px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(237, 20, 61, .14);
  border-radius: 26px;
  background: rgba(255, 255, 255, .82);
  box-shadow: 0 16px 32px rgba(18, 21, 37, .09);
  backdrop-filter: blur(8px);
}

:host(.legacy-page-contact) .contact-support-card.large .contact-support-visual {
  width: 120px;
  height: 120px;
  border-radius: 34px;
}

:host(.legacy-page-contact) .contact-support-visual svg {
  width: 47px;
  height: 47px;
  fill: none;
  stroke: #ed143d;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

:host(.legacy-page-contact) .contact-support-card:nth-child(2) .contact-support-visual svg { stroke: #2463c5; }
:host(.legacy-page-contact) .contact-support-card:nth-child(3) .contact-support-visual svg { stroke: #087f78; }

:host(.legacy-page-contact) .contact-support-card :is(.eyebrow, h2, h3, p) {
  position: relative;
  z-index: 1;
}

:host(.legacy-page-contact) .contact-support-card.large h2 {
  max-width: calc(100% - 100px);
}

@media (max-width: 680px) {
  :host(.legacy-page-contact) .contact-support-card.large h2 { max-width: 100%; }
  :host(.legacy-page-contact) .contact-support-visual,
  :host(.legacy-page-contact) .contact-support-card.large .contact-support-visual {
    position: relative;
    top: auto;
    right: auto;
    width: 72px;
    height: 72px;
    margin-bottom: 24px;
    border-radius: 22px;
  }
}

/* Final service-page guardrails. Migrated pages used Font Awesome and several
 * late white-card rules; these local styles keep every service hero, icon and
 * route CTA complete inside the shadow root without an external icon font. */
:host(.top-level-service-page) .legacy-document :is(.service-guide-hero, .subservice-hero, .sub-hero) {
  position: relative !important;
  isolation: isolate;
  overflow: hidden !important;
  padding: clamp(56px, 7vw, 88px) 0 !important;
  border-bottom: 1px solid rgba(18, 21, 37, .09) !important;
  background:
    radial-gradient(circle at 88% 16%, rgba(196, 30, 58, .12), transparent 31%),
    radial-gradient(circle at 9% 92%, rgba(36, 99, 197, .08), transparent 28%),
    linear-gradient(145deg, #ffffff 0%, #fbfcff 54%, #f2f5fa 100%) !important;
  color: #101525 !important;
}

:host(.top-level-service-page) .legacy-document :is(.service-guide-hero__grid, .service-guide-hero-grid, .subservice-hero-grid, .sub-hero-grid) {
  position: relative !important;
  z-index: 1 !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1.32fr) minmax(300px, 380px) !important;
  gap: clamp(32px, 5vw, 68px) !important;
  width: min(1180px, calc(100% - 44px)) !important;
  max-width: 1180px !important;
  margin-inline: auto !important;
  align-items: center !important;
}

:host(.top-level-service-page) .legacy-document :is(.service-guide-hero__content, .service-guide-copy, .subservice-hero-copy, .sub-hero-copy) {
  min-width: 0 !important;
}

:host(.top-level-service-page) .legacy-document :is(.service-guide-hero, .subservice-hero, .sub-hero) h1 {
  max-width: 900px !important;
  color: #101525 !important;
  text-shadow: none !important;
  text-wrap: balance;
}

:host(.top-level-service-page) .legacy-document :is(.service-guide-hero, .subservice-hero, .sub-hero) :is(.hero-lead, .service-guide-hero__content > p, .service-guide-copy > p, .subservice-hero-text, .sub-hero-copy > p) {
  color: #526178 !important;
}

:host(.top-level-service-page) .legacy-document :is(.service-guide-hero__card, .subservice-hero-card, .subservice-visual-card, .sub-hero-card) {
  position: relative !important;
  z-index: 1 !important;
  min-width: 0 !important;
  overflow: hidden !important;
  padding: clamp(26px, 3vw, 38px) !important;
  border: 1px solid rgba(18, 21, 37, .11) !important;
  border-radius: 28px !important;
  background: rgba(255, 255, 255, .97) !important;
  color: #526178 !important;
  box-shadow: 0 28px 74px rgba(18, 21, 37, .13) !important;
}

:host(.top-level-service-page) .legacy-document :is(.service-guide-hero__card, .subservice-hero-card, .subservice-visual-card, .sub-hero-card) :is(h2, h3, strong) {
  color: #101828 !important;
}

:host(.top-level-service-page) .legacy-document .authority-card-icon:has(img) {
  display: grid !important;
  width: 112px !important;
  height: 112px !important;
  margin: 0 0 20px !important;
  place-items: center !important;
  border: 1px solid rgba(18, 21, 37, .09) !important;
  border-radius: 22px !important;
  background: #ffffff !important;
  box-shadow: 0 14px 34px rgba(15, 23, 42, .08) !important;
}

:host(.top-level-service-page) .legacy-document .authority-card-icon:has(img)::before {
  display: none !important;
}

:host(.top-level-service-page) .legacy-document .authority-card-icon:has(img) img {
  display: block !important;
  width: 84px !important;
  height: 84px !important;
  object-fit: contain !important;
}

:host(.top-level-service-page) .legacy-document i.legacy-fa-icon {
  display: inline-grid !important;
  width: 46px !important;
  height: 46px !important;
  min-width: 46px !important;
  margin: 0 0 13px !important;
  padding: 0 !important;
  place-items: center !important;
  border: 1px solid color-mix(in srgb, var(--service-accent, #2463c5) 18%, transparent) !important;
  border-radius: 14px !important;
  background: color-mix(in srgb, var(--service-accent, #2463c5) 9%, white) !important;
  color: transparent !important;
  font-size: 0 !important;
  font-style: normal !important;
  line-height: 1 !important;
  box-shadow: none !important;
}

:host(.top-level-service-page) .legacy-document i.legacy-fa-icon::before {
  content: "" !important;
  display: block !important;
  width: 24px !important;
  height: 24px !important;
  margin: 0 !important;
  background: var(--ci-local-icon, var(--ci-icon-shield)) center / contain no-repeat !important;
}

:host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > .guide-block.route-cta-block,
:host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > .content-section.route-cta {
  position: relative !important;
  isolation: isolate;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  align-items: center !important;
  column-gap: clamp(28px, 5vw, 64px) !important;
  row-gap: 10px !important;
  min-width: 0 !important;
  margin-top: 4px !important;
  padding: clamp(30px, 4vw, 48px) !important;
  overflow: hidden !important;
  border: 1px solid rgba(255, 255, 255, .13) !important;
  border-radius: 28px !important;
  background:
    radial-gradient(circle at 91% 8%, rgba(65, 130, 255, .28), transparent 34%),
    radial-gradient(circle at 8% 100%, rgba(237, 20, 61, .22), transparent 34%),
    linear-gradient(135deg, #07152b 0%, #142846 58%, #1b3154 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 28px 72px rgba(7, 21, 43, .24) !important;
}

:host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta)::after {
  content: "";
  position: absolute;
  right: -62px;
  bottom: -90px;
  z-index: -1;
  width: 250px;
  height: 250px;
  border: 36px solid rgba(255, 255, 255, .055);
  border-radius: 50%;
  pointer-events: none;
}

:host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) :is(h2, h3) {
  grid-column: 1;
  margin: 0 !important;
  color: #ffffff !important;
  font-size: clamp(1.8rem, 3vw, 2.7rem) !important;
  line-height: 1.08 !important;
  letter-spacing: -.045em !important;
}

:host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) p {
  grid-column: 1;
  max-width: 760px;
  margin: 2px 0 0 !important;
  color: rgba(255, 255, 255, .76) !important;
  line-height: 1.72 !important;
}

:host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) > :is(a, button) {
  grid-column: 2;
  grid-row: 1 / span 2;
  position: relative;
  z-index: 2;
  justify-self: end !important;
  display: inline-flex !important;
  min-width: 190px !important;
  min-height: 52px !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 22px !important;
  border: 1px solid rgba(255, 255, 255, .2) !important;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #ed143d, #ff4969) !important;
  color: #ffffff !important;
  font-weight: 850 !important;
  text-align: center !important;
  text-decoration: none !important;
  box-shadow: 0 18px 38px rgba(237, 20, 61, .30) !important;
}

@media (max-width: 980px) {
  :host(.top-level-service-page) .legacy-document :is(.service-guide-hero__grid, .service-guide-hero-grid, .subservice-hero-grid, .sub-hero-grid),
  :host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  :host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) > :is(a, button) {
    grid-column: 1;
    grid-row: auto;
    justify-self: start !important;
    margin-top: 10px !important;
  }
}

@media (max-width: 640px) {
  :host(.top-level-service-page) .legacy-document :is(.service-guide-hero__grid, .service-guide-hero-grid, .subservice-hero-grid, .sub-hero-grid) {
    width: min(100% - 28px, 1180px) !important;
  }

  :host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) {
    padding: 25px !important;
    border-radius: 22px !important;
  }

  :host(.top-level-service-page.subservice-page) .legacy-document :is(.guide-article, .service-guide-content, .service-content) > :is(.guide-block.route-cta-block, .content-section.route-cta) > :is(a, button) {
    width: 100% !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  :host(.top-level-service-page) .legacy-document :is(.guide-block, .content-section) > :is([class$="-grid"], [class*="-grid "]) > :is(article, div, a, li),
  :host(.subservice-page) .legacy-document :is(.document-checklist, .related-grid, .mini-stats) > :is(li, a, span) {
    animation: none !important;
    transition: none !important;
  }
}
`;

const LegacyContent = ({ html, css, stylesheet, bodyClass, pageKey }) => {
  const hostRef = useRef(null);

  useEffect(() => {
    if (!hostRef.current) return undefined;

    const shadow = hostRef.current.shadowRoot || hostRef.current.attachShadow({ mode: 'open' });
    shadow.replaceChildren();

    const stylesheetLink = document.createElement('link');
    stylesheetLink.rel = 'stylesheet';
    stylesheetLink.href = stylesheet;

    const pageStyles = document.createElement('style');
    pageStyles.textContent = `${css || ''}\n${legacyEnhancementsCss}`;

    const content = document.createElement('div');
    content.className = 'legacy-document';
    content.innerHTML = html;

    if (pageKey === 'services') {
      const authorityCount = content.querySelector('.services-board-grid strong');
      if (authorityCount) authorityCount.textContent = '13';

      const directoryDescription = content.querySelector('.services-intro-strip > p');
      if (directoryDescription) {
        directoryDescription.textContent = 'Use this directory to identify whether your requirement belongs to BIS, BEE, WPC, TEC, STQC, Consumer Affairs, CDSCO, FSSAI, DOT, PESO, DGFT, ARAI or GeM. Each card links to the relevant service page for deeper information.';
      }

      const serviceGrid = content.querySelector('.services-authority-grid');
      const legalMetrologyRoutes = content.querySelector('#consumer-affairs .services-route-list');
      if (legalMetrologyRoutes && !legalMetrologyRoutes.querySelector('[href="/legal-metrology-compliance-services"]')) {
        const complianceRoute = document.createElement('a');
        complianceRoute.href = '/legal-metrology-compliance-services';
        complianceRoute.innerHTML = '<img src="/legacy-source/assets/services/consumeraffair.png" alt="Legal Metrology Compliance Services logo"><span>Legal Metrology Compliance Services</span>';
        legalMetrologyRoutes.prepend(complianceRoute);
      }

      if (serviceGrid && !serviceGrid.querySelector('#government-e-marketplace-gem')) {
        const gemCard = document.createElement('article');
        gemCard.className = 'services-authority-card';
        gemCard.id = 'government-e-marketplace-gem';
        gemCard.innerHTML = `
          <div class="services-authority-top">
            <span class="services-count">13</span>
            <span class="services-logo"><img src="/assets/services/gem-logo.jpg" alt="Government e Marketplace GeM logo"></span>
          </div>
          <h3>Government e Marketplace (GeM)</h3>
          <p>Registration, vendor assessment, catalogue, tender, order and ongoing account management support.</p>
          <div class="services-route-list">
            <a href="/gem-registration"><img src="/assets/services/gem-logo.jpg" alt="GeM registration and account management logo"><span>GeM Registration &amp; Account Management</span></a>
          </div>
        `;
        serviceGrid.append(gemCard);
      }
    }

    if (pageKey === 'about') {
      const heroPanel = content.querySelector('.about-hero-panel');
      if (heroPanel) {
        const heroVisual = document.createElement('figure');
        heroVisual.className = 'about-hero-visual';
        heroVisual.innerHTML = '<img src="/images/mission-values-team.png" alt="Complise compliance specialists collaborating on regulatory documentation" loading="eager"><figcaption><span>One accountable desk</span><strong>Regulatory strategy, technical coordination and compliance operations.</strong></figcaption>';
        heroPanel.prepend(heroVisual);
      }

      const storyPlaceholder = content.querySelector('.about-image-placeholder');
      if (storyPlaceholder) {
        const storyVisual = document.createElement('figure');
        storyVisual.className = 'about-story-card about-story-visual';
        storyVisual.innerHTML = '<img src="/images/about-compliance-team.png" alt="Complise India team reviewing a product compliance roadmap" loading="lazy"><figcaption><span>Our story in practice</span><strong>Hands-on guidance from product mapping through approval and renewal.</strong></figcaption>';
        storyPlaceholder.replaceWith(storyVisual);
      }

      const timeline = content.querySelector('.about-timeline');
      if (timeline) {
        timeline.replaceChildren(...journey.map((item) => {
          const article = document.createElement('article');
          const year = document.createElement('span');
          year.textContent = item.year;
          const title = document.createElement('h3');
          title.textContent = item.title;
          const copy = document.createElement('p');
          copy.textContent = item.desc;
          const tag = document.createElement('small');
          tag.textContent = item.tag;
          article.append(year, title, copy, tag);
          return article;
        }));
      }
    }

    if (pageKey === 'contact') {
      const mapPlaceholder = content.querySelector('.contact-map-card');
      if (mapPlaceholder) {
        const mapVisual = document.createElement('figure');
        mapVisual.className = 'contact-map-card contact-coverage-visual';
        mapVisual.innerHTML = '<img src="/legacy-source/assets/home/global-map.webp" alt="Complise India market access and compliance coverage map" loading="lazy"><figcaption><span>Location &amp; coverage</span><strong>Gurugram-led, pan-India coordination with global market-access support.</strong></figcaption>';
        mapPlaceholder.replaceWith(mapVisual);
      }

      const supportVisuals = [
        '<span></span><span></span><span></span><svg viewBox="0 0 64 64" aria-hidden="true"><path d="M13 48V25l19-11 19 11v23"/><path d="M23 48V34h18v14"/><circle cx="32" cy="24" r="4"/></svg>',
        '<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M19 12h10l5 13-7 5c4 8 8 12 16 16l5-7 13 5v10c0 4-3 7-7 7C29 59 5 35 5 10c0-4 3-7 7-7h7Z"/><path d="M42 10h15M50 3v15"/></svg>',
        '<svg viewBox="0 0 64 64" aria-hidden="true"><rect x="7" y="9" width="20" height="20" rx="5"/><rect x="37" y="9" width="20" height="20" rx="5"/><rect x="7" y="39" width="20" height="20" rx="5"/><rect x="37" y="39" width="20" height="20" rx="5"/><path d="M27 19h10M17 29v10M47 29v10M27 49h10"/></svg>',
      ];
      content.querySelectorAll('.contact-support-card').forEach((card, index) => {
        const visual = document.createElement('div');
        visual.className = `contact-support-visual contact-support-visual--${index + 1}`;
        visual.innerHTML = supportVisuals[index] || supportVisuals[0];
        card.prepend(visual);
      });
    }

    content.querySelectorAll('i.fa-solid, i.fa-regular').forEach((icon) => {
      icon.classList.add('legacy-fa-icon');
      icon.dataset.icon = getLegacyFaIconType(icon);
      icon.setAttribute('aria-hidden', 'true');
    });

    const serviceFamily = topLevelServiceFamilyByPageKey[pageKey];
    const photoFeatures = serviceVisualStories[serviceFamily] || [];
    if (photoFeatures.length) {
      const makePhotoFeature = (feature, index, authority = false) => {
        const figure = document.createElement('figure');
        figure.className = `isi-photo-feature service-photo-feature${index % 2 ? ' is-reversed' : ''}${authority ? ' authority-photo-feature shell' : ''}`;

        const image = document.createElement('img');
        image.src = feature.src;
        image.alt = feature.alt;
        image.loading = 'lazy';
        image.decoding = 'async';

        const caption = document.createElement('figcaption');
        const eyebrow = document.createElement('span');
        eyebrow.textContent = feature.eyebrow;
        const title = document.createElement('strong');
        title.textContent = feature.title;
        const copy = document.createElement('p');
        copy.textContent = feature.copy;
        caption.append(eyebrow, title, copy);
        figure.append(image, caption);
        return figure;
      };

      const authorityPage = content.querySelector('.authority-page');
      if (authorityPage) {
        const authorityAnchors = [
          authorityPage.querySelector('.authority-stats'),
          authorityPage.querySelector('.authority-section.authority-split'),
          authorityPage.querySelector('.authority-process'),
        ].filter(Boolean);

        photoFeatures.forEach((feature, index) => {
          const anchor = authorityAnchors[Math.min(index, authorityAnchors.length - 1)];
          if (anchor) anchor.insertAdjacentElement('afterend', makePhotoFeature(feature, index, true));
        });
      } else {
        const article = content.querySelector('.guide-article, .service-guide-content, .service-content');
        const guideBlocks = article
          ? [...article.children].filter((element) => (
            element.classList.contains('guide-block') || element.classList.contains('content-section')
          ))
          : [];
        const anchorIndexes = guideBlocks.length
          ? [
            Math.min(1, guideBlocks.length - 1),
            Math.max(2, Math.floor(guideBlocks.length * 0.52)),
            Math.max(3, guideBlocks.length - 2),
          ]
          : [];

        photoFeatures.forEach((feature, index) => {
          const anchor = guideBlocks[anchorIndexes[index]];
          if (anchor) anchor.insertAdjacentElement('afterend', makePhotoFeature(feature, index));
        });
      }
    }

    const amcHeroGrid = content.querySelector('.amc-page .amc-hero-grid');
    if (amcHeroGrid) {
      const scope = amcHeroScopes[pageKey] || amcHeroScopes['amc-for-brands'];
      const oldVisual = amcHeroGrid.querySelector('.amc-dashboard-card');
      const orbit = document.createElement('div');
      orbit.className = 'amc-orbit-visual amc-scope-orbit';
      orbit.setAttribute('aria-label', scope.ariaLabel);

      const glow = document.createElement('span');
      glow.className = 'amc-orbit-glow';
      glow.setAttribute('aria-hidden', 'true');

      const ring = document.createElement('span');
      ring.className = 'amc-orbit-ring';
      ring.setAttribute('aria-hidden', 'true');

      const track = document.createElement('div');
      track.className = 'amc-orbit-track';
      scope.items.forEach((scopeItem, index) => {
        const item = document.createElement('span');
        item.className = 'amc-orbit-item';
        item.style.setProperty('--amc-angle', `${(360 / scope.items.length) * index}deg`);
        item.style.setProperty('--amc-scope-accent', scopeItem.accent);

        const chip = document.createElement('span');
        chip.className = 'amc-orbit-chip';
        chip.tabIndex = 0;
        chip.title = `${scopeItem.title} — ${scopeItem.detail}`;

        const number = document.createElement('span');
        number.className = 'amc-scope-index';
        number.textContent = String(index + 1).padStart(2, '0');
        const title = document.createElement('strong');
        title.textContent = scopeItem.title;
        const detail = document.createElement('small');
        detail.textContent = scopeItem.detail;
        chip.append(number, title, detail);
        item.appendChild(chip);
        track.appendChild(item);
      });

      const centre = document.createElement('div');
      centre.className = 'amc-orbit-centre';
      const centreLogo = document.createElement('img');
      centreLogo.src = LOGO_URL;
      centreLogo.alt = 'Complise India';
      const centreLabel = document.createElement('span');
      centreLabel.textContent = scope.label;
      centre.append(centreLogo, centreLabel);

      orbit.append(glow, ring, track, centre);
      amcHeroGrid.classList.add('amc-orbit-hero-grid');
      if (oldVisual) oldVisual.replaceWith(orbit);
      else amcHeroGrid.appendChild(orbit);
    }

    let cleanupBeeProductDirectory = () => {};
    if (pageKey === 'bee-certification') {
      const coveredProducts = content.querySelector('#covered-products');
      if (coveredProducts) {
        const heading = document.createElement('div');
        heading.className = 'guide-section-head compact';
        heading.innerHTML = '<span>Official appliance directory</span><h2>Mandatory and voluntary products under BEE</h2><p>Search the appliance lists published on the official BEE Star Label portal. Each product opens a dedicated route and includes the official schedule or notification plus its sample-label file.</p>';

        const toolbar = document.createElement('div');
        toolbar.className = 'bee-product-toolbar';
        const search = document.createElement('input');
        search.type = 'search';
        search.placeholder = 'Search appliance, schedule or scheme…';
        search.setAttribute('aria-label', 'Search BEE mandatory and voluntary appliances');
        const resultCount = document.createElement('span');
        resultCount.setAttribute('role', 'status');
        resultCount.setAttribute('aria-live', 'polite');
        toolbar.append(search, resultCount);

        const directory = document.createElement('div');
        directory.className = 'bee-official-product-grid';

        const normaliseBeeSearch = (value = '') => value.toLocaleLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, ' ').trim();
        const makeSchemeList = (scheme) => {
          const panel = document.createElement('section');
          panel.className = `bee-scheme-panel bee-scheme-panel--${scheme.toLowerCase()}`;
          const panelHead = document.createElement('div');
          panelHead.className = 'bee-scheme-panel__head';
          const titleWrap = document.createElement('div');
          const eyebrow = document.createElement('span');
          eyebrow.textContent = `${scheme} scheme`;
          const title = document.createElement('h3');
          title.textContent = `${scheme} appliances`;
          titleWrap.append(eyebrow, title);
          const source = document.createElement('a');
          source.href = beeProductData.sources[scheme.toLowerCase()];
          source.target = '_blank';
          source.rel = 'noopener';
          source.textContent = 'Official list ↗';
          panelHead.append(titleWrap, source);

          const list = document.createElement('div');
          list.className = 'bee-scheme-list';
          list.setAttribute('role', 'list');
          list.setAttribute('aria-label', `${scheme} BEE appliances`);
          beeProductData.products.filter((item) => item.scheme === scheme).forEach((item) => {
            const row = document.createElement('article');
            row.className = 'bee-scheme-product';
            row.setAttribute('role', 'listitem');
            row.dataset.search = normaliseBeeSearch([item.product, item.notificationTitle, item.status, ...(item.aliases || [])].filter(Boolean).join(' '));
            const number = document.createElement('span');
            number.className = 'bee-scheme-product__number';
            number.textContent = String(item.serial).padStart(2, '0');
            const copy = document.createElement('div');
            copy.className = 'bee-scheme-product__copy';
            const name = document.createElement('a');
            name.className = 'bee-scheme-product__name';
            name.href = item.route;
            name.textContent = item.product;
            const schedule = document.createElement('small');
            schedule.textContent = item.notificationTitle;
            copy.append(name, schedule);
            if (item.status) {
              const status = document.createElement('em');
              status.textContent = item.status;
              copy.appendChild(status);
            }
            const actions = document.createElement('div');
            actions.className = 'bee-scheme-product__actions';
            const notification = document.createElement('a');
            notification.href = item.notificationUrl;
            notification.target = '_blank';
            notification.rel = 'noopener';
            notification.textContent = 'Schedule PDF';
            const label = document.createElement('a');
            label.href = item.labelUrl;
            label.target = '_blank';
            label.rel = 'noopener';
            label.textContent = item.labelFormat === 'Image' ? 'Sample label' : 'Label PDF';
            actions.append(notification, label);
            row.append(number, copy, actions);
            list.appendChild(row);
          });
          panel.append(panelHead, list);
          return panel;
        };

        directory.append(makeSchemeList('Mandatory'), makeSchemeList('Voluntary'));
        const sourceNote = document.createElement('div');
        sourceNote.className = 'bee-official-source';
        sourceNote.innerHTML = `<span>Verified against the official BEE Star Label portal · checked ${new Date(beeProductData.sourceCheckedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span><a href="${beeProductData.sources.mandatory}" target="_blank" rel="noopener">Open BEE source ↗</a>`;

        const updateBeeProducts = () => {
          const words = normaliseBeeSearch(search.value).split(' ').filter(Boolean);
          let visible = 0;
          const rows = [...directory.querySelectorAll('.bee-scheme-product')];
          rows.forEach((row) => {
            const matches = words.every((word) => row.dataset.search.includes(word));
            row.hidden = !matches;
            if (matches) visible += 1;
          });
          directory.querySelectorAll('.bee-scheme-panel').forEach((panel) => {
            panel.classList.toggle('is-empty', !panel.querySelector('.bee-scheme-product:not([hidden])'));
          });
          resultCount.textContent = search.value.trim() ? `${visible} of ${rows.length} listings` : `${rows.length} official listings`;
        };
        search.addEventListener('input', updateBeeProducts);
        cleanupBeeProductDirectory = () => search.removeEventListener('input', updateBeeProducts);
        coveredProducts.replaceChildren(heading, toolbar, directory, sourceNote);
        updateBeeProducts();
      }
    }

    let cleanupIsiMarkingCalculator = () => {};
    if (pageKey === 'isi-mark-certification-scheme-1') {
      const feesSection = content.querySelector('#fees');
      const feeTable = feesSection?.querySelector('.isi-fee-table');
      if (feesSection && feeTable && !feesSection.querySelector('.isi-marking-calculator')) {
        const feeSizes = [
          { key: 'micro', label: 'Micro', detail: 'Micro-scale fee' },
          { key: 'small', label: 'Small', detail: 'Small-scale fee' },
          { key: 'medium', label: 'Medium', detail: 'Medium-scale fee' },
          { key: 'large', label: 'Large', detail: 'Large-scale fee' },
        ];
        const normaliseIsIdentity = (value = '') => value
          .toLocaleUpperCase()
          .replace(/\*/g, '')
          .replace(/:\s*(?:19|20)\d{2}\b/g, '')
          .replace(/\bSECTION\b/g, 'SEC')
          .replace(/\bPART\s*[-:]?\s*/g, 'PART')
          .replace(/\bSEC\s*[-:]?\s*/g, 'SEC')
          .replace(/&/g, 'AND')
          .replace(/AND\s*PART/g, 'AND')
          .replace(/[^A-Z0-9]+/g, '');
        const mandatoryStandards = new Set(
          bisSchemeOneData.products.map((item) => normaliseIsIdentity(item.standard)),
        );
        const standards = [...new Map(isiMarkingFeeData.standards.map((item) => (
          [`${item.isNumber}::${item.title}::${JSON.stringify(item.fees)}`, item]
        ))).values()].map((item) => ({
          ...item,
          schemeType: mandatoryStandards.has(normaliseIsIdentity(item.isNumber))
            ? 'Mandatory'
            : 'Voluntary',
        }));
        const mandatoryFeeCount = standards.filter((item) => item.schemeType === 'Mandatory').length;
        const normaliseFeeSearch = (value = '') => value
          .toLocaleLowerCase()
          .normalize('NFKD')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
        const indexedStandards = standards.map((item) => ({
          ...item,
          searchText: normaliseFeeSearch(`${item.isNumber} ${item.title}`),
        }));
        const money = new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: isiMarkingFeeData.currency || 'INR',
          maximumFractionDigits: 0,
        });
        const unitRateMoney = new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: isiMarkingFeeData.currency || 'INR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        });
        const formatUnitRate = (value) => {
          const numericValue = Number(String(value || '').replace(/,/g, ''));
          return Number.isFinite(numericValue) ? unitRateMoney.format(numericValue) : value || '—';
        };
        const feeController = new AbortController();
        let selectedSize = 'micro';
        let selectedStandard = null;

        const panel = document.createElement('section');
        panel.className = 'isi-marking-calculator';
        panel.setAttribute('aria-labelledby', 'isiMarkingCalculatorTitle');

        const head = document.createElement('div');
        head.className = 'isi-marking-calculator__head';
        const headCopy = document.createElement('div');
        const eyebrow = document.createElement('span');
        eyebrow.textContent = 'Interactive fee lookup';
        const heading = document.createElement('h3');
        heading.id = 'isiMarkingCalculatorTitle';
        heading.textContent = 'Check the annual minimum marking fee';
        const description = document.createElement('p');
        description.textContent = 'Choose the enterprise category, then search by Indian Standard number or product title. Every result is identified as mandatory or voluntary.';
        headCopy.append(eyebrow, heading, description);
        const total = document.createElement('strong');
        total.className = 'isi-marking-calculator__count';
        total.textContent = `${mandatoryFeeCount.toLocaleString('en-IN')} mandatory · ${(standards.length - mandatoryFeeCount).toLocaleString('en-IN')} voluntary`;
        head.append(headCopy, total);

        const controls = document.createElement('div');
        controls.className = 'isi-marking-calculator__controls';

        const sizeField = document.createElement('div');
        sizeField.className = 'isi-marking-field';
        const sizeLabel = document.createElement('span');
        sizeLabel.id = 'isiMarkingSizeLabel';
        sizeLabel.textContent = '1. Select enterprise category';
        const sizeGrid = document.createElement('div');
        sizeGrid.className = 'isi-marking-size-grid';
        sizeGrid.setAttribute('role', 'radiogroup');
        sizeGrid.setAttribute('aria-labelledby', sizeLabel.id);
        feeSizes.forEach((size, index) => {
          const option = document.createElement('label');
          option.className = 'isi-marking-size-option';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'isi-marking-enterprise-size';
          input.value = size.key;
          input.checked = index === 0;
          const copy = document.createElement('span');
          const name = document.createElement('strong');
          name.textContent = size.label;
          const detail = document.createElement('small');
          detail.textContent = size.detail;
          copy.append(name, detail);
          option.append(input, copy);
          sizeGrid.appendChild(option);
        });
        sizeField.append(sizeLabel, sizeGrid);

        const standardField = document.createElement('div');
        standardField.className = 'isi-marking-field';
        const standardLabel = document.createElement('span');
        standardLabel.id = 'isiMarkingStandardLabel';
        standardLabel.textContent = '2. Select IS number or title';
        const searchWrap = document.createElement('div');
        searchWrap.className = 'isi-marking-search-wrap';
        const search = document.createElement('input');
        search.className = 'isi-marking-search';
        search.type = 'search';
        search.placeholder = 'Type IS 302, cement, helmet, cable…';
        search.autocomplete = 'off';
        search.setAttribute('aria-labelledby', standardLabel.id);
        search.setAttribute('aria-controls', 'isiMarkingResults');
        search.setAttribute('aria-autocomplete', 'list');
        const searchIcon = document.createElement('span');
        searchIcon.className = 'isi-marking-search-icon';
        searchIcon.setAttribute('aria-hidden', 'true');
        searchWrap.append(search, searchIcon);
        const searchStatus = document.createElement('div');
        searchStatus.className = 'isi-marking-search-status';
        searchStatus.setAttribute('role', 'status');
        searchStatus.setAttribute('aria-live', 'polite');
        searchStatus.textContent = 'Start typing to search the complete fee schedule.';
        const searchResults = document.createElement('div');
        searchResults.id = 'isiMarkingResults';
        searchResults.className = 'isi-marking-results';
        searchResults.setAttribute('role', 'listbox');
        searchResults.hidden = true;
        standardField.append(standardLabel, searchWrap, searchStatus, searchResults);

        controls.append(sizeField, standardField);

        const result = document.createElement('div');
        result.className = 'isi-marking-calculator__result';
        result.setAttribute('aria-live', 'polite');
        const resultCopy = document.createElement('div');
        resultCopy.className = 'isi-marking-result-copy';
        const resultEyebrow = document.createElement('span');
        resultEyebrow.textContent = 'Selected Indian Standard';
        const resultTitle = document.createElement('strong');
        resultTitle.textContent = 'No standard selected';
        resultTitle.style.setProperty('color', '#ffffff', 'important');
        const resultDescription = document.createElement('p');
        resultDescription.textContent = 'Search and choose an IS number or product title to display its category-specific marking fee.';
        resultDescription.style.setProperty('color', '#d2d9e5', 'important');
        resultCopy.append(resultEyebrow, resultTitle, resultDescription);
        const resultValue = document.createElement('div');
        resultValue.className = 'isi-marking-result-value';
        const amount = document.createElement('strong');
        amount.textContent = '—';
        amount.style.setProperty('color', '#ffffff', 'important');
        const amountLabel = document.createElement('span');
        amountLabel.textContent = 'Micro · Annual minimum';
        amountLabel.style.setProperty('color', '#ff9aad', 'important');
        resultValue.append(amount, amountLabel);
        result.append(resultCopy, resultValue);

        const details = document.createElement('div');
        details.className = 'isi-marking-calculator__details';
        details.hidden = true;

        const statusCard = document.createElement('div');
        statusCard.className = 'isi-marking-detail-card isi-marking-detail-card--status';
        const statusLabel = document.createElement('span');
        statusLabel.textContent = 'Certification route';
        const statusValue = document.createElement('strong');
        const statusHelp = document.createElement('small');
        statusHelp.textContent = 'Mapped against the BIS Scheme-I compulsory product list';
        statusCard.append(statusLabel, statusValue, statusHelp);

        const unitCard = document.createElement('div');
        unitCard.className = 'isi-marking-detail-card';
        const unitLabel = document.createElement('span');
        unitLabel.textContent = 'Unit of product';
        const unitValue = document.createElement('strong');
        const unitHelp = document.createElement('small');
        unitHelp.textContent = 'The measurement unit used in the fee schedule';
        unitCard.append(unitLabel, unitValue, unitHelp);

        const ratesCard = document.createElement('div');
        ratesCard.className = 'isi-marking-rate-card';
        const ratesHead = document.createElement('div');
        const ratesLabel = document.createElement('span');
        ratesLabel.textContent = 'Per-unit marking rate';
        const ratesHelp = document.createElement('small');
        ratesHelp.textContent = 'Rates and applicable quantity slabs from the supplied BIS marking-fee schedule';
        ratesHead.append(ratesLabel, ratesHelp);
        const ratesGrid = document.createElement('div');
        ratesGrid.className = 'isi-marking-rate-grid';
        ratesCard.append(ratesHead, ratesGrid);
        details.append(statusCard, unitCard, ratesCard);

        const note = document.createElement('p');
        note.className = 'isi-marking-calculator__note';
        note.textContent = `Mandatory status is mapped to the ${bisSchemeOneData.count.toLocaleString('en-IN')}-product BIS Scheme-I compulsory list displayed on this page; other fee-schedule entries are shown as voluntary. Final payable fees can depend on production quantity, unit-rate slabs and the latest BIS revision, so confirm the current schedule before filing.`;

        panel.append(head, controls, result, details, note);
        feeTable.insertAdjacentElement('afterend', panel);

        const markingFeeRow = [...feeTable.querySelectorAll('.isi-fee-row')]
          .find((row) => row.firstElementChild?.textContent.trim().toLowerCase() === 'marking fee');
        if (markingFeeRow?.lastElementChild) markingFeeRow.lastElementChild.textContent = 'Use fee lookup below';

        const selectedLabel = (item) => `${item.isNumber} — ${item.title}`;
        const updateResult = () => {
          const size = feeSizes.find((item) => item.key === selectedSize);
          amountLabel.textContent = `${size?.label || selectedSize} · Annual minimum`;
          if (!selectedStandard) {
            amount.textContent = '—';
            resultTitle.textContent = 'No standard selected';
            resultDescription.textContent = 'Search and choose an IS number or product title to display its category-specific marking fee.';
            details.hidden = true;
            return;
          }
          amount.textContent = money.format(selectedStandard.fees[selectedSize]);
          resultTitle.textContent = `${selectedStandard.isNumber} · ${selectedStandard.title}`;
          resultDescription.textContent = `${size?.label || selectedSize} enterprise marking fee from the supplied ${isiMarkingFeeData.feeType.toLowerCase()} schedule.`;
          details.hidden = false;
          statusValue.textContent = selectedStandard.schemeType;
          statusValue.dataset.route = selectedStandard.schemeType.toLowerCase();
          unitValue.textContent = selectedStandard.unit || 'Not specified';
          ratesGrid.replaceChildren(...(selectedStandard.rateSlabs || []).map((slab) => {
            const rate = document.createElement('div');
            rate.className = 'isi-marking-rate-slab';
            const label = document.createElement('span');
            label.textContent = `Slab ${slab.slab}`;
            const value = document.createElement('strong');
            value.textContent = `${formatUnitRate(slab.rate)} / ${selectedStandard.unit || 'unit'}`;
            const quantity = document.createElement('small');
            quantity.textContent = `Applicable quantity: ${slab.quantity || 'Not specified'}`;
            rate.append(label, value, quantity);
            return rate;
          }));
        };

        const chooseStandard = (item) => {
          selectedStandard = item;
          search.value = selectedLabel(item);
          searchResults.hidden = true;
          searchStatus.textContent = `${item.isNumber} selected.`;
          updateResult();
        };

        const renderSearchResults = () => {
          const query = normaliseFeeSearch(search.value);
          if (!query) {
            searchResults.hidden = true;
            searchStatus.textContent = 'Start typing to search the complete fee schedule.';
            return;
          }
          const words = query.split(' ').filter(Boolean);
          const matches = indexedStandards.filter((item) => (
            words.every((word) => item.searchText.includes(word))
          )).sort((left, right) => {
            const leftNumber = normaliseFeeSearch(left.isNumber);
            const rightNumber = normaliseFeeSearch(right.isNumber);
            const relevance = (item, number) => {
              if (number === query) return 0;
              if (number.startsWith(query)) return 1;
              if (item.searchText.startsWith(query)) return 2;
              return 3;
            };
            return relevance(left, leftNumber) - relevance(right, rightNumber)
              || left.serial - right.serial;
          }).slice(0, 14);
          searchResults.replaceChildren(...matches.map((item) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'isi-marking-result-option';
            button.setAttribute('role', 'option');
            const copy = document.createElement('span');
            const isNumber = document.createElement('strong');
            isNumber.textContent = item.isNumber;
            const title = document.createElement('small');
            title.textContent = item.title;
            copy.append(isNumber, title);
            const meta = document.createElement('span');
            meta.className = 'isi-marking-result-option__meta';
            const route = document.createElement('i');
            route.className = `isi-marking-route isi-marking-route--${item.schemeType.toLowerCase()}`;
            route.textContent = item.schemeType;
            const preview = document.createElement('b');
            preview.textContent = money.format(item.fees[selectedSize]);
            meta.append(route, preview);
            button.append(copy, meta);
            button.addEventListener('click', () => chooseStandard(item), { signal: feeController.signal });
            return button;
          }));
          searchResults.hidden = matches.length === 0;
          searchStatus.textContent = matches.length
            ? `Showing ${matches.length}${matches.length === 14 ? ' best' : ''} match${matches.length === 1 ? '' : 'es'}. Select one to calculate the fee.`
            : 'No standard matches this search. Try an IS number or a shorter product title.';
        };

        sizeGrid.addEventListener('change', (event) => {
          if (!event.target.matches('input[type="radio"]')) return;
          selectedSize = event.target.value;
          updateResult();
          if (!searchResults.hidden) renderSearchResults();
        }, { signal: feeController.signal });
        search.addEventListener('input', () => {
          if (selectedStandard && search.value !== selectedLabel(selectedStandard)) {
            selectedStandard = null;
            updateResult();
          }
          renderSearchResults();
        }, { signal: feeController.signal });
        search.addEventListener('focus', renderSearchResults, { signal: feeController.signal });
        search.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          searchResults.hidden = true;
          searchStatus.textContent = 'Search suggestions closed.';
        }, { signal: feeController.signal });
        cleanupIsiMarkingCalculator = () => feeController.abort();
      }
    }

    const productSearch = content.querySelector('#isiProductSearch');
    const productTable = content.querySelector('#isiCompulsoryTable');
    let updateProductSearch;
    let clearProductSearch;

    if (productSearch && productTable) {
      const searchBox = productSearch.closest('.isi-product-search-box');
      const tableWrap = productTable.closest('.isi-table-wrap');
      const originalRows = [...productTable.querySelectorAll('tbody tr')];
      const productKey = (value = '') => value
        .toLocaleLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
      const localRoutes = new Map(originalRows.map((row) => {
        const link = row.querySelector('.isi-product-link');
        return [productKey(link?.textContent), link?.getAttribute('href')];
      }).filter(([key, href]) => key && href));
      const tableBody = productTable.querySelector('tbody');

      if (tableBody && bisSchemeOneData?.products?.length) {
        const officialRows = bisSchemeOneData.products.map((item) => {
          const row = document.createElement('tr');
          row.dataset.search = [item.serial, item.standard, item.product, item.category, item.notification].join(' ');

          const serial = document.createElement('td');
          serial.textContent = String(item.serial);
          serial.setAttribute('aria-label', `Serial number ${item.serial}`);

          const standard = document.createElement('td');
          standard.textContent = item.standard;

          const product = document.createElement('td');
          const productRoute = localRoutes.get(productKey(item.product)) || item.route;
          if (productRoute) {
            const link = document.createElement('a');
            link.className = 'isi-product-link';
            link.href = productRoute;
            link.textContent = item.product;
            product.appendChild(link);
          } else {
            const name = document.createElement('span');
            name.className = 'isi-product-name';
            name.textContent = item.product;
            product.appendChild(name);
          }

          const category = document.createElement('td');
          category.textContent = item.category;

          const notification = document.createElement('td');
          const notificationLink = document.createElement('a');
          notificationLink.className = 'isi-qco-link';
          notificationLink.href = item.notificationUrl || bisSchemeOneData.source;
          notificationLink.target = '_blank';
          notificationLink.rel = 'noopener';
          notificationLink.textContent = 'View BIS notification';
          if (item.notification) notificationLink.title = item.notification;
          notification.appendChild(notificationLink);

          row.append(serial, standard, product, category, notification);
          return row;
        });
        tableBody.replaceChildren(...officialRows);
      }

      const rows = [...productTable.querySelectorAll('tbody tr')];
      const searchWords = (value) => value
        .toLocaleLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      const wordStem = (word) => {
        if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
        if (word.length > 4 && word.endsWith('es')) return word.slice(0, -2);
        if (word.length > 3 && word.endsWith('s')) return word.slice(0, -1);
        return word;
      };

      productSearch.placeholder = 'Search product, IS number, category or QCO…';
      productSearch.setAttribute('aria-controls', 'isiCompulsoryTable');

      const sectionCopy = productSearch.closest('.guide-block')?.querySelector('.guide-section-head p');
      if (sectionCopy) {
        sectionCopy.textContent = `Search all ${rows.length} numbered products in the official BIS Scheme-I list by product name, Indian Standard, category, or QCO reference.`;
      }

      const serialHeading = productTable.querySelector('thead th:first-child');
      if (serialHeading) serialHeading.textContent = 'Sr. No.';

      rows.forEach((row, index) => {
        const serialCell = row.querySelector('td:first-child');
        if (!serialCell) return;
        serialCell.dataset.originalSerial = serialCell.textContent.trim();
        serialCell.textContent = String(index + 1);
        serialCell.setAttribute('aria-label', `Serial number ${index + 1}`);
      });

      if (!productTable.querySelector('colgroup')) {
        const colgroup = document.createElement('colgroup');
        ['9%', '17%', '31%', '18%', '25%'].forEach((width) => {
          const column = document.createElement('col');
          column.style.width = width;
          colgroup.appendChild(column);
        });
        productTable.prepend(colgroup);
      }

      const meta = document.createElement('div');
      meta.className = 'isi-search-meta';

      const count = document.createElement('span');
      count.className = 'isi-search-count';
      count.setAttribute('role', 'status');
      count.setAttribute('aria-live', 'polite');

      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'isi-search-clear';
      clear.textContent = 'Clear search';
      clear.hidden = true;

      const empty = document.createElement('div');
      empty.className = 'isi-search-empty';
      empty.textContent = 'No ISI products match your search. Try a product name, IS number, category, or QCO reference.';
      empty.hidden = true;

      meta.append(count, clear);
      searchBox?.appendChild(meta);

      const officialSource = document.createElement('div');
      officialSource.className = 'isi-official-source';
      const officialSourceText = document.createElement('span');
      const sourceDate = bisSchemeOneData.sourceUpdatedAt
        ? new Date(bisSchemeOneData.sourceUpdatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
        : '';
      officialSourceText.textContent = `Verified against the official BIS Scheme-I list${sourceDate ? ` · source updated ${sourceDate}` : ''}`;
      const officialSourceLink = document.createElement('a');
      officialSourceLink.href = bisSchemeOneData.source;
      officialSourceLink.target = '_blank';
      officialSourceLink.rel = 'noopener';
      officialSourceLink.textContent = 'Open BIS source ↗';
      officialSource.append(officialSourceText, officialSourceLink);
      tableWrap?.insertAdjacentElement('beforebegin', officialSource);
      tableWrap?.insertAdjacentElement('afterend', empty);
      tableWrap?.setAttribute('role', 'region');
      tableWrap?.setAttribute('aria-label', 'Searchable ISI compulsory products table');
      tableWrap?.setAttribute('tabindex', '0');

      updateProductSearch = () => {
        const query = productSearch.value.trim();
        const queryWords = searchWords(query);
        let visible = 0;
        rows.forEach((row) => {
          const searchableWords = searchWords(row.dataset.search || row.textContent || '');
          const searchableText = searchableWords.join(' ');
          const searchableStems = new Set(searchableWords.map(wordStem));
          const matches = queryWords.length === 0 || queryWords.every((word) => (
            searchableText.includes(word) || searchableStems.has(wordStem(word))
          ));
          row.hidden = !matches;
          if (matches) visible += 1;
        });
        count.textContent = query ? `${visible} of ${rows.length} products` : `${rows.length} products`;
        clear.hidden = !query;
        empty.hidden = visible !== 0;
        if (tableWrap) tableWrap.hidden = visible === 0;
      };

      clearProductSearch = () => {
        productSearch.value = '';
        updateProductSearch();
        productSearch.focus();
      };

      productSearch.addEventListener('input', updateProductSearch);
      clear.addEventListener('click', clearProductSearch);
      updateProductSearch();
    }

    let productDirectoryController;
    let cleanupProductDirectory = () => {};
    const directoryPageKey = serviceProductDirectoryAliases[pageKey] || pageKey;
    const directoryConfig = serviceProductDirectoryConfigs[directoryPageKey];

    if (directoryConfig && !['isi-mark-certification-scheme-1', 'bee-certification'].includes(pageKey)) {
      productDirectoryController = new AbortController();
      const topServiceRoutes = new Set(Object.keys(serviceProductDirectoryConfigs).map((key) => `/${key}`));
      const buildProductDirectory = (manifest = []) => {
        if (productDirectoryController.signal.aborted) return;

        const manifestEntriesRaw = manifest
          .filter((item) => directoryConfig.prefixes.some((prefix) => item.route.startsWith(prefix)))
          .filter((item) => !topServiceRoutes.has(item.route))
          .map((item) => ({
            name: item.title
              .replace(/\s+(ISI Certification|CRS Registration|Compliance Route).*$/i, '')
              .replace(/\s*\|.*$/, '')
              .trim(),
            route: item.route,
            meta: directoryConfig.authority,
          }));
        const manifestEntries = [...new Map(manifestEntriesRaw
          .sort((left, right) => right.route.length - left.route.length)
          .map((item) => [item.name.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(), item])).values()];

        const isiEntries = directoryConfig.includeIsi
          ? bisSchemeOneData.products.map((item) => ({
            name: item.product,
            route: item.route,
            meta: `${item.standard} · ${item.category}`,
            serial: item.serial,
          }))
          : [];
        const beeEntries = directoryConfig.includeBee
          ? [...new Map(beeProductData.products.map((item) => [item.route, {
            name: item.product,
            route: item.route,
            meta: `${item.scheme} · ${item.status || item.notificationTitle}`,
            serial: item.serial,
          }])).values()]
          : [];
        const entries = [...new Map([...isiEntries, ...beeEntries, ...manifestEntries].map((item) => [item.route, item])).values()]
          .sort((left, right) => (
            (left.serial || Number.MAX_SAFE_INTEGER) - (right.serial || Number.MAX_SAFE_INTEGER)
            || left.name.localeCompare(right.name)
          ));
        if (!entries.length) return;

        const target = content.querySelector('.guide-article, .service-guide-content, .service-content')
          || content.querySelector('.authority-page')
          || content.querySelector('main');
        if (!target || target.querySelector('.service-product-directory')) return;

        const section = document.createElement('section');
        section.className = 'guide-block service-product-directory';
        section.id = 'product-directory';

        const head = document.createElement('div');
        head.className = 'service-product-directory__head';
        const headingCopy = document.createElement('div');
        const eyebrow = document.createElement('span');
        eyebrow.textContent = 'Products & approval routes';
        const heading = document.createElement('h2');
        heading.textContent = directoryConfig.title;
        const description = document.createElement('p');
        description.textContent = 'Search the complete website directory for this service. Every listed item opens a dedicated product or approval-route page with applicability, documents, process and next steps.';
        headingCopy.append(eyebrow, heading, description);
        const count = document.createElement('div');
        count.className = 'service-product-directory__count';
        count.textContent = `${entries.length} routes`;
        head.append(headingCopy, count);

        const searchWrap = document.createElement('div');
        searchWrap.className = 'service-product-directory__search';
        const searchInput = document.createElement('input');
        searchInput.type = 'search';
        searchInput.placeholder = `Search ${directoryConfig.authority} products or routes…`;
        searchInput.setAttribute('aria-label', `Search ${directoryConfig.authority} products and routes`);
        searchWrap.appendChild(searchInput);

        const list = document.createElement('div');
        list.className = 'service-product-directory__list';
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'service-product-directory__more';
        const empty = document.createElement('div');
        empty.className = 'service-product-directory__empty';
        empty.textContent = 'No listed product or route matches this search.';
        empty.hidden = true;

        let visibleLimit = 30;
        const normaliseDirectoryText = (value = '') => value.toLocaleLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, ' ').trim();
        const renderDirectory = () => {
          const query = normaliseDirectoryText(searchInput.value);
          const words = query.split(' ').filter(Boolean);
          const filtered = entries.filter((item) => {
            const text = normaliseDirectoryText(`${item.name} ${item.meta}`);
            return words.every((word) => text.includes(word));
          });
          const visible = filtered.slice(0, visibleLimit);
          list.replaceChildren(...visible.map((item, index) => {
            const link = document.createElement('a');
            link.className = 'service-product-directory__item';
            link.href = item.route;
            const number = document.createElement('span');
            number.className = 'service-product-directory__number';
            number.textContent = String(item.serial || index + 1).padStart(2, '0');
            const copy = document.createElement('span');
            const name = document.createElement('strong');
            name.textContent = item.name;
            const meta = document.createElement('small');
            meta.textContent = item.meta;
            copy.append(name, meta);
            const open = document.createElement('span');
            open.className = 'service-product-directory__open';
            open.textContent = 'Open guide →';
            link.append(number, copy, open);
            return link;
          }));
          count.textContent = query ? `${filtered.length} of ${entries.length} routes` : `${entries.length} routes`;
          empty.hidden = filtered.length !== 0;
          more.hidden = filtered.length <= visibleLimit;
          more.textContent = `Show more (${Math.max(0, filtered.length - visibleLimit)} remaining)`;
        };

        const onSearch = () => {
          visibleLimit = searchInput.value.trim() ? 80 : 30;
          renderDirectory();
        };
        const onMore = () => {
          visibleLimit += 50;
          renderDirectory();
        };
        searchInput.addEventListener('input', onSearch);
        more.addEventListener('click', onMore);
        cleanupProductDirectory = () => {
          searchInput.removeEventListener('input', onSearch);
          more.removeEventListener('click', onMore);
        };

        section.append(head, searchWrap, list, empty, more);
        const directChildFor = (selector) => {
          let match = target.querySelector(selector);
          while (match && match.parentElement && match.parentElement !== target) match = match.parentElement;
          return match?.parentElement === target ? match : null;
        };
        const coverageAnchor = directChildFor('#products, #product-categories, #covered-products, #scope, [id*="product"]');
        const nextStage = directChildFor('#documents, #process, #fees, #timeline, .route-cta-block, .guide-question-panel, .guide-cta-panel, .authority-cta, .subservice-final-cta, .sub-cta');
        if (coverageAnchor) coverageAnchor.insertAdjacentElement('afterend', section);
        else if (nextStage) target.insertBefore(section, nextStage);
        else target.appendChild(section);
        const toc = content.querySelector('.guide-toc-card');
        if (toc && !toc.querySelector('a[href="#product-directory"]')) {
          const tocLink = document.createElement('a');
          tocLink.href = '#product-directory';
          tocLink.textContent = 'Products covered';
          const tocCta = toc.querySelector('.toc-cta');
          if (tocCta) toc.insertBefore(tocLink, tocCta);
          else toc.appendChild(tocLink);
        }
        renderDirectory();
      };

      fetch('/legacy-pages/manifest.json', { signal: productDirectoryController.signal })
        .then((response) => (response.ok ? response.json() : []))
        .then(buildProductDirectory)
        .catch((error) => {
          if (error.name !== 'AbortError' && directoryConfig.includeIsi) buildProductDirectory([]);
        });
    }

    const handleClick = (event) => {
      const serviceToggle = event.target.closest?.('[data-service-toggle]');
      if (serviceToggle) {
        event.preventDefault();
        const dropdown = serviceToggle.closest('[data-service-multiselect]');
        const shouldOpen = !dropdown.hasAttribute('data-open');
        shadow.querySelectorAll('[data-service-multiselect][data-open]').forEach((item) => item.removeAttribute('data-open'));
        if (shouldOpen) dropdown.setAttribute('data-open', '');
        return;
      }

      const anchor = event.target.closest?.('a');
      const href = anchor?.getAttribute('href');
      if (!href?.startsWith('#') || href.length === 1) return;
      const target = shadow.getElementById(href.slice(1));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleChange = (event) => {
      if (!event.target.matches?.('[data-service-multiselect] input[name="services"]')) return;
      const dropdown = event.target.closest('[data-service-multiselect]');
      const count = dropdown.querySelectorAll('input[name="services"]:checked').length;
      const counter = dropdown.querySelector('[data-service-toggle] span');
      if (counter) counter.textContent = `${count} selected`;
    };

    const handleSubmit = async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const googleSheetUrl = form.dataset.googleSheetUrl;
      if (googleSheetUrl) {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        const status = form.querySelector('[role="status"]');
        if (button) button.disabled = true;
        if (status) status.textContent = 'Sending your registration…';

        try {
          const payload = new FormData(form);
          payload.append('event', form.dataset.event || 'Complise India event');
          await fetch(googleSheetUrl, { method: 'POST', body: payload, mode: 'no-cors' });
          form.reset();
          if (status) status.textContent = 'Thank you. Your registration has been sent.';
        } catch {
          if (status) status.textContent = `Please email ${form.dataset.fallbackEmail || 'info@compliseindia.com'} to register.`;
        } finally {
          if (button) button.disabled = false;
        }
        return;
      }

      const action = form.getAttribute('action');
      if (action) return;

      event.preventDefault();
      const fields = [...new FormData(form).entries()]
        .filter(([, value]) => typeof value === 'string' && value.trim())
        .map(([name, value]) => `${name.replace(/[-_]/g, ' ')}: ${value}`);
      const subject = encodeURIComponent('Compliance Consultation Request');
      const body = encodeURIComponent(fields.join('\n'));
      window.location.href = `mailto:info@compliseindia.com?subject=${subject}&body=${body}`;
    };

    content.addEventListener('click', handleClick);
    content.addEventListener('change', handleChange);
    content.addEventListener('submit', handleSubmit);
    shadow.append(stylesheetLink, pageStyles, content);

    return () => {
      if (productSearch && updateProductSearch) productSearch.removeEventListener('input', updateProductSearch);
      const clear = content.querySelector('.isi-search-clear');
      if (clear && clearProductSearch) clear.removeEventListener('click', clearProductSearch);
      productDirectoryController?.abort();
      cleanupProductDirectory();
      cleanupBeeProductDirectory();
      cleanupIsiMarkingCalculator();
      content.removeEventListener('click', handleClick);
      content.removeEventListener('change', handleChange);
      content.removeEventListener('submit', handleSubmit);
    };
  }, [html, css, stylesheet, pageKey]);

  const isTopLevelServicePage = Boolean(topLevelServiceFamilyByPageKey[pageKey]);

  return (
    <div
      ref={hostRef}
      className={`block w-full legacy-page-${pageKey} ${bodyClass || ''}${isTopLevelServicePage ? ' isi-service-family top-level-service-page' : ''}`}
    />
  );
};

const PageState = ({ type }) => (
  <main className="min-h-[62vh] bg-[#FDFBF7] flex items-center justify-center px-6 py-24">
    <div className="max-w-xl text-center">
      {type === 'loading' ? (
        <LoaderCircle className="w-10 h-10 text-[#C41E3A] animate-spin mx-auto mb-5" />
      ) : (
        <span className="w-16 h-16 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center mx-auto mb-5">
          <FileSearch className="w-7 h-7 text-[#C41E3A]" />
        </span>
      )}
      <h1 className="font-display text-3xl md:text-4xl font-extrabold text-neutral-900 mb-3">
        {type === 'loading' ? 'Loading page…' : 'This page was not found'}
      </h1>
      {type !== 'loading' && (
        <>
          <p className="text-neutral-600 leading-relaxed mb-7">
            The address may have changed during the website migration. Browse the complete page directory or return home.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-semibold">
              <ArrowLeft className="w-4 h-4" /> Back home
            </Link>
            <Link to="/pages" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C41E3A] text-white text-sm font-semibold">
              Browse all pages <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  </main>
);

const LegacyPage = () => {
  const location = useLocation();
  const pageKey = useMemo(() => getPageKey(location.pathname), [location.pathname]);
  const [state, setState] = useState({ status: 'loading', page: null });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading', page: null });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    fetch(`/legacy-pages/${pageKey}.json`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Page not found');
        return response.json();
      })
      .then((page) => setState({ status: 'ready', page }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        const virtualPage = virtualIsiProductPage(pageKey) || virtualBeeProductPage(pageKey);
        setState(virtualPage
          ? { status: 'ready', page: virtualPage }
          : { status: 'missing', page: null });
      });

    return () => controller.abort();
  }, [pageKey]);

  useEffect(() => {
    if (state.status !== 'ready') return undefined;
    const previousTitle = document.title;
    const pageTitle = state.page.title.includes('Complise')
      ? state.page.title
      : `${state.page.title} | Complise India`;
    document.title = pageTitle;

    let description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');
    if (!description) {
      description = document.createElement('meta');
      description.name = 'description';
      document.head.appendChild(description);
    }
    description.content = state.page.description;

    return () => {
      document.title = previousTitle;
      if (previousDescription) description.content = previousDescription;
    };
  }, [state]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-neutral-900">
      <Header />
      {state.status === 'ready' ? (
        <LegacyContent
          html={state.page.html}
          css={state.page.css}
          stylesheet={state.page.stylesheet}
          bodyClass={state.page.bodyClass}
          pageKey={pageKey}
        />
      ) : <PageState type={state.status} />}
      <Footer />
    </div>
  );
};

export default LegacyPage;
